import { useFavoriteTab } from '../../hooks/useFavoriteTab';
import { LC_WORKSPACE_CARD_LIMIT, countWorkspaceCards, isWorkspaceCardShown, canSaveWorkspaceCard } from '../../constants/lcWorkspaces';
import LCManagedWorkspacesModal from '../../components/LCManagedWorkspacesModal';
import MuleBadge from '../../components/MuleBadge';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GridStack, GridItemHTMLElement } from 'gridstack';
import 'gridstack/dist/gridstack.min.css';
import { panelGeometryTarget, panelGeometryNeedsUpdate, isDefaultPanelArrangement } from '../../components/panelGrid/panelGeometry';
import {
  attachFitOnDoubleClick,
  fitPanelElement as fitPanelElementShared,
  growPanelToFitContent as growPanelToFitContentShared,
} from '../../components/panelGrid/panelFit';
import {
  trashPanelLayout,
  restorePanelLayout,
  restorePanelBatch,
  latestRestorableBatch,
  applyCopiedPanelLayouts,
  readPanelTrash,
  PanelTrashMap,
} from '../../components/panelGrid/panelLayoutTrash';
import {
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  TrashIcon,
  ArrowTopRightOnSquareIcon,
  AdjustmentsHorizontalIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  UserGroupIcon,
  FunnelIcon,
  ArrowLeftIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import { cachedGet } from '../../utils/lookupCache';
import userSettingsService, { LcPanelLayoutSource } from '../../services/userSettings.service';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import PermissionConfigurable from '../../components/PermissionConfigurable';
import PermissionsContainer, { useResolvedAccessLevel } from '../../components/PermissionsContainer';
import { usePermissionsMode } from '../../contexts/PermissionsModeContext';
import { useCustomizeLayoutMode } from '../../contexts/CustomizeLayoutModeContext';
import { useDispatchFooter } from '../../contexts/DispatchFooterContext';
import { useToast } from '../../contexts/ToastContext';
import TabBar, { Tab } from '../../components/TabBar';
import { PlusIcon } from '@heroicons/react/24/outline';
import FirstCardModal from '../../components/FirstCardModal';
import BucketFilterModal from '../../components/BucketFilterModal';
import CardResultPill from '../../components/CardResultPill';
import DuplicateCardModal from '../../components/DuplicateCardModal';
import { ProDetailsView } from '../../components/ProDetailsView';
import ChangeTemplateBulkModal from '../pro/components/ChangeTemplateBulkModal';
import { useCanEditOrderLcFields } from '../pro/useCanEditOrderLcFields';
import { useCanUseChangeAll } from '../pro/useCanUseChangeAll';
import { usePrefetchChangeAllFields } from '../../hooks/useChangeAllFields';
import { useCanCreateOrder } from '../pro/useCanCreateOrder';
import { useCreateOrderFlow } from '../pro/createOrder/CreateOrderFlowContext';
import { config } from '../../config/config';
import { TanTable, BulkEditMeta } from '../../components/TanTable';
import { ColumnDef, VisibilityState } from '@tanstack/react-table';
import { useFieldDefinitions, type FieldDefinition } from '../../contexts/FieldDefinitionsContext';
import { generateColumnDef, decorateMuleDriverColumn, getApiFieldName, formatDate, formatDateTime, BASE_BUCKET_FIELDS, decorateLocationColumn } from '../../utils/dynamicColumns';
import { decorateTraceColumn } from '../../components/trace/traceColumn';
import { hasNoClockTime } from '../../utils/dateUtils';
import { customCardScopeKey } from '../../utils/customCardKeys';
import { readTabWorkspaceId, resolveWorkspaceId, writeTabWorkspaceId } from '../../utils/dashboardWorkspaceSession';
import ordersService, {
  type ChangeAllChangeSet,
  type ClearApptDatesPreview,
} from '../../services/orders.service';
import ClearApptDatesModal from './ClearApptDatesModal';
import { COMPUTED_FIELD_OPTIONS, buildComputedColumnDefs } from '../../utils/computedColumns';
import { useDisplayPreferences } from '../../contexts/DisplayPreferencesContext';
import GridLabel from '../../components/GridLabel';
import FilterBar from '../../components/FilterBar';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import styles from './LCDashboards.module.css';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import { useFallingEdge } from '../../hooks/useFallingEdge';
import { useModalDrag } from '../../hooks/useModalDrag';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
  type ComputedFieldSet,
} from '../../hooks/useAdditionalFields';
import ModalHeader from '../../components/ui/ModalHeader';
import CountPanel, { CountPanelConfig, CountPanelSummary, CountSeverity, summarizeCountPanel } from '../../components/CountPanel';
import { worstUrgency } from '../../utils/ediUrgency';
import DashboardTile, { PANEL_DRAG_HANDLE_CLASS } from '../../components/DashboardTile';
import SchedulePanel, { ScheduleCategory, ScheduleData } from '../../components/SchedulePanel';
import TrackingPanel, { TrackingData } from '../../components/TrackingPanel';
import ExportsSummaryPanel, { ExportsSummaryData } from '../../components/ExportsSummaryPanel';
import ExportsBoardPanel, { ExportsBoardData } from '../../components/ExportsBoardPanel';
import InvoicingStatsPanel, { InvoicingStatsData } from '../../components/InvoicingStatsPanel';
import PerDiemPanel, { PerDiemData } from '../../components/PerDiemPanel';
import AdditionalCostPanel, { AdditionalCostData } from '../../components/AdditionalCostPanel';
import AdditionalCostItemModal from '../../components/pro/AdditionalCostItemModal';
import ChassisChargesPanel, { ChassisData } from '../../components/ChassisChargesPanel';
import EdiBucketPanel from '../../components/EdiBucketPanel';
import EdiBucketDrill, { EdiDrillSelection } from '../../components/EdiBucketDrill';
import { getEdiBuckets, EdiBucket } from '../../services/pendingEdiOrders.service';
import { Drawer } from '../../components/ui/Drawer';
import Toggle from '../../components/Toggle';
import ProResultCard, { FieldHighlightProvider, PRO_CARD_FIELD_IDS } from '../../components/ProResultCard';
import { buildProBulkActions, prosFromRows } from '../../components/proBulkActions';
import {
  buildProCardColumns,
  PRO_CARD_COVERED_FIELDNAMES,
  PRO_CARD_HEADER_COLUMN_IDS,
} from '../../components/proCardColumns';
import type { BulkAction } from '../../components/TableActionBar';
import CardDocumentsTable from '../search-documents/CardDocumentsTable';
import { MAX_SEARCH_PROS } from '../../types/documentsSearch';
import { useMsal } from '@azure/msal-react';
import searchService, { type ProSearchData } from '../../services/search.service';
import { mergeRefreshedProRows, isProRow } from '../../utils/proRowRefresh';
import { TraceResultsProvider } from '../../components/trace/TraceResultsContext';

interface FilterCondition {
  id: number;
  field: string;
  compare: string;
  values: string;
}

interface CardConfig {
  required?: boolean;
  key: string;
  title: string;
  column: number;
  order: number;
  visible: boolean;
  hidden?: boolean;
  span: number;
  workspaceId?: string;
  locked?: boolean;  // System/locked cards that cannot be edited or deleted
  // Data builder configuration
  filterConditions?: FilterCondition[];
  filterView?: string;
  // "Include Closed Orders" on the card's filter: query ordersall/legsall instead
  // of orderscurrent/legscurrent. Custom-filter cards only — a bucket card's scope
  // comes from the backend bucket registry.
  includeClosed?: boolean;
  visibleColumns?: string[];
  bucket?: string;  // Pre-built bucket identifier (e.g., 'per-diem-not-dispatched')
  grouping?: string[];  // Current grouping columns
  tabGrouping?: string;  // Tab-based grouping field (for bucket cards with tabs)
  // Dynamic columns configuration
  additionalFields?: string[];  // Extra fields beyond bucket defaults, triggers refetch when changed
  // StatButton configuration
  showStatButton?: boolean;  // Whether to show this card in the stats grid
  statButtonLabel?: string;  // Custom label for the stat button (defaults to card title)
  statButtonCountField?: string;  // Field to use for counting (e.g., 'perdiemstartdate', 'cutoffdatetime')
  // Card height configuration
  cardHeight?: 'sm' | 'md' | 'lg';  // Height of the card table
  // GridStack position (if set, used directly instead of computing from column/order/span)
  gsX?: number;
  gsY?: number;
  gsW?: number;
  gsH?: number;
  expandedGsH?: number;  // Stores the height when card is collapsed, so it can be restored when expanded
  isShared?: boolean;
}

interface Workspace {
  required?: boolean;
  id: string;
  name: string;
  order: number;
  columnCount: number;
}

// =============================================================================
// COUNT PANELS (LC config)
// =============================================================================
// Presentation config for the LC dashboard's "counts" view, rendered by the
// shared <CountPanel>. Each row references a bucket key; its count comes from
// /bucket-counts and clicking it opens that bucket's drill-down table. The
// bucket's data/criteria live in the backend LC_BUCKETS registry.

const LC_COUNT_PANELS: CountPanelConfig[] = [
  {
    key: 'pending-orders',
    title: 'Pending Orders',
    columns: [
      {
        header: { key: 'lc-imports-pending', label: 'Imports' },
        rows: [
          { key: 'lc-imports-not-arrived', label: 'Not Arrived', bold: true },
          { key: 'lc-imports-not-arrived-nontraceable', label: 'Non-Traceable', indent: 1, color: 'orange' },
          { key: 'lc-imports-arrived', label: 'Arrived', bold: true },
          { key: 'lc-imports-arrived-onhold', label: 'On Hold', indent: 1 },
          { key: 'lc-imports-arrived-available', label: 'Available for Delivery', indent: 1, color: 'green' },
        ],
      },
      {
        header: { key: 'lc-exports-pending', label: 'Exports' },
        rows: [
          { key: 'lc-exports-onhold', label: 'On Hold', color: 'orange' },
          { key: 'lc-exports-available', label: 'Available for Delivery', color: 'green' },
          { key: 'lc-exports-pending-doc-cutoff', label: 'Pending Doc Cutoff Date', color: 'red' },
          { key: 'lc-exports-pending-cutoff', label: 'Pending Cutoff Date', color: 'red' },
          { key: 'lc-exports-rolling-cutoffs', label: 'Rolling Cutoffs' },
        ],
      },
      {
        header: { key: 'lc-domestic-repo-pending', label: 'Domestic / Repo' },
        rows: [
          { key: 'lc-domrepo-available', label: 'Available for Delivery', color: 'green' },
          // Dry Run is a subset of Domestic/Repo moves — its own header-styled row.
          { key: 'lc-dry-run', label: 'Dry Run Pro #s', headerStyle: true },
        ],
      },
    ],
  },
  {
    // Offhire: containers being off-hired (returned to the leasing line), bucketed by
    // the order's offhire status. Single headerless section — all three flag red when
    // their count is non-zero (a chip only colors when count > 0).
    key: 'offhire',
    title: 'Offhire',
    columns: [
      {
        rows: [
          { key: 'lc-offhire-pending', label: 'Pending', color: 'red' },
          { key: 'lc-offhire-requested', label: 'Requested', color: 'red' },
          { key: 'lc-offhire-missing-term-location', label: 'Missing Term Location', color: 'red' },
        ],
      },
    ],
  },
  {
    // Street Turn: containers being reused via a street turn, bucketed by reuse-request
    // status. Single headerless section. Request Not Sent / Rejected / Cancelled / Cannot
    // Street Turn flag red (problems), Approved flags green (good), Pending Approval stays
    // uncolored. Cannot Street Turn cuts across the statuses (requested, but off-hired or
    // held), so it overlaps the rows above by design.
    key: 'street-turn',
    title: 'Street Turn',
    columns: [
      {
        rows: [
          { key: 'lc-streetturn-request-not-sent', label: 'Request Not Sent', color: 'red' },
          { key: 'lc-streetturn-pending', label: 'Pending Approval' },
          { key: 'lc-streetturn-rejected', label: 'Rejected', color: 'red' },
          { key: 'lc-streetturn-approved', label: 'Approved', color: 'green' },
          { key: 'lc-streetturn-cancelled', label: 'Cancelled Requests', color: 'red' },
          { key: 'lc-streetturn-cannot', label: 'Cannot Street Turn', color: 'red' },
        ],
      },
    ],
  },
  {
    // PreAdvise: EXPORT hazmat containers to the export port terminals, tracked through
    // the pre-advise / haz-doc lifecycle. Single headerless section. Pending Pre-advise
    // flags red (action needed), Pre-advised flags green (done), Pre-advise Sent is
    // uncolored (in progress).
    key: 'preadvise',
    title: 'PreAdvise',
    columns: [
      {
        rows: [
          { key: 'lc-preadvise-pending', label: 'Pending Pre-advise', color: 'red' },
          { key: 'lc-preadvise-sent', label: 'Pre-advise Sent' },
          { key: 'lc-preadvise-done', label: 'Pre-advised', color: 'green' },
        ],
      },
    ],
  },
  {
    // Rail Billing: EXPORT orders not yet trace-terminated, by rail-bill status. Single
    // headerless section. Ready to Railbill is an indented SUBSET of Pending Rail Billing
    // (the pending orders that already have seal/piece/weight). Pending/Ready/Cancel flag
    // red, Rail Billed flags green, Rail Billing Sent is uncolored (in progress).
    key: 'rail-billing',
    title: 'Rail Billing',
    columns: [
      {
        rows: [
          { key: 'lc-railbill-pending', label: 'Pending Rail Billing', color: 'red' },
          { key: 'lc-railbill-ready', label: 'Ready to Railbill', color: 'red', indent: 1 },
          { key: 'lc-railbill-sent', label: 'Rail Billing Sent' },
          { key: 'lc-railbill-billed', label: 'Rail Billed', color: 'green' },
          { key: 'lc-railbill-cancel', label: 'Cancel Rail Billing', color: 'red' },
        ],
      },
    ],
  },
  {
    // Dropped Containers: containers the delivery driver dropped that aren't moving on yet,
    // split by where they're sitting (GWI yard vs customer). After Delivery is an
    // independently-counted subset shown nested under GWI (dropped at a GWI yard after the
    // delivery leg arrived). Drop Released (the container has been released) flags green.
    key: 'dropped-containers',
    title: 'Dropped Containers',
    columns: [
      {
        rows: [
          { key: 'lc-dropped-total', label: 'Dropped' },
          { key: 'lc-dropped-gwi', label: 'GWI', indent: 1 },
          { key: 'lc-dropped-gwi-after-delivery', label: 'After Delivery', indent: 2 },
          { key: 'lc-dropped-customer', label: 'Customer', indent: 1 },
          { key: 'lc-dropped-released', label: 'Drop Released', color: 'green' },
        ],
      },
    ],
  },
  {
    // Missing Line Item Total: orders whose main invoice (accessorialnum=0) carries a
    // billing line item with a zero total — the LC still needs to fill in the amount.
    // The dark header total counts orders missing ANY of the four items (each order once,
    // so it's < the sum of the rows); each row counts orders missing that specific item.
    key: 'missing-line-item',
    title: 'Missing Line Item Total',
    columns: [
      {
        header: { key: 'lc-missingli-total', label: 'Missing Line Item Total' },
        rows: [
          { key: 'lc-missingli-bobtail', label: 'Bobtail' },
          { key: 'lc-missingli-chassis-split', label: 'Chassis Split' },
          { key: 'lc-missingli-grounded', label: 'Grounded' },
          { key: 'lc-missingli-waiting-time', label: 'Waiting Time' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Tracing: import containers whose gwiTrace milestones are
    // overdue or out of sequence. Single headerless section — the tile title bar
    // names the panel, so the four buckets render flush beneath it.
    key: 'problem-resolution-tracing',
    title: 'Problem Resolution - Tracing',
    columns: [
      {
        rows: [
          // All four are problems, so each flags red when its count is non-zero
          // (CountPanel only colors a chip when the count is > 0).
          { key: 'lc-tracing-eta-past-due', label: 'ETA Past Due', color: 'red' },
          { key: 'lc-tracing-not-sailed', label: 'Not Sailed', color: 'red' },
          { key: 'lc-tracing-not-moving', label: 'Not Moving', color: 'red' },
          { key: 'lc-tracing-missing-events', label: 'Missing / Out-of-Order Events', color: 'red' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Order Entry: orders missing data the LC must enter (LFD,
    // pickup number/appt, per diem start, booking). Single headerless section.
    key: 'problem-resolution-order-entry',
    title: 'Problem Resolution - Order Entry',
    columns: [
      {
        rows: [
          { key: 'lc-oe-no-lfd', label: 'No LFD', color: 'red' },
          { key: 'lc-oe-lfd-today-no-pickup', label: 'LFD ≤ Today, Not Scheduled for Pickup', color: 'red' },
          { key: 'lc-oe-no-pickup-number', label: 'No Pickup Number', color: 'red' },
          { key: 'lc-oe-no-per-diem', label: 'No Per Diem', color: 'red' },
          { key: 'lc-oe-booking-not-on-file', label: 'Booking Not On File', color: 'red' },
          { key: 'lc-oe-pregate-errors', label: 'Pregate Errors', color: 'red' },
          // Imports whose container is also on another live import (same box entered twice).
          { key: 'lc-oe-duplicate-containers', label: 'Duplicate Containers', color: 'red' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Scheduling: orders whose delivery appointment needs
    // attention today (e.g. running late for the appointment window). Single
    // headerless section.
    key: 'problem-resolution-scheduling',
    title: 'Problem Resolution - Scheduling',
    columns: [
      {
        rows: [
          { key: 'lc-sched-late-for-appt', label: 'Late for Appointment', color: 'red' },
          { key: 'lc-sched-past-due-not-dispatched', label: 'Past Due - Not Dispatched', color: 'red' },
          { key: 'lc-sched-past-due-outgate', label: 'Past Due Outgate', color: 'red' },
          { key: 'lc-sched-driver-no-delivery-date', label: 'Driver Assigned with No Delivery Date', color: 'red' },
          { key: 'lc-sched-outgate-no-pickup-date', label: 'Outgated with No Pickup Date', color: 'red' },
          { key: 'lc-sched-scheduled-not-available', label: 'Scheduled Not Available', color: 'red' },
          { key: 'lc-sched-missing-appointment', label: 'Missing Appointment', color: 'red' },
          { key: 'lc-sched-rescheduled-for-today', label: 'Rescheduled For Today', color: 'red' },
          { key: 'lc-sched-available-not-ready', label: 'Available, Not Ready to Schedule', color: 'red' },
          { key: 'lc-sched-per-diem-follow-up', label: 'Per Diem Follow Up', color: 'red' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Termination: the trace-terminate / cutoff lifecycle. The
    // Cutoff Today bucket has a Port/Rail/Other sub-tier (indented rows, each its own
    // bucket). Single headerless section.
    key: 'problem-resolution-termination',
    title: 'Problem Resolution - Termination',
    columns: [
      {
        rows: [
          { key: 'lc-term-missing-driver', label: 'Missing Driver Terminate', color: 'red' },
          { key: 'lc-term-cutoff-no-tt', label: 'Cutoff Today No Trace Terminate', color: 'red' },
          { key: 'lc-term-cutoff-no-tt-port', label: 'Port', color: 'red', indent: 1 },
          { key: 'lc-term-cutoff-no-tt-rail', label: 'Rail', color: 'red', indent: 1 },
          { key: 'lc-term-cutoff-no-tt-other', label: 'Other', color: 'red', indent: 1 },
          { key: 'lc-term-terminated-not-complete', label: 'Terminated, Not Complete', color: 'red' },
          { key: 'lc-term-import-no-tt', label: 'Import - No Trace Terminate', color: 'red' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Grounded Yards: grounded-yard containers whose grounded
    // start/end dates are missing relative to where the move actually is. Single
    // headerless section.
    key: 'problem-resolution-grounded',
    title: 'Problem Resolution - Grounded Yards',
    columns: [
      {
        rows: [
          { key: 'lc-grounded-import-missing-start', label: 'Imports - Missing Grounded Start Date', color: 'red' },
          { key: 'lc-grounded-import-missing-end', label: 'Imports - Missing Grounded End Date', color: 'red' },
          { key: 'lc-grounded-export-missing-start', label: 'Exports - Missing Grounded Start Date', color: 'red' },
          { key: 'lc-grounded-export-not-terminated', label: 'Exports - Not Terminated', color: 'red' },
          { key: 'lc-grounded-export-missing-end', label: 'Exports - Missing Grounded End Date', color: 'red' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Warehousing: the GWI-warehouse receiving (MR) / BL / check-in
    // lifecycle. The busiest section — single headerless column of its many buckets.
    key: 'problem-resolution-warehousing',
    title: 'Problem Resolution - Warehousing',
    columns: [
      {
        rows: [
          { key: 'lc-whse-missing-mr-bl', label: 'Missing MR/BL Line Items', color: 'red' },
          { key: 'lc-whse-non-gwi-mr', label: 'Non-GWI MR Line Items', color: 'red' },
          { key: 'lc-whse-not-arrived', label: 'Not Arrived', color: 'red' },
          { key: 'lc-whse-checked-in-not-complete', label: 'Checked In and Not Complete', color: 'red' },
          { key: 'lc-whse-delivery-past-not-complete', label: 'Delivery < Today, Not Complete', color: 'red' },
          { key: 'lc-whse-load-past-not-complete', label: 'Load Date < Today, Not Complete', color: 'red' },
          { key: 'lc-whse-perdiem-not-complete', label: 'Per Diem LFD ≤ Today, Not Complete', color: 'red' },
          { key: 'lc-whse-cutoff-not-complete', label: 'Cutoff ≤ Today, Not Complete', color: 'red' },
          { key: 'lc-whse-loaded-not-scheduled', label: 'Loaded/Unloaded and Not Scheduled', color: 'red' },
          { key: 'lc-whse-completed-not-released', label: 'Completed and Not Released', color: 'red' },
          { key: 'lc-whse-transfer-missing-mr', label: 'Whse Transfer Missing MR', color: 'red' },
          { key: 'lc-whse-transfer-not-arrived', label: 'Whse Transfer Not Arrived', color: 'red' },
        ],
      },
    ],
  },
  {
    // Problem Resolution – Tablemaint: table-maintenance gaps. Its one bucket counts
    // locations missing GPS coordinates (fed by /tablemaint-counts) and opens the Locations
    // table filtered to those recnos rather than a pro drill-down. Single headerless section.
    key: 'problem-resolution-tablemaint',
    title: 'Problem Resolution - Tablemaint',
    columns: [
      {
        rows: [
          { key: 'lc-tablemaint-missing-coords', label: 'Missing GPS Coordinates', color: 'red' },
        ],
      },
    ],
  },
  {
    // Approval (Accounting workspace). Single bucket — orders not yet approved
    // (approvedby blank). Templates aren't ported to gwi360, so no other buckets.
    key: 'approval',
    title: 'Approval',
    columns: [
      {
        rows: [
          { key: 'lc-orders-not-approved', label: 'Orders Not Approved' },
        ],
      },
    ],
  },
  {
    // Pending Invoicing / Invoicing Holds (Accounting workspace). Two sections, each
    // with a clickable header total (distinct orders). Holds are checked first; an
    // order only lands in Pending Invoicing (CMS / Do Not Bill) if it's in no hold.
    key: 'pending-invoicing',
    title: 'Pending Invoicing',
    columns: [
      {
        header: { key: 'lc-pinv-total', label: 'Pending Invoicing' },
        rows: [
          { key: 'lc-pinv-cms', label: 'CMS' },
          { key: 'lc-pinv-donotbill', label: 'Do Not Bill' },
        ],
      },
      {
        header: { key: 'lc-pinv-holds-total', label: 'Invoicing Holds' },
        rows: [
          { key: 'lc-pinv-pending-approval', label: 'Pending Approval', color: 'red' },
          { key: 'lc-pinv-revised-do', label: 'Invoice Hold', color: 'orange' },
          { key: 'lc-pinv-pod-bol', label: 'POD/BOL/MR/PL/EIR Required', color: 'orange' },
          { key: 'lc-pinv-edi', label: 'EDI Required', color: 'red' },
          { key: 'lc-pinv-trouble-ticket', label: 'Trouble Ticket', color: 'orange' },
          { key: 'lc-pinv-margin', label: 'Margin Approval Required', color: 'orange' },
          { key: 'lc-pinv-tender', label: 'Tender Not Complete', color: 'orange' },
          { key: 'lc-pinv-missing-li', label: 'Missing Line Item Total', color: 'orange' },
          { key: 'lc-pinv-15day', label: '15 Days Past Due Invoicing', color: 'red' },
        ],
      },
    ],
  },
];

// Drill-down card defs derived from the panels (qualified titles for the table header).
const PANEL_CARD_DEFS: { key: string; title: string }[] = (() => {
  const defs: { key: string; title: string }[] = [];
  for (const panel of LC_COUNT_PANELS) {
    for (const col of panel.columns) {
      // A headerless column has no broad count of its own; its rows qualify their
      // drill-down title with the panel name instead of the (absent) header label.
      if (col.header) defs.push({ key: col.header.key, title: col.header.label });
      for (const row of col.rows) {
        const qualifier = col.header ? col.header.label : panel.title;
        defs.push({ key: row.key, title: `${qualifier} – ${row.label}` });
      }
    }
    for (const f of panel.footers || []) {
      defs.push({ key: f.key, title: f.label });
    }
  }
  return defs;
})();

// Every bucket key referenced by the panels.
const PANEL_BUCKET_KEYS: string[] = PANEL_CARD_DEFS.map((d) => d.key);

// Bucket keys grouped by their owning count panel, so the count request can be
// scoped to just the panels that are currently expanded. All buckets still share
// one /bucket-counts call (one table scan); collapsing a panel simply drops its
// SUM(CASE …) columns out of that scan instead of issuing a separate request.
const PANEL_BUCKET_KEYS_BY_PANEL: { [panelKey: string]: string[] } = (() => {
  const map: { [panelKey: string]: string[] } = {};
  for (const panel of LC_COUNT_PANELS) {
    const keys: string[] = [];
    for (const col of panel.columns) {
      if (col.header) keys.push(col.header.key);
      for (const row of col.rows) keys.push(row.key);
    }
    for (const f of panel.footers || []) keys.push(f.key);
    map[panel.key] = keys;
  }
  return map;
})();

// =============================================================================
// MOVABLE COUNT PANELS (GridStack)
// =============================================================================
// The count panels render as tiles in a dedicated GridStack grid so the user can
// drag/resize them and collapse each to a header strip. Layout + collapsed state
// persist per-workspace in the saved `lc` layout under `panelLayouts`, keyed by
// panel key (separate from the drill-down `cardConfigs`, so the two grids never
// collide).

// Stored GridStack position + collapse/lock state for one count panel.
interface PanelLayoutItem {
  key: string;
  x: number;
  y: number;
  w: number;
  h: number;          // live grid height (1 when collapsed)
  collapsed?: boolean;
  locked?: boolean;   // when true, the tile can't be dragged or resized
  expandedH?: number; // height to restore to when expanded
}

// Default tile size (24-col grid units / 40px row units) per panel. Panels not
// listed fall back to PANEL_DEFAULT_*. New panels (e.g. the schedule grid) add an
// entry here for their starting footprint.
const PANEL_GRID_DEFAULTS: { [key: string]: { w: number; h: number } } = {
  'pending-orders': { w: 12, h: 6 },
  'offhire': { w: 6, h: 4 },
  'street-turn': { w: 6, h: 5 },
  'preadvise': { w: 6, h: 4 },
  'rail-billing': { w: 6, h: 5 },
  'dropped-containers': { w: 6, h: 5 },
  'missing-line-item': { w: 6, h: 5 },
  'chassis-charges': { w: 6, h: 3 },
  'new-melrose-pros': { w: 6, h: 10 },
  'warehouse-photos': { w: 6, h: 6 },
  'delivery-schedule': { w: 16, h: 6 },
  'tracking': { w: 14, h: 7 },
  'exports-summary': { w: 18, h: 4 },
  'exports-board': { w: 24, h: 12 },
  'invoicing-stats': { w: 18, h: 7 },
  'approval': { w: 6, h: 3 },
  'pending-invoicing': { w: 12, h: 7 },
  'per-diem': { w: 6, h: 8 },
  'additional-cost': { w: 6, h: 3 },
  'tickets': { w: 6, h: 4 },
  'problem-resolution-tracing': { w: 7, h: 5 },
  'problem-resolution-order-entry': { w: 8, h: 6 },
  'problem-resolution-scheduling': { w: 8, h: 9 },
  'problem-resolution-termination': { w: 7, h: 7 },
  'problem-resolution-grounded': { w: 9, h: 6 },
  'problem-resolution-warehousing': { w: 9, h: 11 },
  'problem-resolution-tablemaint': { w: 6, h: 3 },
  'pending-edi-orders': { w: 8, h: 6 },
};
const PANEL_DEFAULT_W = 12;
const PANEL_DEFAULT_H = 6;

// Layout version for the panel grid. Bumped to 2 when the grid was made finer
// (12→24 cols, 80→40px rows); older saved layouts are scaled ×2 on load.
const PANEL_GRID_VERSION = 2;

// Default count-panel positions for users who have not arranged their own — mirrors
// the org default published via backend/sql/lc_dashboard_force_default_layout.sql.
// Authored at PANEL_GRID_VERSION 2 (24-col grid). Panels absent here (the Exports
// workspace and the permission-gated Invoicing Stats) fall back to the auto-fit flow
// in currentPanelItems. Returns fresh objects so the shared default is never mutated.
const getDefaultPanelLayouts = (): { [workspaceId: string]: PanelLayoutItem[] } => ({
  default: [
    { key: 'pending-orders', x: 12, y: 0, w: 12, h: 6, expandedH: 6 },
    { key: 'offhire', x: 0, y: 20, w: 6, h: 3, expandedH: 3 },
    { key: 'street-turn', x: 0, y: 5, w: 6, h: 4, expandedH: 4 },
    { key: 'preadvise', x: 6, y: 4, w: 6, h: 3, expandedH: 3 },
    { key: 'rail-billing', x: 6, y: 11, w: 6, h: 4, expandedH: 4 },
    { key: 'dropped-containers', x: 6, y: 7, w: 6, h: 4, expandedH: 4 },
    { key: 'missing-line-item', x: 0, y: 0, w: 6, h: 5, expandedH: 5 },
    { key: 'problem-resolution-tracing', x: 0, y: 23, w: 6, h: 4, expandedH: 4 },
    { key: 'problem-resolution-order-entry', x: 0, y: 15, w: 6, h: 5, expandedH: 5 },
    { key: 'problem-resolution-scheduling', x: 18, y: 20, w: 6, h: 6, expandedH: 6 },
    { key: 'problem-resolution-termination', x: 0, y: 9, w: 6, h: 6, expandedH: 6 },
    { key: 'problem-resolution-grounded', x: 6, y: 15, w: 6, h: 5, expandedH: 5 },
    { key: 'problem-resolution-warehousing', x: 12, y: 20, w: 6, h: 6, expandedH: 6 },
    { key: 'problem-resolution-tablemaint', x: 0, y: 27, w: 6, h: 2, expandedH: 2 },
    { key: 'delivery-schedule', x: 12, y: 13, w: 12, h: 7, expandedH: 7 },
    { key: 'tracking', x: 12, y: 6, w: 12, h: 7, expandedH: 7 },
    { key: 'chassis-charges', x: 6, y: 0, w: 6, h: 4, expandedH: 4 },
    { key: 'new-melrose-pros', x: 12, y: 26, w: 12, h: 8, expandedH: 8 },
    { key: 'warehouse-photos', x: 6, y: 23, w: 6, h: 2, expandedH: 2 },
    { key: 'tickets', x: 6, y: 20, w: 6, h: 3, expandedH: 3 },
  ],
  accounting: [
    { key: 'approval', x: 0, y: 0, w: 6, h: 2, expandedH: 2 },
    { key: 'pending-invoicing', x: 6, y: 0, w: 12, h: 9, expandedH: 9 },
    { key: 'per-diem', x: 18, y: 0, w: 6, h: 10, expandedH: 10 },
    { key: 'additional-cost', x: 0, y: 2, w: 6, h: 3, expandedH: 3 },
  ],
});

// GridStack sizing for the panel grid. Shared by init and the double-click
// fit-to-content calc so the snap math matches the rendered layout. Kept finer
// (24 cols / 40px rows) than the drill-down grid so double-click fit hugs content.
const PANEL_COLUMNS = 24;
const PANEL_CELL_HEIGHT = 40;
const PANEL_MARGIN = 4;

// Keys for the matrix-style panels (not CountPanels).
const SCHEDULE_PANEL_KEY = 'delivery-schedule';
const TRACKING_PANEL_KEY = 'tracking';
// The Exports Summary panel is a 3-row × 11-column grid (Past Due / Today / Tomorrow ×
// exception categories) fed by its own /exports-summary-counts endpoint, fetched in
// parallel. Its cells drill down through the shared grid host (kind 'exports-summary').
// Belongs to the Exports workspace (see PANEL_WORKSPACES).
const EXPORTS_SUMMARY_PANEL_KEY = 'exports-summary';
// The Exports Board is the large grouped booking table (legacy dash_CSR_exports board).
// Fed by its own /exports-board endpoint (one row per booking key with status counts),
// fetched in parallel. Its Cutoff ⇄ Delivery By toggle (group_by) re-runs the fetch.
// Belongs to the Exports workspace (see PANEL_WORKSPACES).
const EXPORTS_BOARD_PANEL_KEY = 'exports-board';
// The Invoicing Statistics grid is the Accounting workspace's top panel: a view-only
// Daily/Weekly/Monthly × Previous/Current/Goal read-out from /invoicing-stats. No
// global filters, no clickable cells. Belongs to the Accounting workspace.
const INVSTATS_PANEL_KEY = 'invoicing-stats';
// The Invoicing Statistics panel is gated behind a component permission, hidden by
// default — an admin must grant a group View/Edit access for it to appear at all.
const INVSTATS_PERMISSION_ID = 'lc-invoicing-stats';
const INVSTATS_PERMISSION_NAME = 'Invoicing Statistics';
// The Per Diem grid (legacy dash_CSR_PerDiem) is an Accounting workspace panel with two
// value columns per bucket (PRO count + per-diem days), fed by /per-diem-counts. Its rows
// drill the standard pro view (kind 'per-diem'); the Approved/Pending buckets are archived
// (ordersall), so their drill endpoints read ordersall/legsall.
const PERDIEM_PANEL_KEY = 'per-diem';
// Bucket key -> label, for building the drill-down title (mirrors PerDiemPanel ROWS).
const PERDIEM_LABELS: { [key: string]: string } = {
  'lc-pd-not-approved': 'Not Approved',
  'lc-pd-not-approved-terminated': 'Not Approved · Terminated',
  'lc-pd-approved': 'Approved',
  'lc-pd-gulf-winds': 'Gulf Winds',
  'lc-pd-customer': 'Customer',
  'lc-pd-gwi-customer': 'GWI/Customer',
  'lc-pd-dispute': 'Dispute',
  'lc-pd-ssline-waived': 'SSLine Waived',
  'lc-pd-pending-final-app': 'Pending Final App',
};
// The Additional Cost grid (legacy dash_CSR_AddCost, simplified to one "No Pay Status"
// bucket) is an Accounting workspace panel with two value columns — record count + total
// cost — fed by /additional-cost-counts. Its drill is additional-cost-level (its own flat
// column set), table-only, like Pending Invoicing.
const ADDCOST_PANEL_KEY = 'additional-cost';
const ADDCOST_DRILL_KEY = 'additional-cost-drill';
// The Tickets panel (Main workspace, legacy dash_CSR_TroubleTicket) counts active
// trouble tickets — Driver / New / Pending — via its own /trouble-ticket-counts
// endpoint. Unlike the other panels its buckets DON'T drill into an order results
// table: a click opens the TTs & Service Failures page in a new tab scoped to that
// bucket's exact trouble-ticket recnos (the dashboard's customer/bill-to/etc. filters
// can't be reproduced by that page's LC-only filter, so it passes recnos).
const TICKETS_PANEL_KEY = 'tickets';
const TICKETS_PAGE = '/scheduling/tts-and-service-failures';
const TT_DRIVER_KEY = 'lc-tt-driver';
const TT_NEW_KEY = 'lc-tt-new';
const TT_PENDING_KEY = 'lc-tt-pending';
const TT_LABELS: { [key: string]: string } = {
  [TT_DRIVER_KEY]: 'Driver', [TT_NEW_KEY]: 'New', [TT_PENDING_KEY]: 'Pending',
};
const TICKETS_CONFIG: CountPanelConfig = {
  key: TICKETS_PANEL_KEY,
  title: 'Tickets',
  columns: [{
    rows: [
      { key: TT_DRIVER_KEY, label: 'Driver', color: 'red' },
      { key: TT_NEW_KEY, label: 'New', color: 'yellow' },
      { key: TT_PENDING_KEY, label: 'Pending', color: 'yellow' },
    ],
  }],
};
// The Warehousing count panel is fed by its own /warehouse-counts endpoint (its
// warehouse-move queries are heavy), fetched in parallel with /bucket-counts.
const WAREHOUSING_PANEL_KEY = 'problem-resolution-warehousing';
// The Tablemaint panel counts LOCATIONS (not pros) via its own /tablemaint-counts endpoint;
// its one bucket opens the Locations table (filtered to the matching recnos) instead of a
// pro drill-down.
const TABLEMAINT_PANEL_KEY = 'problem-resolution-tablemaint';
const TABLEMAINT_MISSING_COORDS_KEY = 'lc-tablemaint-missing-coords';
// The Chassis Charges panel is a 2x2 grid fed by its own /chassis-charge-counts endpoint
// (its GWI-charged rule is a per-row Python evaluation), fetched in parallel like the
// warehouse counts. Its cells drill down through the shared grid host (kind 'chassis').
const CHASSIS_PANEL_KEY = 'chassis-charges';
// The New Melrose Pros panel is a CountPanel whose rows are built dynamically from its
// own /melrose-counts endpoint (one row per customer with a new Melrose pro), fetched
// in parallel. Its rows drill down through the shared grid host (kind 'melrose').
const MELROSE_PANEL_KEY = 'new-melrose-pros';
// Bucket-key prefix for the dynamic per-customer Melrose rows: `lc-melrose-<customercode>`.
const MELROSE_KEY_PREFIX = 'lc-melrose-';
// The Warehouse Photos panel is a CountPanel whose rows are built dynamically from its
// own /warehouse-photos/unassigned-counts endpoint (one row per warehouse with photos
// not yet assigned to a pro), fetched in parallel. Unlike the other dynamic panels its
// clicks open the Warehouse Photos PAGE in a new tab (no in-dashboard drill).
const WHSEPHOTO_PANEL_KEY = 'warehouse-photos';
const WHSEPHOTO_KEY_PREFIX = 'lc-whsephoto-';        // per-warehouse row: `lc-whsephoto-<code>`
const WHSEPHOTO_TOTAL_KEY = 'lc-whsephoto-total';    // clickable header total
const WHSEPHOTO_PAGE = '/order-management/warehouse-photos';
// The Pending EDI Orders panel embeds the shared EdiBucketPanel (edihandle × Bill To
// buckets of inbound EDI orders awaiting a pro), scoped to the signed-in user's buckets
// via /pending-edi-orders/buckets?lc_username. Not a CountPanel — clicking a bucket opens
// the shared EdiBucketDrill in a drawer (accept/reject/delete + mapping, in context).
const EDI_PANEL_KEY = 'pending-edi-orders';
// Settings/columns key for the schedule cell drill-down (table + cards reuse the
// LC column set and per-table settings, like the bucket drill-downs).
const SCHEDULE_DRILL_KEY = 'schedule-drill';
// Settings/columns key for the Chassis Charges cell drill-down. Its own key (separate
// from SCHEDULE_DRILL_KEY) because it uses the bespoke custom column set, not LC columns.
const CHASSIS_DRILL_KEY = 'chassis-drill';

// The synthetic SF/TT column ids, so the Multi Row View's saved extra-column set
// keeps them (they are not field definitions). Module-level for a stable identity.
const COMPUTED_CARD_FIELDNAMES: ReadonlyArray<string> = COMPUTED_FIELD_OPTIONS.map((o) => o.fieldname);

// Pending Invoicing drill-down is invoice-level (its own /pending-invoicing-invoices
// endpoint), so it has its own flat column set + settings key rather than the LC order
// columns. Columns mirror the legacy accounting invoice query, in order.
const PINV_DRILL_KEY = 'pending-invoicing-drill';
const _money = (v: any) => (v == null || v === '' ? '' : Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
// Columns after the (component-injected) clickable PRO# column.
const PINV_INVOICE_COLUMNS: ColumnDef<any>[] = [
  { accessorKey: 'accessorialnum', header: 'Acc #' },
  { accessorKey: 'quotenumber', header: 'Quote #' },
  { accessorKey: 'csr', header: 'CSR' },
  { accessorKey: 'whsecsr', header: 'Whse CSR' },
  { accessorKey: 'dispatcher', header: 'Dispatcher' },
  { accessorKey: 'cname', header: 'Bill To' },
  { accessorKey: 'custname', header: 'Customer' },
  { accessorKey: 'procompletedate', header: 'PRO Complete', cell: (c) => formatDate(c.getValue() as string) },
  { accessorKey: 'mastercompletedate', header: 'Master Complete', cell: (c) => formatDate(c.getValue() as string) },
  // senttoaccountingdate carries a real clock time since migration 487, so show it when
  // there is one; an exact midnight is "no time recorded" (see hasNoClockTime).
  { accessorKey: 'senttoaccountingdate', header: 'Sent to Acct',
    cell: (c) => {
      const v = c.getValue() as string;
      return hasNoClockTime(v) ? formatDate(v) : formatDateTime(v);
    } },
  { accessorKey: 'mas90code', header: 'Sage Tag' },
  { accessorKey: 'movetype', header: 'Move Type' },
  { accessorKey: 'templatetype', header: 'Rate Zone' },
  { accessorKey: 'container', header: 'Equipment #' },
  { accessorKey: 'bol', header: 'BOL/BKG/Ref #' },
  { accessorKey: 'pickup', header: 'Pickup' },
  { accessorKey: 'delivery', header: 'Delivery' },
  { accessorKey: 'deliverydate', header: 'Delivery Date', cell: (c) => formatDateTime(c.getValue() as string) },
  { accessorKey: 'driver', header: 'Driver', cell: ({ row }) => <>{row.original.driver}<MuleBadge isMule={row.original.driverIsMule} /></>, meta: { exportValue: (row: { original: Record<string, unknown> }) => `${row.original.driver || ''}${row.original.driverIsMule ? ' (Mule)' : ''}` } },
  { accessorKey: 'po', header: 'PO' },
  { accessorKey: 'billtorefno', header: 'Bill To Ref' },
  { accessorKey: 'invoicecomments', header: 'Invoice Comments' },
  { accessorKey: 'mrblwhse', header: 'MR/BL Whse' },
  { accessorKey: 'cargolbs', header: 'Cargo Lbs', cell: (c) => _money(c.getValue()) },
  { accessorKey: 'invoicetotal', header: 'Invoice Total', cell: (c) => _money(c.getValue()) },
  { accessorKey: 'creditamt', header: 'Credit Amt', cell: (c) => _money(c.getValue()) },
  { accessorKey: 'creditreason', header: 'Credit Reason' },
  { accessorKey: 'invoicedate', header: 'Invoice Date', cell: (c) => formatDate(c.getValue() as string) },
  { accessorKey: 'masexportdate', header: 'Sage Date', cell: (c) => formatDate(c.getValue() as string) },
  { accessorKey: 'masterpro', header: 'Invoice #' },
];

// Additional Cost drill columns (after the component-injected clickable PRO# column).
// Additional-cost-level, in the order Lee specified.
const ADDCOST_COLUMNS: ColumnDef<any>[] = [
  { accessorKey: 'costtype', header: 'Cost Type' },
  { accessorKey: 'responsible', header: 'Responsible' },
  { accessorKey: 'scac', header: 'SCAC' },
  { accessorKey: 'createdatetime', header: 'Create Date', cell: (c) => formatDateTime(c.getValue() as string) },
  { accessorKey: 'invoicenumber', header: 'Invoice #' },
  { accessorKey: 'invoicedate', header: 'Invoice Date', cell: (c) => formatDate(c.getValue() as string) },
  { accessorKey: 'qty', header: 'Qty' },
  { accessorKey: 'rate', header: 'Rate', cell: (c) => _money(c.getValue()) },
  { accessorKey: 'total', header: 'Line Total', cell: (c) => _money(c.getValue()) },
  { accessorKey: 'disputereason', header: 'Dispute Reason' },
  { accessorKey: 'comments', header: 'Comments' },
];

// Columns already shown in each bespoke drill (incl. the injected PRO#), so they're
// excluded from the optional order-field catalog to avoid duplicate columns.
const _colKey = (c: ColumnDef<any>) => String((c as { accessorKey?: string }).accessorKey || '');
const PINV_BASE_KEYS = new Set<string>(['pronumber', ...PINV_INVOICE_COLUMNS.map(_colKey)]);
const ADDCOST_BASE_KEYS = new Set<string>(['pronumber', ...ADDCOST_COLUMNS.map(_colKey)]);
// The bespoke drills' fixed columns as availableField entries so the column picker can
// also toggle THEIR visibility (not just add order fields). Listed as existing columns
// (matched by id), so the picker shows + hides them.
type DrillFieldEntry = { fieldname: string; displayname: string; abbreviation: string | null; type: string };
const _baseFieldEntries = (cols: ColumnDef<any>[]): DrillFieldEntry[] => [
  { fieldname: 'pronumber', displayname: 'PRO#', abbreviation: null, type: 'string' },
  ...cols.map((c) => ({
    fieldname: _colKey(c),
    displayname: typeof c.header === 'string' ? c.header : _colKey(c),
    abbreviation: null,
    type: 'string',
  })),
];
const PINV_BASE_FIELD_ENTRIES = _baseFieldEntries(PINV_INVOICE_COLUMNS);
const ADDCOST_BASE_FIELD_ENTRIES = _baseFieldEntries(ADDCOST_COLUMNS);
// Phase 2: shared leg fields offered on both drills — location, appointment, and arrival
// for the pickup (originate), delivery, and terminate legs. 1:1 with the order, so no row
// expansion. Names are field_definitions fieldnames; the backend adds the leg joins.
const DRILL_LEG_FIELDS = [
  'origname', 'o_origapptdate', 'o_arrivaldatetime',     // pickup (originate) leg
  'destname', 'd_destapptdate', 'd_arrivaldatetime',     // delivery leg
  't_destname', 't_destapptdate', 't_arrivaldatetime',   // terminate leg
];
// Per-view optional fields not in field_definitions — a view's own table + subtable
// columns. `expands` marks a row-expanding subtable field (one parent -> many rows),
// flagged in the picker so the user knows. `type` drives column formatting.
interface ViewExtraField { fieldname: string; displayname: string; type: string; expands?: boolean }
const PINV_VIEW_FIELDS: ViewExtraField[] = [
  { fieldname: 'billfromsystem', displayname: 'Bill From', type: 'string' },
  { fieldname: 'donotbillreason', displayname: 'Do Not Bill Status', type: 'string' },
  { fieldname: 'liitem', displayname: 'Line Item', type: 'string', expands: true },
  { fieldname: 'limastype', displayname: 'Line Item GL Code', type: 'string', expands: true },
  { fieldname: 'liqty', displayname: 'Line Item Qty', type: 'number', expands: true },
  { fieldname: 'lirate', displayname: 'Line Item Rate', type: 'decimal', expands: true },
  { fieldname: 'liratetype', displayname: 'Line Item Rate Type', type: 'string', expands: true },
  { fieldname: 'lidescription', displayname: 'Line Item Description', type: 'string', expands: true },
  { fieldname: 'litotal', displayname: 'Line Item Total', type: 'decimal', expands: true },
];
const ADDCOST_VIEW_FIELDS: ViewExtraField[] = []; // fleshed out in the next phase
// fieldname -> metadata, for building columns with clean headers + type formatting.
const VIEW_FIELD_META: Record<string, ViewExtraField> = Object.fromEntries(
  [...PINV_VIEW_FIELDS, ...ADDCOST_VIEW_FIELDS].map((f) => [f.fieldname, f]),
);
// Picker entries: row-expanding fields get a "(pros may dupe)" suffix in the picker only
// (the column header itself stays clean — built separately from VIEW_FIELD_META).
const viewFieldPickerEntries = (fields: ViewExtraField[]) => fields.map((f) => ({
  fieldname: f.fieldname,
  displayname: f.expands ? `${f.displayname} (pros may dupe)` : f.displayname,
  abbreviation: null as string | null,
  type: f.type,
}));

// The list of movable panels, in default flow order. Count panels come from
// LC_COUNT_PANELS; the Delivery Schedule and Tracking grids are distinct matrix
// panels. A panel's `type` selects which body component renders inside its tile.
const LC_PANELS: { key: string; title: string; type: 'counts' | 'schedule' | 'tracking' | 'exports-summary' | 'exports-board' | 'invoicing-stats' | 'per-diem' | 'additional-cost' | 'tickets' | 'chassis' | 'melrose' | 'whsephotos' | 'edi-buckets' }[] = [
  ...LC_COUNT_PANELS.map((p) => ({ key: p.key, title: p.title, type: 'counts' as const })),
  { key: SCHEDULE_PANEL_KEY, title: 'Delivery Schedule', type: 'schedule' as const },
  { key: TRACKING_PANEL_KEY, title: 'Tracking', type: 'tracking' as const },
  { key: EXPORTS_SUMMARY_PANEL_KEY, title: 'Exports Summary', type: 'exports-summary' as const },
  { key: EXPORTS_BOARD_PANEL_KEY, title: 'Exports Board', type: 'exports-board' as const },
  { key: INVSTATS_PANEL_KEY, title: 'Invoicing Statistics', type: 'invoicing-stats' as const },
  { key: PERDIEM_PANEL_KEY, title: 'Per Diem', type: 'per-diem' as const },
  { key: ADDCOST_PANEL_KEY, title: 'Additional Cost', type: 'additional-cost' as const },
  { key: CHASSIS_PANEL_KEY, title: 'Chassis Charges', type: 'chassis' as const },
  { key: MELROSE_PANEL_KEY, title: 'New Melrose Pros', type: 'melrose' as const },
  { key: WHSEPHOTO_PANEL_KEY, title: 'Warehouse Photos', type: 'whsephotos' as const },
  { key: TICKETS_PANEL_KEY, title: 'Tickets', type: 'tickets' as const },
  { key: EDI_PANEL_KEY, title: 'Pending EDI Orders', type: 'edi-buckets' as const },
];

// Panel types whose body is a <CountPanel>. CountPanel reflows its bucket blocks
// through a CSS multi-column track (one column per BLOCK_MIN_WIDTH of tile width),
// so narrowing a tile drops it 3 → 2 → 1 columns and multiplies its content height.
// Those are the tiles the grow-to-fit observer watches; every other panel type is a
// fixed grid/table that owns its own scrolling and must NOT be sized to its content.
const COUNT_PANEL_TYPES = new Set<string>(['counts', 'tickets', 'melrose', 'whsephotos', 'edi-buckets']);

// Which workspace each panel belongs to. The LC dashboard has three fixed workspaces
// (Main='default', Exports='exports', Accounting='accounting'); a panel only renders in
// its assigned workspace, and only that workspace's panels run their count queries.
// Panels not listed here default to the Main workspace.
const PANEL_WORKSPACES: { [panelKey: string]: string } = {
  [EXPORTS_SUMMARY_PANEL_KEY]: 'exports',
  [EXPORTS_BOARD_PANEL_KEY]: 'exports',
  [INVSTATS_PANEL_KEY]: 'accounting',
  'approval': 'accounting',
  'pending-invoicing': 'accounting',
  [PERDIEM_PANEL_KEY]: 'accounting',
  [ADDCOST_PANEL_KEY]: 'accounting',
  [EDI_PANEL_KEY]: 'default',
};
const workspaceForPanel = (panelKey: string): string => PANEL_WORKSPACES[panelKey] || 'default';
// Panels visible in a given workspace, in LC_PANELS order.
const panelsForWorkspace = (workspaceId: string) =>
  LC_PANELS.filter((p) => workspaceForPanel(p.key) === workspaceId);

// Workspace that owns each count bucket (via its panel) — so a bucket's drill-down
// card lives in the same workspace as its panel. Buckets not in a placed panel
// default to Main. Used to scope the auto-generated bucket card configs.
const BUCKET_WORKSPACE: { [bucketKey: string]: string } = (() => {
  const map: { [k: string]: string } = {};
  for (const [panelKey, keys] of Object.entries(PANEL_BUCKET_KEYS_BY_PANEL)) {
    const ws = workspaceForPanel(panelKey);
    for (const k of keys) map[k] = ws;
  }
  return map;
})();

// The three code-defined LC workspaces (Main / Exports / Accounting). They are
// fixed panel workspaces with locked layouts: protected from rename/delete, and
// NOT valid targets for a user-duplicated bucket card — the Exports/Accounting
// panels carry no drill cards, so they'd otherwise read "0/10" and look free.
const LC_DEFAULT_WORKSPACES: Workspace[] = [
  { id: 'default', name: 'Main', order: 0, columnCount: 2 },
  { id: 'exports', name: 'Exports', order: 1, columnCount: 2 },
  { id: 'accounting', name: 'Accounting', order: 2, columnCount: 2 },
];
const LC_SYSTEM_WORKSPACE_IDS = new Set(LC_DEFAULT_WORKSPACES.map((w) => w.id));
const isLcSystemWorkspace = (workspaceId: string) => LC_SYSTEM_WORKSPACE_IDS.has(workspaceId) || workspaceId.startsWith('managed:');

// One per-customer row of the New Melrose Pros panel (from /melrose-counts).
interface MelroseRow {
  customercode: number;
  custname: string;
  count: number;
}

// Warehouse Photos panel counts (from /warehouse-photos/unassigned-counts).
interface WhsePhotoCounts {
  total: number;
  warehouses: { warehouse: string; count: number }[];
}

// Drill-down endpoints per grid-panel kind. The cell-identity fields differ
// (schedule: category/start/end; tracking: row/bucket; chassis: category/state) but the
// Cards/Table response shapes are identical, so one generic drill host serves them all.
const GRID_DRILL_ENDPOINTS: { [kind: string]: { cards: string; table: string } } = {
  schedule: { cards: '/lc-dashboards/schedule-cards', table: '/lc-dashboards/schedule-table' },
  tracking: { cards: '/lc-dashboards/tracking-cards', table: '/lc-dashboards/tracking-table' },
  'exports-summary': { cards: '/lc-dashboards/exports-summary-cards', table: '/lc-dashboards/exports-summary-table' },
  'exports-board': { cards: '/lc-dashboards/exports-board-cards', table: '/lc-dashboards/exports-board-table' },
  'per-diem': { cards: '/lc-dashboards/per-diem-cards', table: '/lc-dashboards/per-diem-table' },
  chassis: { cards: '/lc-dashboards/chassis-charge-cards', table: '/lc-dashboards/chassis-charge-table' },
  melrose: { cards: '/lc-dashboards/melrose-cards', table: '/lc-dashboards/melrose-table' },
};

// Columns the LC bucket drill-down tables show. Starts from the dispatch base set
// plus the LC-specific additions (rail/"Terminal", csr, whsecsr, commodity, and the
// main-invoice billtorefno). All LC buckets share this for now; per-bucket overrides
// can come later. (Source of truth for LC columns — the page sends these as
// base_fields and builds the table columns from them, instead of the shared
// dispatch getBucketFields default.)
const LC_BUCKET_FIELDS: string[] = [
  ...new Set([
    ...BASE_BUCKET_FIELDS,
    'rail',
    'csr',
    'whsecsr',
    'commodity',
    'billtorefno',
  ]),
];

// Extra columns specific to the Import buckets (vessel/voyage, last free day, out-gate,
// per diem start, trace-terminate) — a good starting view for import work.
const LC_IMPORT_EXTRA_FIELDS = [
  'vessel', 'voyage', 'lastfreedaydate', 'outgatedatetime', 'perdiemstartdate', 'traceterminatedatetime',
];

// The drill-down column set for a given LC bucket: the shared LC base, plus any
// per-section extras (Import buckets get the import columns). Users' own
// add/hide/rearrange choices persist on top of this starting set.
// Missing / Out-of-Order Events drills down to the event timeline (milestones in the
// order they should occur) rather than the usual LC columns, so the missing/out-of-
// sequence milestone is visible at a glance. Order here IS the column order; kept in
// sync with the backend bucket's _MOE_FIELDS.
const LC_MOE_FIELDS: string[] = [
  'pronumber', 'csr', 'mode', 'movetype', 'rail', 'scac',
  'vesselsaildatetime', 'vesselarrivaldatetime', 'traindepartdatetime',
  'arrivalgroundeddatetime', 'availablegoodtogodatetime', 'outgatedatetime',
];

// Parse a milestone value to a comparable day number (YYYYMMDD), or null when blank /
// zero-date. Handles ISO ('YYYY-MM-DD[ HH:MM:SS]') and MM/DD[/YYYY] forms.
function moeParseDay(v?: string | null): number | null {
  if (!v) return null;
  const s = String(v).trim();
  if (!s || s.startsWith('0000-00-00')) return null;
  const datePart = s.split(' ')[0].split('T')[0];
  let y = 0, m = 0, d = 0;
  if (datePart.includes('-')) {
    [y, m, d] = datePart.split('-').map(Number);
  } else if (datePart.includes('/')) {
    const segs = datePart.split('/').map(Number);
    m = segs[0]; d = segs[1];
    y = segs.length >= 3 && segs[2] ? (segs[2] < 100 ? 2000 + segs[2] : segs[2]) : new Date().getFullYear();
  } else {
    return null;
  }
  if (!y || !m || !d) return null;
  return y * 10000 + m * 100 + d;
}

// Mirrors the legacy fv_datecomparecolor: flag (red) when the earlier milestone (d1)
// is missing while the next one (d2) happened, or when d1 is dated after d2 (out of
// order). If the next milestone (d2) is itself missing, nothing is flagged. Date-level.
function moeOutOfOrder(d1?: string | null, d2?: string | null): boolean {
  const p2 = moeParseDay(d2);
  if (p2 === null) return false;
  const p1 = moeParseDay(d1);
  if (p1 === null) return true;
  return p1 > p2;
}

// Per-cell out-of-order test for the Missing / Out-of-Order Events table: each
// milestone is compared to the milestone that should come next (mode-aware), exactly
// like the legacy fv_problemresolutiontracing. Out-Gate (last event) is never flagged.
const MOE_HIGHLIGHT: { [field: string]: (row: any) => boolean } = {
  vesselsaildatetime: (r) => moeOutOfOrder(r.vesselsaildatetime, r.vesselarrivaldatetime),
  vesselarrivaldatetime: (r) =>
    r.mode === 'MLB' ? moeOutOfOrder(r.vesselarrivaldatetime, r.traindepartdatetime)
      : r.mode === 'AWR' ? moeOutOfOrder(r.vesselarrivaldatetime, r.arrivalgroundeddatetime)
        : false,
  traindepartdatetime: (r) =>
    r.mode === 'MLB' ? moeOutOfOrder(r.traindepartdatetime, r.arrivalgroundeddatetime) : false,
  arrivalgroundeddatetime: (r) => moeOutOfOrder(r.arrivalgroundeddatetime, r.availablegoodtogodatetime),
  availablegoodtogodatetime: (r) => moeOutOfOrder(r.availablegoodtogodatetime, r.outgatedatetime),
};

// The "No Pickup Number" bucket — the one drill where the pickup number
// (o_origapptnumber = originate-leg origapptnumber) is force-shown and inline-editable.
const NO_PICKUP_BUCKET = 'lc-oe-no-pickup-number';
// The "Pregate Errors" bucket — its drill always leads with the Pregate Error column
// (field_definitions.pregateerror, which coalesces the loaded and empty refusals).
const PREGATE_ERRORS_BUCKET = 'lc-oe-pregate-errors';
// Problem Resolution - Scheduling bucket that gets the "Clear All Leg Appt Dates" button.
const PAST_DUE_NOT_DISPATCHED_BUCKET = 'lc-sched-past-due-not-dispatched';

// Editable pickup-number cell (mirrors LcResolutionBoard's TextCell): keeps keystrokes
// local and commits on blur/Enter so typing doesn't rebuild the column array per keypress.
// Resyncs when `value` changes (bulk edit / discard / refresh). Right-click + drag to select
// a range bubbles to the TanTable cell handlers (no stopPropagation here) → BulkEditModal.
const PickupCell: React.FC<{
  pro: string;
  value: string;
  edited: boolean;
  onCommit: (v: string) => void;
}> = ({ pro, value, edited, onCommit }) => {
  const [val, setVal] = useState(value);
  useEffect(() => { setVal(value); }, [value]);
  // Up/Down arrows hop to the pickup cell in the adjacent row (spreadsheet-style vertical
  // nav) instead of moving the text cursor. Skips non-data rows (group/expanded) that carry
  // no pickup input. Blurring commits any pending edit — which re-renders and REPLACES the
  // target input node — so we capture the target's PRO first and re-query + focus it on the
  // next frame (after the commit's re-render), not the now-stale node reference.
  const moveVertically = (e: React.KeyboardEvent<HTMLInputElement>, dir: 'nextElementSibling' | 'previousElementSibling') => {
    e.preventDefault();
    const input = e.currentTarget;
    const row = input.closest('tr');
    if (!row) return;
    let sib = row[dir] as Element | null;
    while (sib && !sib.querySelector('input[data-pickup-cell]')) sib = sib[dir] as Element | null;
    const targetInput = sib?.querySelector('input[data-pickup-cell]') as HTMLInputElement | null;
    if (!targetInput) return;
    const targetPro = targetInput.getAttribute('data-pickup-pro');
    input.blur(); // commit the current cell (onBlur), which may re-render the grid
    requestAnimationFrame(() => {
      const el = (targetPro
        ? document.querySelector(`input[data-pickup-cell][data-pickup-pro="${targetPro}"]`)
        : targetInput) as HTMLInputElement | null;
      if (el) { el.focus(); el.select(); }
    });
  };
  return (
    <input
      type="text"
      value={val}
      maxLength={100}
      data-pickup-cell="true"
      data-pickup-pro={pro}
      onChange={(e) => setVal(e.target.value)}
      onBlur={() => { if (val !== value) onCommit(val); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') { e.currentTarget.blur(); return; }
        if (e.key === 'ArrowDown') moveVertically(e, 'nextElementSibling');
        else if (e.key === 'ArrowUp') moveVertically(e, 'previousElementSibling');
      }}
      placeholder="—"
      className={`w-full px-1 py-0.5 text-xs border border-transparent hover:border-slate-300 dark:hover:border-slate-600 rounded bg-transparent ${edited ? 'bg-amber-50 dark:bg-amber-900/40' : ''}`}
    />
  );
};

function lcBucketFields(bucketKey: string): string[] {
  // One bucket gets a bespoke column set (the milestone timeline).
  if (bucketKey === 'lc-tracing-missing-events') return [...LC_MOE_FIELDS];
  // Tracing & Order Entry problem buckets surface import-style columns (LFD, per diem,
  // out-gate, vessel), so they share the Import column set.
  const isImportLike = bucketKey.startsWith('lc-imports')
    || bucketKey.startsWith('lc-tracing')
    || bucketKey.startsWith('lc-oe')
    || bucketKey.startsWith('lc-sched')
    || bucketKey.startsWith('lc-term')
    || bucketKey.startsWith('lc-grounded')
    || bucketKey.startsWith('lc-whse')
    || bucketKey.startsWith('lc-offhire')
    || bucketKey.startsWith('lc-streetturn')
    || bucketKey.startsWith('lc-preadvise')
    || bucketKey.startsWith('lc-railbill')
    || bucketKey.startsWith('lc-dropped')
    || bucketKey.startsWith('lc-missingli');
  const extras = isImportLike ? LC_IMPORT_EXTRA_FIELDS : [];
  const fields = [...new Set([...LC_BUCKET_FIELDS, ...extras])];
  // No Pickup Number always shows the Pickup Number (PU#) column for everyone — it's the
  // field this bucket is about and the one users edit inline — defaulted immediately after
  // the pro number. (Backend registration mirrors its presence in the fallback field set.)
  if (bucketKey === NO_PICKUP_BUCKET) {
    const rest = fields.filter(f => f !== 'o_origapptnumber');
    const pnIdx = rest.indexOf('pronumber');
    rest.splice(pnIdx >= 0 ? pnIdx + 1 : 0, 0, 'o_origapptnumber');
    return rest;
  }
  // Pregate Errors always shows the error text right after the pro number — it's the
  // field this bucket is about. (Backend registration mirrors it in the fallback set.)
  if (bucketKey === PREGATE_ERRORS_BUCKET) {
    const rest = fields.filter(f => f !== 'pregateerror');
    const pnIdx = rest.indexOf('pronumber');
    rest.splice(pnIdx >= 0 ? pnIdx + 1 : 0, 0, 'pregateerror');
    return rest;
  }
  return fields;
}

function LCDashboardsContent() {
  // Permissions mode context
  const { permissionsMode, setPageComponents } = usePermissionsMode();
  const [showManagedWorkspaces, setShowManagedWorkspaces] = useState(false);
  const manageWorkspacesAccess = useResolvedAccessLevel('lc-manage-required-workspaces', true);
  // Resolved access for the permission-gated Invoicing Statistics panel (0 = hidden).
  const invStatsAccess = useResolvedAccessLevel(INVSTATS_PERMISSION_ID, true);
  const { columnCount, setColumnCount, setCustomActions } = useCustomizeLayoutMode();
  const { setDispatchButton } = useDispatchFooter();
  // "+ New Order" toolbar button (design doc §10) — shares the same flow +
  // gate as SearchResults' header button and the Order Management nav item.
  const canCreateOrder = useCanCreateOrder();
  const { openCreateOrder } = useCreateOrderFlow();
  const { showGridHighlight, showGridLabels, showGridBorders } = useDisplayPreferences();
  const { showToast } = useToast();

  // Field definitions for dynamic columns
  const { fieldDefinitions, loading: fieldDefinitionsLoading, error: fieldDefinitionsError, refetch: refetchFieldDefinitions } = useFieldDefinitions();

  // Trigger refetch if field definitions failed to load or are empty after loading completed
  useEffect(() => {
    if (!fieldDefinitionsLoading && fieldDefinitions.length === 0 && !fieldDefinitionsError) {
      // Field definitions didn't load but no error - try again
      refetchFieldDefinitions();
    }
  }, [fieldDefinitionsLoading, fieldDefinitions.length, fieldDefinitionsError, refetchFieldDefinitions]);

  // All fields available for dynamic columns (only displayable fields)
  const availableFields = useMemo(() => {
    return [
      // Service Failure (SF) + Trouble Ticket (TT) computed columns — not real DB
      // fields; enriched server-side and rendered via buildComputedColumnDefs.
      ...COMPUTED_FIELD_OPTIONS,
      ...fieldDefinitions
        .filter(f => f.is_displayable)
        .map(f => ({
          fieldname: f.fieldname,
          displayname: f.displayname,
          abbreviation: f.abbreviation,
          type: f.type,
          // Backend-provided (app.core.row_expanding): drives the TanTable column
          // picker "(pros may dupe)" warning. LC now skips GROUP BY o.pronumber when
          // one of these is displayed (execute_bucket_query), so pros intentionally dupe.
          expands: !!f.expands,
        })),
    ];
  }, [fieldDefinitions]);

  // Optional extra columns for the bespoke invoice-/additionalcost-level drills.
  // Phase 1: pure ORDER fields (orderscurrent, no join) — both views already join the
  // order, so these don't change the grain. (Legs, then per-view table/subtable fields,
  // come next.) Each view excludes the columns it already shows.
  // join_key 'orderscurrent' is the base order join (alias `o`) — i.e. pure order
  // columns, present in both drills. Fields on other aliases (dispatchers, reuse_origin)
  // need a join the bespoke views don't have, so they're left out (legs come in Phase 2).
  const drillOrderFields = useMemo(
    () => availableFields.filter((f) => {
      const def = fieldDefinitions.find((d) => d.fieldname === f.fieldname);
      return def?.tablename === 'orderscurrent' && def?.join_key === 'orderscurrent';
    }),
    [availableFields, fieldDefinitions],
  );
  // The shared leg fields (Phase 2), in pickup→delivery→terminate display order.
  const drillLegFields = useMemo(
    () => DRILL_LEG_FIELDS
      .map((fn) => availableFields.find((f) => f.fieldname === fn))
      .filter((f): f is NonNullable<typeof f> => !!f),
    [availableFields],
  );
  // Order + leg fields, shared by both drills.
  const drillSharedFields = useMemo(() => [...drillOrderFields, ...drillLegFields], [drillOrderFields, drillLegFields]);

  // Base columns first (so the picker can hide them), then the addable shared fields.
  const pinvAvailableFields = useMemo(
    () => [
      ...PINV_BASE_FIELD_ENTRIES,
      ...drillSharedFields.filter((f) => !PINV_BASE_KEYS.has(f.fieldname)),
      ...viewFieldPickerEntries(PINV_VIEW_FIELDS),
    ],
    [drillSharedFields],
  );
  const addcostAvailableFields = useMemo(
    () => [
      ...ADDCOST_BASE_FIELD_ENTRIES,
      ...drillSharedFields.filter((f) => !ADDCOST_BASE_KEYS.has(f.fieldname)),
      ...viewFieldPickerEntries(ADDCOST_VIEW_FIELDS),
    ],
    [drillSharedFields],
  );
  // Build TanTable columns for the user-picked extra fields. Reuse generateColumnDef for
  // the shared type-aware rendering (date → MM/DD, datetime → MM/DD HH:MM, number, etc.),
  // but override accessorKey to the raw fieldname — the bespoke endpoints key each extra
  // column by its fieldname (vs generateColumnDef's getApiFieldName remap).
  const buildDrillExtraColumns = useCallback(
    (fields: string[]): ColumnDef<any>[] => fields.map((fn) => {
      // Per-view fields (this view's own table/subtable) use a clean header (no "(expands
      // rows)" suffix) + their declared type; everything else comes from field_definitions.
      const view = VIEW_FIELD_META[fn];
      const def = view
        ? ({ fieldname: fn, displayname: view.displayname, abbreviation: null, type: view.type, is_displayable: true, is_filterable: true } as FieldDefinition)
        : (fieldDefinitions.find((d) => d.fieldname === fn && d.tablename === 'orderscurrent')
          || fieldDefinitions.find((d) => d.fieldname === fn));
      if (!def) return { accessorKey: fn, header: fn };
      return decorateLocationColumn({ ...generateColumnDef(def), accessorKey: fn, id: fn }, fn);
    }),
    [fieldDefinitions],
  );

  // To ensure we reload when making large changes
  const CURRENT_VERSION = '13'; // Bumped for Critical workspace + Trouble Tickets bucket

  // State to track which sections are collapsed
  const [collapsed, setCollapsed] = useState<{ [key: string]: boolean }>({});

  // State for saving overlay
  const [isSaving, setIsSaving] = useState(false);

  // State for trashcan modal
  const [showTrashModal, setShowTrashModal] = useState(false);
  const [trashItems, setTrashItems] = useState<any[]>([]);
  const [trashLoading, setTrashLoading] = useState(false);

  // State for ProDetailsView modal
  const [selectedProNumber, setSelectedProNumber] = useState<string | null>(null);

  // State for duplicate card modal
  const [duplicateCardConfig, setDuplicateCardConfig] = useState<CardConfig | null>(null);

  // State for new workspace modal
  const [showNewWorkspaceModal, setShowNewWorkspaceModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');

  // State for global table controls
  const [showGlobalControls, setShowGlobalControls] = useState(false);
  const [globalResetTrigger, setGlobalResetTrigger] = useState(0);
  const [globalPageSize, setGlobalPageSize] = useState<number | undefined>(undefined);
  const [globalShowFilters, setGlobalShowFilters] = useState<boolean | undefined>(undefined);
  const [globalSuperCompact, setGlobalSuperCompact] = useState<boolean | undefined>(undefined);
  const [globalControlsPosition, setGlobalControlsPosition] = useState<{ top: number; right: number } | null>(null);

  const [globalCardHeight, setGlobalCardHeight] = useState<'sm' | 'md' | 'lg' | undefined>(undefined);
  const globalControlsRef = React.useRef<HTMLDivElement>(null);
  const globalControlsDropdownRef = React.useRef<HTMLDivElement>(null);

  // State for reset layout confirmation
  const [showResetLayoutConfirm, setShowResetLayoutConfirm] = useState(false);

  // GridStack refs
  const gridRef = useRef<HTMLDivElement>(null);
  const gridInstanceRef = useRef<GridStack | null>(null);
  const isGridInitializing = useRef<boolean>(true); // Skip change events during initialization

  // Separate GridStack instance for the movable count panels (counts view).
  const panelGridRef = useRef<HTMLDivElement>(null);
  const panelGridInstanceRef = useRef<GridStack | null>(null);
  const isPanelGridInitializing = useRef<boolean>(true);
  // Workspace whose saved layout has actually been pushed into the live GridStack
  // instance (see applyPanelGeometry). The 'change' handler reads geometry back OFF
  // the grid, so persisting before this matches currentWorkspaceId would write the
  // on-screen default footprints over a saved layout that hasn't landed yet — the
  // mechanism behind the lost LC panel layouts (#33552).
  const panelGeometryAppliedRef = useRef<string | null>(null);
  // Teardown for the panel grid's double-click fit-to-content listener.
  const panelGridCleanupRef = useRef<(() => void) | null>(null);
  // True once the panel GridStack instance exists (it's created in a setTimeout, so
  // effects that need it — e.g. the grow-to-fit ResizeObserver — wait on this).
  const [panelGridReady, setPanelGridReady] = useState(false);

  // GridStack item interface for positioning
  interface GridItem {
    id: string;
    x: number;
    y: number;
    w: number;
    h: number;
  }

  // Default configuration function
  //
  // One locked drill-down card per count-panel bucket (derived from the panel
  // config). Clicking a count row opens its card full-screen; the card's bucket
  // criteria + columns are defined server-side in the backend LC_BUCKETS registry.
  const getDefaultCardConfigs = (): CardConfig[] => {
    return PANEL_CARD_DEFS.map((def, i) => ({
      key: def.key,
      title: def.title,
      column: 0,
      order: i,
      visible: true,
      span: 2,
      workspaceId: BUCKET_WORKSPACE[def.key] || 'default',
      locked: true,
      bucket: def.key,
      filterConditions: [],
      filterView: 'Orders',
    }));
  };

  // Default workspaces. The LC dashboard has three fixed, code-defined workspaces
  // (see LC_DEFAULT_WORKSPACES); each shows only the panels assigned to it in
  // PANEL_WORKSPACES. They're all protected (can't be renamed or deleted) so the
  // panel→workspace mapping stays valid. Return copies so callers can't mutate the
  // module constant (some sort the array in place).
  const getDefaultWorkspaces = (): Workspace[] =>
    LC_DEFAULT_WORKSPACES.map((w) => ({ ...w }));

  // System ("locked") buckets are defined in code (getDefaultCardConfigs) and backed by
  // canonical SQL on the server. Their query is not user-editable: customization happens by
  // duplicating a bucket, never by editing it in place. This map (key -> default config) is
  // the single source of truth for "is this a system bucket?" — used both to hide the filter
  // cog and to scrub any query overrides a user saved back when in-place editing existed.
  const systemBucketDefaults = useMemo(() => {
    const map = new Map<string, CardConfig>();
    getDefaultCardConfigs().forEach(c => {
      if (c.locked) map.set(c.key, c);
    });
    return map;
    // getDefaultCardConfigs is a pure constant factory; the set never changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // State for card configuration - initialize with defaults, will be loaded from DB
  const [cardConfigs, setCardConfigs] = useState<CardConfig[]>(getDefaultCardConfigs());
  const [configsLoaded, setConfigsLoaded] = useState(false);
  // `configsLoaded` is set in a `finally`, so it goes true even when the layout GET
  // failed — which unblocks every save effect while cardConfigs still holds
  // getDefaultCardConfigs(). This flag records that failure so saveLayout can refuse to
  // persist defaults over the user's real layout. Read through a ref: saveLayout is a
  // no-deps useCallback and would otherwise close over a stale value.
  const [layoutLoadFailed, setLayoutLoadFailed] = useState(false);
  const layoutLoadFailedRef = useRef(false);
  useEffect(() => { layoutLoadFailedRef.current = layoutLoadFailed; }, [layoutLoadFailed]);

  // Per-workspace GridStack layout (position + collapse) for the movable count
  // panels. Loaded from / saved to the `lc` layout under `panelLayouts`.
  const [panelLayouts, setPanelLayouts] = useState<{ [workspaceId: string]: PanelLayoutItem[] }>(getDefaultPanelLayouts());
  const panelLayoutsRef = useRef(panelLayouts);
  useEffect(() => { panelLayoutsRef.current = panelLayouts; }, [panelLayouts]);

  // Layouts displaced by a reset or a workspace delete, kept in the same setting so
  // both are undoable — the panel blob is overwritten in place, so without this a reset
  // was unrecoverable (#33552). One entry per workspace; see panelLayoutTrash.
  const [panelTrash, setPanelTrash] = useState<PanelTrashMap<PanelLayoutItem>>({});
  const panelTrashRef = useRef(panelTrash);
  useEffect(() => { panelTrashRef.current = panelTrash; }, [panelTrash]);

  // Move a layout to/from the trash. Both halves come from one call so the live map and
  // the trash can never drift apart, and the refs are updated synchronously because the
  // grid's 'change' handler reads panelLayoutsRef.
  const applyPanelTrashResult = useCallback((result: {
    layouts: { [workspaceId: string]: PanelLayoutItem[] };
    trash: PanelTrashMap<PanelLayoutItem>;
  }) => {
    panelLayoutsRef.current = result.layouts;
    panelTrashRef.current = result.trash;
    setPanelLayouts(result.layouts);
    setPanelTrash(result.trash);
  }, []);

  // ---- Copy a colleague's count-panel configuration -----------------------------
  const [showCopyPanelsModal, setShowCopyPanelsModal] = useState(false);
  const [copySources, setCopySources] = useState<LcPanelLayoutSource[] | null>(null);
  const [copySourcesLoading, setCopySourcesLoading] = useState(false);
  const [copySourcesError, setCopySourcesError] = useState(false);

  const loadCopySources = useCallback(async () => {
    setCopySourcesLoading(true);
    setCopySourcesError(false);
    try {
      setCopySources(await userSettingsService.getLcPanelLayoutSources());
    } catch (err) {
      console.error('Failed to load copyable panel layouts:', err);
      setCopySourcesError(true);
    } finally {
      setCopySourcesLoading(false);
    }
  }, []);

  const [copySourceQuery, setCopySourceQuery] = useState('');

  // Only offer colleagues who have actually arranged their board — copying someone
  // still on the code default achieves nothing. The frontend decides this because it
  // owns the definition of "default" (see isDefaultPanelArrangement).
  const copyableSources = useMemo(() => {
    if (!copySources) return [];
    const defaults = getDefaultPanelLayouts();
    return copySources.filter(
      (source) => !isDefaultPanelArrangement(source.panel_layouts as any, defaults as any),
    );
  }, [copySources]);

  // ~40 colleagues qualify in practice, so the list needs narrowing by name.
  const visibleCopySources = useMemo(() => {
    const q = copySourceQuery.trim().toLowerCase();
    if (!q) return copyableSources;
    return copyableSources.filter((s) => (
      s.display_name.toLowerCase().includes(q) || s.user_email.toLowerCase().includes(q)
    ));
  }, [copyableSources, copySourceQuery]);

  // Panel layouts persist in their OWN user setting (not the typed dashboard-layout
  // endpoint, whose schema would drop this unknown field) — same pattern as
  // lc_dashboard_filters. `panelsLoaded` gates the auto-save so we don't clobber the
  // saved value with the empty initial state before the load lands.
  const [panelsLoaded, setPanelsLoaded] = useState(false);
  // A FAILED load must never mark panels as loaded. `panelsLoaded` unblocks the
  // auto-save effect below, and the state it would save is getDefaultPanelLayouts() —
  // so treating a failure as "loaded" persists the default layout over the user's saved
  // one. Keep saving blocked until a load actually succeeds, and retry on backoff.
  const [panelsLoadFailed, setPanelsLoadFailed] = useState(false);
  const [panelsLoadAttempt, setPanelsLoadAttempt] = useState(0);
  useEffect(() => {
    let cancelled = false;
    const loadPanels = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('lc_dashboard_panels');
        if (cancelled) return;
        const val = setting?.setting_value;
        if (val?.panelLayouts && typeof val.panelLayouts === 'object') {
          setPanelLayouts(val.panelLayouts);
        }
        // Absent on blobs written before the trash existed — readPanelTrash yields {}.
        setPanelTrash(readPanelTrash<PanelLayoutItem>(val?.panelTrash));
        setPanelsLoadFailed(false);
        setPanelsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load panel layouts — panel saving stays blocked:', err);
        setPanelsLoadFailed(true);
      }
    };
    loadPanels();
    return () => { cancelled = true; };
  }, [panelsLoadAttempt]);

  // Retry a failed panel load so a transient blip doesn't leave saving blocked for the
  // whole session (mirrors the tableSettingsLoadFailed retry).
  useEffect(() => {
    if (!panelsLoadFailed) return;
    const timer = setTimeout(() => setPanelsLoadAttempt(a => a + 1), 15000);
    return () => clearTimeout(timer);
  }, [panelsLoadFailed, panelsLoadAttempt]);

  // One writer shape for the setting, shared by the debounced auto-save and the
  // immediate save the workspace-restore path needs before it reloads the page.
  const savePanelSetting = useCallback((
    layouts: { [workspaceId: string]: PanelLayoutItem[] },
    trash: PanelTrashMap<PanelLayoutItem>,
  ) => userSettingsService.saveUserSetting('lc_dashboard_panels', {
    panelLayouts: layouts,
    version: PANEL_GRID_VERSION,
    panelTrash: trash,
  }), []);

  const savePanelsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!panelsLoaded) return;
    if (savePanelsTimeoutRef.current) clearTimeout(savePanelsTimeoutRef.current);
    savePanelsTimeoutRef.current = setTimeout(() => {
      savePanelSetting(panelLayouts, panelTrash)
        .catch((err) => console.error('Failed to save panel layouts:', err));
    }, 500);
    return () => {
      if (savePanelsTimeoutRef.current) clearTimeout(savePanelsTimeoutRef.current);
    };
  }, [panelLayouts, panelTrash, panelsLoaded, savePanelSetting]);

  // State for workspaces
  const [workspaces, setWorkspaces] = useState<Workspace[]>(getDefaultWorkspaces());
  const [currentWorkspaceId, setCurrentWorkspaceId] = useState<string>('default');
  useFavoriteTab(currentWorkspaceId, setCurrentWorkspaceId, workspaces.map(ws => ws.id), configsLoaded, workspaces.find(ws => ws.id === currentWorkspaceId)?.name ?? currentWorkspaceId);
  const [showHiddenWorkspaces, setShowHiddenWorkspaces] = useState<Record<string, boolean>>({});
  const showHidden = showHiddenWorkspaces[currentWorkspaceId] ?? false;

  // Get grid items for current workspace
  // Uses stored GridStack positions if available, otherwise computes from column/order/span
  const currentGridItems = useMemo((): GridItem[] => {
    const workspaceCards = cardConfigs
      .filter(c => c.workspaceId === currentWorkspaceId && isWorkspaceCardShown(c, showHidden));
    workspaceCards.sort((a, b) => {
      // If both have gsY, sort by that; otherwise sort by column then order
      if (a.gsY !== undefined && b.gsY !== undefined) {
        if (a.gsY !== b.gsY) return a.gsY - b.gsY;
        return (a.gsX || 0) - (b.gsX || 0);
      }
      if (a.column !== b.column) return a.column - b.column;
      return a.order - b.order;
    });

    // Track the next available y position for each column (for cards without gsY)
    const columnHeights: { [col: number]: number } = { 0: 0, 1: 0, 2: 0 };

    const items = workspaceCards.map(card => {
      // If GridStack positions are stored AND valid (w and h >= 1), use them directly
      if (card.gsX !== undefined && card.gsY !== undefined && card.gsW !== undefined && card.gsH !== undefined
        && card.gsW >= 1 && card.gsH >= 1) {
        return { id: card.key, x: card.gsX, y: card.gsY, w: card.gsW, h: card.gsH };
      }

      // Otherwise, compute from column/order/span (for initial layout or migration)
      const col = card.column;
      const span = card.span || 1;

      // Map column to x position (each column is 4 grid units in a 12-column grid)
      const x = col * 4;
      // Map span to width (1 span = 4 units, 2 spans = 8 units, 3 spans = 12 units)
      const w = span * 4;
      // Use the current height for this column
      const y = columnHeights[col];
      // Height based on card height setting
      const h = card.cardHeight === 'sm' ? 3 : card.cardHeight === 'md' ? 5 : 7;

      // Update column heights for cards this card spans
      for (let i = 0; i < span; i++) {
        columnHeights[col + i] = Math.max(columnHeights[col + i] || 0, y + h);
      }

      return { id: card.key, x, y, w, h };
    });
    return items;
  }, [cardConfigs, currentWorkspaceId, showHidden]);

  // Count-panel tiles for the current workspace: stored GridStack position +
  // collapse if the user has arranged them, otherwise a default left-to-right flow
  // (wrapping at 12 columns). Once the user moves/collapses anything, the change
  // handler persists every panel, so partial state is only transient.
  const currentPanelItems = useMemo((): PanelLayoutItem[] => {
    const saved = panelLayouts[currentWorkspaceId] || [];
    const savedByKey = new Map(saved.map((i) => [i.key, i]));
    let cursorX = 0;
    let cursorY = 0;
    let rowH = 0;
    return panelsForWorkspace(currentWorkspaceId)
      // The Invoicing Statistics panel is permission-gated and hidden by default —
      // drop it entirely (no tile, no layout slot, no fetch) unless the user has access
      // or we're in permissions-config mode (so admins can still grant it via the cog).
      .filter((panel) => panel.key !== INVSTATS_PANEL_KEY || permissionsMode || invStatsAccess > 0)
      .map((panel) => {
        const s = savedByKey.get(panel.key);
        if (s && s.w >= 1 && s.h >= 1) {
          return { ...s };
        }
        const def = PANEL_GRID_DEFAULTS[panel.key] || { w: PANEL_DEFAULT_W, h: PANEL_DEFAULT_H };
        if (cursorX + def.w > PANEL_COLUMNS) {
          cursorX = 0;
          cursorY += rowH;
          rowH = 0;
        }
        const item: PanelLayoutItem = { key: panel.key, x: cursorX, y: cursorY, w: def.w, h: def.h };
        cursorX += def.w;
        rowH = Math.max(rowH, def.h);
        return item;
      });
  }, [panelLayouts, currentWorkspaceId, permissionsMode, invStatsAccess]);

  // Signature of WHICH panels are present (not their sizes). Changes only when a panel
  // is added/removed — e.g. a permission-gated panel appearing once permissions resolve.
  // The GridStack init effect re-runs on this so newly-mounted tiles get adopted (and
  // laid out) instead of rendering un-positioned until the next workspace switch.
  const panelKeysSig = useMemo(() => currentPanelItems.map((i) => i.key).join('|'), [currentPanelItems]);

  // Signature of WHICH panels are collapsed. The grow-to-fit observer re-runs on it so a
  // tile gets re-measured when its body mounts on expand (a collapsed tile has no body).
  const panelCollapseSig = useMemo(
    () => currentPanelItems.map((i) => `${i.key}:${i.collapsed ? 1 : 0}`).join('|'),
    [currentPanelItems],
  );

  // State for Add Card modal
  const [isAddCardModalOpen, setIsAddCardModalOpen] = useState(false);
  const [newCardTitle, setNewCardTitle] = useState('');
  const [newCardColumn, setNewCardColumn] = useState(0);

  // State for first card creation modal
  const [isFirstCardModalOpen, setIsFirstCardModalOpen] = useState(false);

  // State for editing existing card filters
  const [editingCardFiltersKey, setEditingCardFiltersKey] = useState<string | null>(null);

  // ---------------------------------------------------------------------------
  // In-place bucket filters (the port of V1's per-bucket Custom Filter).
  //
  // A system bucket's query is canonical and not user-editable, but an LC bucket is a
  // working queue you often want to cut down for a few minutes — which is what V1 let
  // people do without cloning anything. These conditions are ANDed onto the bucket's own
  // WHERE for the duration of the visit.
  //
  // DELIBERATELY NOT PART OF cardConfigs, and never written to user_settings. Plain
  // component state is exactly the required lifetime: it survives the 30s auto-refresh
  // and every saveLayout (neither touches it), and it is gone when the page unmounts, so
  // nobody comes back tomorrow to a bucket that is quietly hiding half its work. Putting
  // it on the card config would persist it through saveLayout and break that guarantee.
  //
  // The panel COUNTS stay on the global header filters alone — fetchBucketCounts does not
  // read this — so the board's numbers remain the same for everyone on the floor. The
  // grid therefore disagrees with the tile above it while a filter is on; CardResultPill's
  // "Filtered · N" badge is what accounts for the difference.
  const [bucketFilters, setBucketFilters] = useState<{ [cardKey: string]: FilterCondition[] }>({});
  // Read by the fetchers so they need no dependency on the map: taking one would rebuild
  // fetchCardData/fetchAllCardData on every filter change and churn the polling effects
  // (the same reason cardConfigsRef exists). Written directly on apply, before setState.
  const bucketFiltersRef = useRef<{ [cardKey: string]: FilterCondition[] }>({});
  useEffect(() => { bucketFiltersRef.current = bucketFilters; }, [bucketFilters]);
  // Which drill's filter dialog is open (null = closed). Carries its own label: a grid
  // drill has no cardConfig to look a title up from.
  const [filterTarget, setFilterTarget] = useState<{ key: string; label: string } | null>(null);

  /**
   * Filter key for a grid-panel drill (Schedule, Tracking, Exports, Per Diem, Melrose).
   *
   * Keyed by PANEL KIND, not by cell, matching how `gridDrillExtraByKind` already keys
   * added columns: a filter like "VAN equipment only" is a lens on the panel, so moving
   * between its cells keeps it rather than making you rebuild it each click. Namespaced
   * so it can share the bucketFilters map with the registry buckets (whose keys are
   * bucket keys) without any chance of collision.
   */
  const gridFilterKey = (kind: string) => `grid:${kind}`;

  /**
   * The conditions a card's query actually runs with: the card's own saved filter (custom
   * cards only — a system bucket has none) plus any in-place bucket filter on top.
   * One place so the batch path, the single-card path and the retry button can't drift.
   */
  const conditionsForCard = useCallback(
    (cardKey: string, saved?: FilterCondition[]): FilterCondition[] => [
      ...(saved || []),
      ...(bucketFiltersRef.current[cardKey] || []),
    ],
    [],
  );

  /**
   * One readable line per condition, for the "Filtered · N" badge's hover. Field names
   * are resolved to their display names — `o_origapptnumber` in a tooltip would tell a
   * user nothing about why their bucket is short.
   */
  const describeConditions = useCallback((conditions?: FilterCondition[]): string | undefined => {
    if (!conditions || conditions.length === 0) return undefined;
    return conditions
      .map((c) => {
        const label = fieldDefinitions.find((f) => f.fieldname === c.field)?.displayname || c.field;
        return `• ${label} ${c.compare.toLowerCase()} ${c.values}`.trimEnd();
      })
      .join('\n');
  }, [fieldDefinitions]);

  // State for card data
  const [cardData, setCardData] = useState<{ [cardKey: string]: any[] }>({});
  const [cardDataLoading, setCardDataLoading] = useState<{ [cardKey: string]: boolean }>({});
  // Per-card load error (so a failed card shows a retry-able message instead of
  // an empty table that looks identical to a genuinely empty bucket).
  const [cardErrors, setCardErrors] = useState<{ [cardKey: string]: string | null }>({});
  // What the last response for each card actually covered: whether it reached the all
  // tables (closed orders included) and whether the backend capped it. Response-derived
  // rather than config-derived so the header pill can never describe a scope the rows
  // on screen don't have. Custom-filter cards only; bucket cards never set it.
  const [cardScopeMeta, setCardScopeMeta] = useState<{
    [cardKey: string]: { includeClosed: boolean; truncated: boolean; limit?: number }
  }>({});

  // Pre-fills the Documents view's bulk-email recipient. `account.username` is the
  // MSAL UPN — the same value PermissionContext sends as `user_email` — so it matches
  // what the rest of the app calls "the current user". Read here rather than inside
  // the documents table so that component stays free of an auth dependency (the same
  // split SearchResults makes).
  const { accounts } = useMsal();
  const currentUserEmail = accounts[0]?.username || '';

  // Tables currently repainted with document results, keyed by table (a card key, or a
  // drill kind for the grid-cell drills). Each entry holds the PRO list captured when
  // the user switched — NOT a boolean: the orders grid whose filters produced that
  // scope is no longer rendering, so there is nothing left to re-derive it from.
  // Switching back and re-filtering re-captures on the next switch. The title travels
  // with it because the drills' titles are not card configs.
  // Deliberately not persisted: a detour, not a preference.
  const [docsViewCards, setDocsViewCards] = useState<{
    [tableKey: string]: { pros: number[]; title: string }
  }>({});

  const openCardDocuments = useCallback((tableKey: string, pros: number[], title: string) => {
    if (pros.length === 0) {
      // The documents endpoint requires at least one PRO (min_length=1), so an empty
      // switch would 422. Say so rather than repainting to a dead table.
      showToast({ type: 'info', message: 'No pros in the current view to show documents for.' });
      return;
    }
    if (pros.length > MAX_SEARCH_PROS) {
      // One documents request carries at most this many PROs.
      showToast({
        type: 'warning',
        message: `${pros.length.toLocaleString()} pros in view — the Documents view takes `
          + `at most ${MAX_SEARCH_PROS.toLocaleString()}. Narrow the table first.`,
      });
      return;
    }
    setDocsViewCards(prev => ({ ...prev, [tableKey]: { pros, title } }));
  }, [showToast]);

  const closeCardDocuments = useCallback((tableKey: string) => {
    setDocsViewCards(prev => {
      if (!(tableKey in prev)) return prev;
      const next = { ...prev };
      delete next[tableKey];
      return next;
    });
  }, []);

  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Batch-loaded table settings for all cards (avoids individual API calls per TanTable)
  const [tableSettings, setTableSettings] = useState<{ [settingsKey: string]: any }>({});
  const [tableSettingsLoaded, setTableSettingsLoaded] = useState(false);
  const [tableSettingsLoadFailed, setTableSettingsLoadFailed] = useState(false);
  // Admin-published default layouts for shared buckets, keyed by `lc-card-<bucket>`.
  const [cardDefaults, setCardDefaults] = useState<{ [settingsKey: string]: any }>({});

  // Tab optimization state - track tab counts and selected tabs per card
  const [cardTabCounts, setCardTabCounts] = useState<{ [cardKey: string]: { tab_value: string; count: number }[] }>({});
  const [cardSelectedTabs, setCardSelectedTabs] = useState<{ [cardKey: string]: string }>({});
  const cardSelectedTabsRef = useRef<{ [cardKey: string]: string }>({});

  // Dashboard loading timer
  const [dashboardLoadSeconds, setDashboardLoadSeconds] = useState(0);
  const dashboardLoadStartRef = useRef<number>(Date.now());

  useEffect(() => {
    // Only run timer while dashboard is loading
    if (!configsLoaded || fieldDefinitionsLoading || (fieldDefinitions.length === 0 && !fieldDefinitionsError)) {
      const interval = setInterval(() => {
        setDashboardLoadSeconds(Math.floor((Date.now() - dashboardLoadStartRef.current) / 1000));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [configsLoaded, fieldDefinitionsLoading, fieldDefinitions.length, fieldDefinitionsError]);

  // Ref to track pending API requests for cancellation
  const abortControllersRef = useRef<{ [cardKey: string]: AbortController }>({});
  const bucketCountsAbortControllerRef = useRef<AbortController | null>(null);
  const batchCardsAbortControllerRef = useRef<AbortController | null>(null);

  // Refs to track latest values without causing useCallback recreation
  const cardConfigsRef = useRef(cardConfigs);
  const collapsedRef = useRef(collapsed);
  // saveLayout is defined further down (it closes over other refs), but the
  // column handlers above it need to persist. Route through a ref to avoid the
  // temporal-dead-zone reference. Kept in sync by an effect after saveLayout.
  const saveLayoutRef = useRef<
    ((cards: CardConfig[], immediate?: boolean, onComplete?: () => void) => void) | null
  >(null);

  // Ref to track when we're restoring columnCount from a workspace switch
  // This prevents the save effect from overwriting the workspace's stored columnCount
  const isRestoringColumnCountRef = useRef(false);

  // Keep refs in sync with state
  useEffect(() => {
    cardConfigsRef.current = cardConfigs;
  }, [cardConfigs]);

  useEffect(() => {
    collapsedRef.current = collapsed;
  }, [collapsed]);

  useEffect(() => {
    cardSelectedTabsRef.current = cardSelectedTabs;
  }, [cardSelectedTabs]);

  // State for active tabs per card (multi-tab selection)
  const [cardActiveTabsMulti, setCardActiveTabsMulti] = useState<{ [cardKey: string]: string[] }>({});

  // State for which cards are currently visible (empty set = show all)
  const [visibleCardKeys, setVisibleCardKeys] = useState<Set<string>>(new Set());
  // Store visible card keys per workspace so they persist when switching tabs
  const [workspaceVisibleCards, setWorkspaceVisibleCards] = useState<{ [workspaceId: string]: string[] }>({});

  // All card keys for comparison
  const allCardKeys = PANEL_BUCKET_KEYS;

  // Toggle a card's visibility (only one stat can be selected at a time)
  const toggleCardVisibility = (cardKey: string) => {
    // Opening a bucket takes over the results area — close any open grid cell.
    setGridSel(null);
    setVisibleCardKeys(prev => {
      // If this card is already selected, deselect it (show all)
      if (prev.has(cardKey) && prev.size === 1) {
        return new Set();
      }
      // Otherwise, select only this card
      return new Set([cardKey]);
    });
  };

  // Check if a card should be visible (show all if none selected or all selected)
  const isCardVisible = (cardKey: string): boolean => {
    if (visibleCardKeys.size === 0 || visibleCardKeys.size === allCardKeys.length) {
      return true; // Show all
    }
    return visibleCardKeys.has(cardKey);
  };

  // Check if exactly one card is selected (for full-screen single card mode)
  const isSingleCardSelected = visibleCardKeys.size === 1;

  // A panel-bearing workspace (Main, Exports) with no bucket selected shows its panel
  // board (the counts/grid tiles) instead of the cards/results area. Generalized from
  // the Main workspace so each workspace surfaces its own assigned panels.
  const currentWorkspaceHasPanels = panelsForWorkspace(currentWorkspaceId).length > 0;
  const isMainBoardUnselected = currentWorkspaceHasPanels && visibleCardKeys.size === 0;

  // Page-specific filters (persisted to lc_dashboard_filters + the saved layout).
  // LC Dashboards filters by Order LC (orderscurrent.csr), Customer, Billing Party,
  // Key Account (order's customer), and Dispatch Region (order's dispatcher).
  const [selectedLCs, setSelectedLCs] = useState<string[]>([]);
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedBilltos, setSelectedBilltos] = useState<string[]>([]);
  const [selectedKeyAccounts, setSelectedKeyAccounts] = useState<string[]>([]);
  const [selectedDispatchRegions, setSelectedDispatchRegions] = useState<string[]>([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState<string[]>([]);
  // View mode: false = in-page (full-screen swap), true = right-side drawer.
  // Persisted per-user alongside the filters (loaded/saved in lc_dashboard_filters).
  const [drawerView, setDrawerView] = useState<boolean>(false);
  // Results view: 'table' = LC TanTable columns, 'cards' = searchresults ProResultCard.
  const [resultsView, setResultsView] = useState<'table' | 'cards'>('table');
  const localFiltersLoaded = useRef(false);
  // State twin of the ref: gates every panel fetch until the saved filters
  // restore, so each panel queries once instead of twice per page load.
  const [filtersReady, setFiltersReady] = useState(false);

  // Mass-send (Send All Haz Docs / Send All Rail Bill) — in-flight flag + the
  // sent/skipped summary shown in a results modal after the run.
  // The open drill-down TanTable reports its currently-filtered rows here so a
  // Send All honors the user's in-grid filtering (not the whole bucket). Held in
  // a ref (read at click time) to avoid re-render churn from the filter callback.
  const visibleDrillProsRef = useRef<string[] | null>(null);
  const handleDrillFilteredRows = useCallback((rows: any[]) => {
    visibleDrillProsRef.current = rows
      .map((r) => String(r?.pronumber ?? ''))
      .filter(Boolean);
  }, []);
  // Change Template for All (bucket drill-downs) — reuses the same visible-pro
  // collection as the mass sends. Gated by the order-lc-fields permission (same gate
  // as editing the order + the /pro Change Template trigger).
  const canEditOrderLcFields = useCanEditOrderLcFields();
  // Change All carries its own component since the split (Dispatch/Scheduling hold
  // it for trouble-ticket fan-out); Change Template for All stays on the LC gate.
  const canUseChangeAll = useCanUseChangeAll();
  // Warm the Change All field registry now rather than when the first
  // checkbox mounts, so the drawer's first paint is already the registry's
  // answer and not the built-in fallback. No request for users without the
  // grant — see usePrefetchChangeAllFields.
  usePrefetchChangeAllFields(canUseChangeAll);
  // Inline pickup-number editing (No Pickup Number bucket only). Staged edits keyed by
  // pronumber; a same-as-original commit drops the key so pendingCount stays honest. Saved
  // as individual updateall saves (one per pro) via /change-all/apply-per-pro.
  const [pickupEdits, setPickupEdits] = useState<Record<string, string>>({});
  const [savingPickups, setSavingPickups] = useState(false);
  const pickupEditsCount = Object.keys(pickupEdits).length;

  const setPickupEdit = useCallback((pronumber: string, value: string, original: string) => {
    setPickupEdits(prev => {
      const next = { ...prev };
      if (value === original) delete next[pronumber];
      else next[pronumber] = value;
      return next;
    });
  }, []);

  // Right-click + drag a range in the Pickup Number column → BulkEditModal → apply here.
  const handlePickupBulkEdit = useCallback((columnId: string, rows: any[], value: unknown) => {
    if (columnId !== 'o_origapptnumber') return;
    const v = ((value as string) ?? '').toString();
    setPickupEdits(prev => {
      const next = { ...prev };
      for (const row of rows) {
        const pro = String(row?.pronumber ?? '');
        if (!pro) continue;
        const original = String(row?.o_origapptnumber ?? '');
        if (v === original) delete next[pro];
        else next[pro] = v;
      }
      return next;
    });
  }, []);

  const discardPickupEdits = useCallback(() => setPickupEdits({}), []);

  // Silent card auto-refresh is paused while pickup edits are staged/saving so a background
  // refetch can't wipe in-progress work (read by the refresh interval below).
  const pickupDirtyRef = useRef(false);
  pickupDirtyRef.current = pickupEditsCount > 0 || savingPickups;

  // Drop staged pickup edits when the No Pickup Number bucket is no longer the open drill
  // (closed or switched away) so they don't silently linger into another view.
  const noPickupBucketOpen = isSingleCardSelected && Array.from(visibleCardKeys)[0] === NO_PICKUP_BUCKET;
  useEffect(() => {
    if (!noPickupBucketOpen) setPickupEdits(prev => (Object.keys(prev).length ? {} : prev));
  }, [noPickupBucketOpen]);

  // "Clear All Leg Appt Dates" (Past Due - Not Dispatched bucket): the staged-clear
  // preview drives the confirm modal; null when the modal is closed. Busy covers both
  // the preview fetch and the apply.
  const [clearApptPreview, setClearApptPreview] = useState<ClearApptDatesPreview | null>(null);
  const [clearApptBusy, setClearApptBusy] = useState(false);

  const [ctBulkOpen, setCtBulkOpen] = useState(false);
  const [ctBulkPros, setCtBulkPros] = useState<(number | string)[]>([]);
  // Change All (bucket drill-downs) — the pro-detail drawer opens in change-all
  // mode anchored on the first visible pro, fanning checked edits across the rest.
  // Same visible-pro collection as Change Template for All; null when inactive.
  const [changeAllPros, setChangeAllPros] = useState<(number | string)[] | null>(null);
  const [massSendBusy, setMassSendBusy] = useState(false);
  const [massSendResult, setMassSendResult] = useState<{
    title: string;
    sent: string[];
    skipped: Array<{ pronumber: string; reason: string }>;
    total: number;
    message: string;
  } | null>(null);

  // Drag-to-move for this page's inline modals — one hook per modal, keyed to that
  // modal's own open state so each reopen starts centered.
  const addCardDrag = useModalDrag(isAddCardModalOpen);
  const resetLayoutDrag = useModalDrag(showResetLayoutConfirm);
  const copyPanelsDrag = useModalDrag(showCopyPanelsModal);
  const trashDrag = useModalDrag(showTrashModal);
  const newWorkspaceDrag = useModalDrag(showNewWorkspaceModal);
  const massSendResultDrag = useModalDrag(!!massSendResult);

  // Load local filters from user settings
  useEffect(() => {
    if (localFiltersLoaded.current) return;
    const loadLocalFilters = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('lc_dashboard_filters');
        if (setting?.setting_value) {
          const filters = setting.setting_value;
          if (filters.selectedLCs) setSelectedLCs(filters.selectedLCs);
          if (filters.selectedCustomers) setSelectedCustomers(filters.selectedCustomers);
          if (filters.selectedBilltos) setSelectedBilltos(filters.selectedBilltos);
          if (filters.selectedKeyAccounts) setSelectedKeyAccounts(filters.selectedKeyAccounts);
          if (filters.selectedDispatchRegions) setSelectedDispatchRegions(filters.selectedDispatchRegions);
          if (filters.selectedWarehouses) setSelectedWarehouses(filters.selectedWarehouses);
          if (typeof filters.drawerView === 'boolean') setDrawerView(filters.drawerView);
          if (filters.resultsView === 'table' || filters.resultsView === 'cards') setResultsView(filters.resultsView);
        }
      } catch (error) {
        // Silently fail
      }
      localFiltersLoaded.current = true;
      // Batched with the setters above (same tick): the gated panel effects
      // below run exactly once, with the restored filters. Without the gate
      // every panel query fired twice per page load — once with defaults on
      // mount and again when this restore landed (2026-08-25 sweep).
      setFiltersReady(true);
    };
    loadLocalFilters();
  }, []);

  // Auto-save local filters when they change (debounced)
  const saveLocalFiltersTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!localFiltersLoaded.current) return;

    if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    saveLocalFiltersTimeoutRef.current = setTimeout(async () => {
      try {
        await userSettingsService.saveUserSetting('lc_dashboard_filters', { selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses, drawerView, resultsView });
      } catch (error) {
        console.error('Failed to save local filters:', error);
      }
    }, 500);

    return () => {
      if (saveLocalFiltersTimeoutRef.current) clearTimeout(saveLocalFiltersTimeoutRef.current);
    };
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses, drawerView, resultsView]);

  const [bucketCounts, setBucketCounts] = useState<{ [key: string]: number }>({});
  const [bucketCountsLoading, setBucketCountsLoading] = useState(false);
  // Delivery Schedule grid data (two-week per-day move counts).
  const [scheduleData, setScheduleData] = useState<ScheduleData | null>(null);
  const [scheduleLoading, setScheduleLoading] = useState(false);
  const scheduleAbortControllerRef = useRef<AbortController | null>(null);
  // Tracking grid data (date-type rows x days-out columns).
  const [trackingData, setTrackingData] = useState<TrackingData | null>(null);
  const [trackingLoading, setTrackingLoading] = useState(false);
  const trackingAbortControllerRef = useRef<AbortController | null>(null);
  // Exports Summary grid data (Past Due/Today/Tomorrow rows x exception-category columns).
  // Own endpoint, fetched in parallel; self-skips when the panel is collapsed.
  const [exportsSummaryData, setExportsSummaryData] = useState<ExportsSummaryData | null>(null);
  const [exportsSummaryLoading, setExportsSummaryLoading] = useState(false);
  const exportsSummaryAbortControllerRef = useRef<AbortController | null>(null);
  const exportsSummaryCollapsedRef = useRef<boolean>(false);
  // Exports Board grouped-booking table (own endpoint, fetched in parallel; self-skips
  // when collapsed). group_by drives the rowid date key + the visible date column.
  const [exportsBoardData, setExportsBoardData] = useState<ExportsBoardData | null>(null);
  const [exportsBoardLoading, setExportsBoardLoading] = useState(false);
  const [exportsBoardGroupBy, setExportsBoardGroupBy] = useState<'cutoff' | 'deliverby'>('cutoff');
  const exportsBoardAbortControllerRef = useRef<AbortController | null>(null);
  const exportsBoardCollapsedRef = useRef<boolean>(false);
  // Invoicing Statistics grid (Accounting workspace). View-only, filter-independent;
  // its own /invoicing-stats endpoint, fetched once per workspace visit + on refresh.
  const [invStatsData, setInvStatsData] = useState<InvoicingStatsData | null>(null);
  const [invStatsLoading, setInvStatsLoading] = useState(false);
  const invStatsAbortControllerRef = useRef<AbortController | null>(null);
  const invStatsCollapsedRef = useRef<boolean>(false);
  // Per Diem grid (Accounting workspace). Own /per-diem-counts endpoint (its bucket logic
  // is a per-row Python scan over orderscurrent + ordersall), fetched in parallel with the
  // other panels. Honors the global filters; self-skips when collapsed/off-workspace.
  const [perDiemData, setPerDiemData] = useState<PerDiemData | null>(null);
  const [perDiemLoading, setPerDiemLoading] = useState(false);
  const perDiemAbortControllerRef = useRef<AbortController | null>(null);
  const perDiemCollapsedRef = useRef<boolean>(false);
  // Additional Cost grid (Accounting workspace). Own /additional-cost-counts endpoint,
  // fetched in parallel; honors the global filters. Self-skips when collapsed/off-workspace.
  const [addCostData, setAddCostData] = useState<AdditionalCostData | null>(null);
  const [addCostLoading, setAddCostLoading] = useState(false);
  const addCostAbortControllerRef = useRef<AbortController | null>(null);
  const addCostCollapsedRef = useRef<boolean>(false);
  // The Additional Cost drill's "Invoice #" cell opens the same AdditionalCostItemModal
  // the pro page uses, keyed on the row's pronumber + ac.recno. A save bumps a nonce that
  // re-runs the open drill fetch (and the panel counts) so both reflect the edit.
  const [addCostModal, setAddCostModal] = useState<{ isOpen: boolean; pronumber: number; recno: number | null }>({
    isOpen: false, pronumber: 0, recno: null,
  });
  const [gridDrillNonce, setGridDrillNonce] = useState(0);
  // User-picked extra display columns for the bespoke drills (Phase 1: order fields).
  // A change re-runs the open drill fetch (the lists are in its effect deps).
  const [pinvExtraFields, setPinvExtraFields] = useState<string[]>([]);
  const [addcostExtraFields, setAddcostExtraFields] = useState<string[]>([]);
  // The panel grid drill (Schedule / Per Diem / Tracking / Exports / Melrose) has no
  // card config, so its added-field list is persisted here too. Keyed by panel kind
  // so each drill's columns are customized independently (the TanTable layout is
  // likewise keyed per kind — see drillKey in renderGridDrillBody).
  const [gridDrillExtraByKind, setGridDrillExtraByKind] = useState<Record<string, string[]>>({});
  // The bespoke Pending-Invoicing / Additional-Cost drills have no card config, so
  // their added-field lists (unlike the standard cards') aren't persisted by TanTable's
  // settings — additionalFields isn't in buildSettingsData. Persist them in their own
  // user setting so add/remove sticks per user (rearrange/hide of the preset columns
  // already persists via each drill's settingsKey). `drillFieldsLoaded` gates the
  // auto-save so the empty initial state doesn't clobber the saved value before load.
  const [drillFieldsLoaded, setDrillFieldsLoaded] = useState(false);
  useEffect(() => {
    const loadDrillFields = async () => {
      try {
        const setting = await userSettingsService.getUserSetting('lc_dashboard_drill_fields');
        const val = setting?.setting_value;
        if (val && typeof val === 'object') {
          if (Array.isArray(val.pinv)) setPinvExtraFields(val.pinv);
          if (Array.isArray(val.addcost)) setAddcostExtraFields(val.addcost);
          // griddrill is a { [panelKind]: string[] } map (per-panel added columns).
          if (val.griddrill && typeof val.griddrill === 'object' && !Array.isArray(val.griddrill)) {
            setGridDrillExtraByKind(val.griddrill);
          }
        }
      } catch {
        // Non-fatal — fall back to the preset columns only.
      }
      setDrillFieldsLoaded(true);
    };
    loadDrillFields();
  }, []);

  const saveDrillFieldsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (!drillFieldsLoaded) return;
    if (saveDrillFieldsTimeoutRef.current) clearTimeout(saveDrillFieldsTimeoutRef.current);
    saveDrillFieldsTimeoutRef.current = setTimeout(() => {
      userSettingsService
        .saveUserSetting('lc_dashboard_drill_fields', { pinv: pinvExtraFields, addcost: addcostExtraFields, griddrill: gridDrillExtraByKind })
        .catch((err) => console.error('Failed to save LC drill fields:', err));
    }, 500);
    return () => {
      if (saveDrillFieldsTimeoutRef.current) clearTimeout(saveDrillFieldsTimeoutRef.current);
    };
  }, [pinvExtraFields, addcostExtraFields, gridDrillExtraByKind, drillFieldsLoaded]);
  // Tickets panel (Main workspace). Own /trouble-ticket-counts endpoint; the response
  // carries each bucket's trouble-ticket recnos so a click can open the TTs & Service
  // Failures page scoped to exactly those tickets. Self-skips when off-workspace.
  const [ticketCounts, setTicketCounts] = useState<{ [key: string]: number }>({});
  const [ticketRecnos, setTicketRecnos] = useState<{ [key: string]: number[] }>({});
  const [ticketLoading, setTicketLoading] = useState(false);
  const ticketAbortControllerRef = useRef<AbortController | null>(null);
  const ticketFetchSkipRef = useRef<boolean>(false);
  // Chassis Charges 2x2 grid data (own endpoint, fetched in parallel; its GWI-charged
  // rule is a per-row Python evaluation). Self-skips when the panel is collapsed.
  const [chassisData, setChassisData] = useState<ChassisData | null>(null);
  const [chassisLoading, setChassisLoading] = useState(false);
  const chassisAbortControllerRef = useRef<AbortController | null>(null);
  const chassisCollapsedRef = useRef<boolean>(false);

  // New Melrose Pros — own endpoint (fetched in parallel). Rows are built DYNAMICALLY
  // from the response (one per customer with a new Melrose pro), so this can't live in
  // the static registry/CountPanel config. Self-skips when off-workspace.
  const [melroseData, setMelroseData] = useState<MelroseRow[] | null>(null);
  const [melroseLoading, setMelroseLoading] = useState(false);
  const melroseAbortControllerRef = useRef<AbortController | null>(null);
  const melroseFetchSkipRef = useRef<boolean>(false);

  // Warehouse Photos — own endpoint (fetched in parallel). Rows are built DYNAMICALLY
  // (one per warehouse with un-assigned photos). Clicks open the Warehouse Photos page
  // in a new tab (no in-dashboard drill). Self-skips when off-workspace.
  const [whsePhotoData, setWhsePhotoData] = useState<WhsePhotoCounts | null>(null);
  const [whsePhotoLoading, setWhsePhotoLoading] = useState(false);
  const whsePhotoAbortControllerRef = useRef<AbortController | null>(null);
  const whsePhotoFetchSkipRef = useRef<boolean>(false);

  // Warehousing panel counts (own endpoint, fetched in parallel). Merged into bucketCounts
  // so the shared CountPanel reads one map; its own loading flag drives its placeholders.
  const [warehouseCountsLoading, setWarehouseCountsLoading] = useState(false);
  const warehouseAbortControllerRef = useRef<AbortController | null>(null);
  const warehousingFetchSkipRef = useRef<boolean>(false);

  // Pending Invoicing / Invoicing Holds counts — own invoice-level endpoint (per invoice
  // recno, not per order), merged into bucketCounts so the shared CountPanel reads one map.
  const PINV_PANEL_KEY = 'pending-invoicing';
  const [pinvCountsLoading, setPinvCountsLoading] = useState(false);
  const pinvAbortControllerRef = useRef<AbortController | null>(null);
  const pinvFetchSkipRef = useRef<boolean>(false);

  // Tablemaint (Missing GPS Coordinates) — own endpoint, fetched in parallel. Its count
  // merges into bucketCounts; the matching location recnos are held for the click handler,
  // which opens the Locations table filtered to them.
  const [tablemaintLoading, setTablemaintLoading] = useState(false);
  const tablemaintAbortControllerRef = useRef<AbortController | null>(null);
  const tablemaintFetchSkipRef = useRef<boolean>(false);
  const tablemaintRecnosRef = useRef<number[]>([]);

  // Open grid-panel cell drill-down (Delivery Schedule, Tracking, or Chassis Charges).
  // `body` carries the cell-identity fields the matching endpoint expects (schedule:
  // category/start/end; tracking: row/bucket; chassis: category/state). Opens the same
  // in-dashboard results screen the bucket counts use (page or drawer per drawerView,
  // Cards/Table per resultsView). null = nothing open.
  const [gridSel, setGridSel] = useState<{
    kind: 'schedule' | 'tracking' | 'exports-summary' | 'exports-board' | 'per-diem' | 'chassis' | 'melrose' | 'pending-invoicing' | 'additional-cost';
    title: string;
    body: Record<string, any>;
  } | null>(null);
  const [gridDrillRows, setGridDrillRows] = useState<ProSearchData[]>([]);   // Cards view
  const [gridDrillRowsLoading, setGridDrillRowsLoading] = useState(false);
  const [gridDrillTable, setGridDrillTable] = useState<any[]>([]);            // Table view
  const [gridDrillTableLoading, setGridDrillTableLoading] = useState(false);
  const gridDrillAbortRef = useRef<AbortController | null>(null);
  // The in-grid filtered-rows snapshot (visibleDrillProsRef) is now shared by BOTH
  // drill hosts — count-panel buckets and grid drills. Clear it whenever the open
  // drill changes so a bulk action (Change All / Change Template / Send All) never
  // reads a stale set from a previously-open drill before the new drill's table
  // reports its rows. Cards view collects from *Rows directly, so it's unaffected.
  useEffect(() => {
    visibleDrillProsRef.current = null;
  }, [gridSel, visibleCardKeys]);
  // Rich card rows (ProSearchData) for the open bucket's Cards view.
  const [cardRows, setCardRows] = useState<ProSearchData[]>([]);
  const [cardRowsLoading, setCardRowsLoading] = useState(false);
  const cardRowsAbortRef = useRef<AbortController | null>(null);

  // ---- Multi Row View extra columns -------------------------------------------
  // The card view's user-added columns — the same field universe the Single Row
  // view's "Add column" offers — persisted per open card-view host (bucket or grid
  // drill) under the `lc-cards-*` key its TanTable layout already saves to. Held
  // APART from the Single Row view's `cardConfig.additionalFields` on purpose: the
  // two views were built with independent layouts, and their column sets differ
  // (the PRO card's static columns already show LFD, Terminal, Cutoff, …), so one
  // selection could not serve both without duplicating columns in the card view.
  // A bucket and a grid drill are never open together (each opener closes the
  // other), so one key covers both; null while no card view is open, which loads
  // and saves nothing. Table-only drills never render cards.
  const cardViewKey = resultsView !== 'cards'
    ? null
    : gridSel
      ? (gridSel.kind === 'pending-invoicing' || gridSel.kind === 'additional-cost')
        ? null
        : gridSel.kind === 'chassis' ? CHASSIS_DRILL_KEY : `${SCHEDULE_DRILL_KEY}-${gridSel.kind}`
      : (visibleCardKeys.size === 1 ? Array.from(visibleCardKeys)[0] : null);
  // Row expansion ON for parity with the Single Row view, which fans a pro across
  // rows when a one-to-many field (invoice line items) is displayed.
  const cardExtras = useAdditionalFieldSelection(
    cardViewKey ? `lc-cards-${cardViewKey}` : null,
    { allowExpanding: true, computedFieldnames: COMPUTED_CARD_FIELDNAMES },
  );
  // String key for effect deps — the array is rebuilt on every selection change.
  const cardExtraKey = cardExtras.additionalFields.join(',');
  const [lcOptions, setLcOptions] = useState<MultiSelectOption[]>([]);
  const [customerOptions, setCustomerOptions] = useState<MultiSelectOption[]>([]);
  const [billtoOptions, setBilltoOptions] = useState<MultiSelectOption[]>([]);
  const [keyAccountOptions, setKeyAccountOptions] = useState<MultiSelectOption[]>([]);
  const [dispatchRegionOptions, setDispatchRegionOptions] = useState<MultiSelectOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<MultiSelectOption[]>([]);

  // Close global controls menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const clickedInButton = globalControlsRef.current?.contains(event.target as Node);
      const clickedInDropdown = globalControlsDropdownRef.current?.contains(event.target as Node);
      if (!clickedInButton && !clickedInDropdown) {
        setShowGlobalControls(false);
      }
    };

    if (showGlobalControls) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showGlobalControls]);

  // Bucket keys for the count request, scoped to the count panels present in the current
  // workspace. currentPanelItems already holds only this workspace's panels, so a count
  // panel missing from it belongs to another workspace and is skipped — that's what keeps
  // the Exports/Accounting workspaces from firing the Main workspace's count queries.
  // COLLAPSED panels are still included: their header carries the roll-up count and
  // severity tone (see isPanelOffWorkspace), which needs the numbers even when the
  // buckets aren't on screen.
  // Held in a ref so fetchBucketCounts stays a stable callback (no re-creation /
  // loading flicker on every collapse/expand) while still reading the live set.
  const activeBucketKeys = useMemo(() => {
    const keys: string[] = [];
    for (const panel of LC_COUNT_PANELS) {
      // Warehousing, Tablemaint, and Pending Invoicing have their own endpoints — keep
      // them out of the shared /bucket-counts request.
      if (panel.key === WAREHOUSING_PANEL_KEY || panel.key === TABLEMAINT_PANEL_KEY || panel.key === PINV_PANEL_KEY) continue;
      if (!currentPanelItems.some((i) => i.key === panel.key)) continue; // not in the current workspace
      keys.push(...(PANEL_BUCKET_KEYS_BY_PANEL[panel.key] || []));
    }
    return keys;
  }, [currentPanelItems]);
  const activeBucketKeysRef = useRef<string[]>(activeBucketKeys);
  activeBucketKeysRef.current = activeBucketKeys;

  // Fetch bucket counts function (extracted for reuse in auto-refresh)
  const fetchBucketCounts = useCallback(async (silent: boolean = false) => {
    // No count panels in this workspace — skip the round-trip.
    const buckets = activeBucketKeysRef.current;
    if (buckets.length === 0) {
      if (!silent) setBucketCountsLoading(false);
      return;
    }
    // If this is a silent background refresh and there's already a request in progress, skip it
    // This prevents canceling user-initiated requests with background refreshes
    if (silent && bucketCountsAbortControllerRef.current) {
      return;
    }

    // Cancel any existing bucket counts request (only for non-silent/user-initiated requests)
    if (bucketCountsAbortControllerRef.current) {
      bucketCountsAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    bucketCountsAbortControllerRef.current = abortController;

    if (!silent) {
      setBucketCountsLoading(true);
    }
    try {
      // The Scheduling and Dropped Containers buckets are the two heaviest groups (per-row
      // leg EXISTS subqueries). Left in the one big SUM(CASE) scan they serialize behind the
      // rest (~6.5s all-expanded). Fire them as their OWN parallel /bucket-counts requests
      // (the endpoint already scopes to the bucket subset) so the three scans run concurrently
      // — wall-clock drops to the slowest single group (~2.2s) instead of their sum. They
      // merge on disjoint keys, same as the parallel /warehouse-counts result.
      const schedKeys = buckets.filter((k) => k.startsWith('lc-sched'));
      const droppedKeys = buckets.filter((k) => k.startsWith('lc-dropped'));
      const mainKeys = buckets.filter((k) => !k.startsWith('lc-sched') && !k.startsWith('lc-dropped'));
      const filterPayload = {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      };
      const requestGroup = async (groupKeys: string[]) => {
        if (groupKeys.length === 0) return;
        const response = await api.post('/lc-dashboards/bucket-counts', {
          buckets: groupKeys,
          ...filterPayload,
        }, { signal: abortController.signal });
        // Merge (don't replace) so concurrent groups + /warehouse-counts don't clobber each
        // other — each owns disjoint keys.
        setBucketCounts((prev) => ({ ...prev, ...(response.data.counts || {}) }));
      };
      await Promise.all([requestGroup(mainKeys), requestGroup(schedKeys), requestGroup(droppedKeys)]);
    } catch (err: any) {
      // Don't log cancelled requests as errors
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to fetch bucket counts:', err);
    } finally {
      // Clean up the abort controller
      bucketCountsAbortControllerRef.current = null;

      if (!silent) {
        setBucketCountsLoading(false);
      }
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch bucket counts when filters or the active workspace change. Count panels live
  // only in the Main workspace, so skip the query entirely on the others.
  useEffect(() => {
    if (!filtersReady) return;
    if (panelsForWorkspace(currentWorkspaceId).some((p) => p.type === 'counts')) fetchBucketCounts();
  }, [fetchBucketCounts, currentWorkspaceId, filtersReady]);

  // Fetch the Delivery Schedule grid (two-week per-day counts). Same global-filter
  // payload as the bucket counts; silent refreshes skip if one is already in flight.
  const fetchScheduleCounts = useCallback(async (silent: boolean = false) => {
    if (silent && scheduleAbortControllerRef.current) return;
    if (scheduleAbortControllerRef.current) scheduleAbortControllerRef.current.abort();

    const abortController = new AbortController();
    scheduleAbortControllerRef.current = abortController;
    if (!silent) setScheduleLoading(true);
    try {
      const response = await api.post('/lc-dashboards/schedule-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setScheduleData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch schedule counts:', err);
    } finally {
      if (scheduleAbortControllerRef.current === abortController) scheduleAbortControllerRef.current = null;
      if (!silent) setScheduleLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch the schedule grid on mount, on filter change, and on workspace switch — only
  // when this panel belongs to the active workspace.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(SCHEDULE_PANEL_KEY) === currentWorkspaceId) fetchScheduleCounts();
  }, [fetchScheduleCounts, currentWorkspaceId, filtersReady]);

  // Fetch the Chassis Charges 2x2 grid from its own endpoint, in parallel. Self-skips when
  // the panel is collapsed (its rule scans a candidate set per row). Same global filters.
  const fetchChassisCharges = useCallback(async (silent: boolean = false) => {
    if (chassisCollapsedRef.current) {
      if (!silent) setChassisLoading(false);
      return;
    }
    if (silent && chassisAbortControllerRef.current) return;
    if (chassisAbortControllerRef.current) chassisAbortControllerRef.current.abort();

    const abortController = new AbortController();
    chassisAbortControllerRef.current = abortController;
    if (!silent) setChassisLoading(true);
    try {
      const response = await api.post('/lc-dashboards/chassis-charge-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setChassisData(response.data?.cells || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch chassis charges:', err);
    } finally {
      if (chassisAbortControllerRef.current === abortController) chassisAbortControllerRef.current = null;
      if (!silent) setChassisLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch chassis charges on mount, on filter change, and on workspace switch — only
  // when this panel belongs to the active workspace.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(CHASSIS_PANEL_KEY) === currentWorkspaceId) fetchChassisCharges();
  }, [fetchChassisCharges, currentWorkspaceId, filtersReady]);

  // Fetch the New Melrose Pros rows (one per customer). Same parallel/abort pattern as
  // the chassis panel; the rows + counts are derived from the response below. Unlike the
  // grid panels this keeps running while COLLAPSED — its collapsed header carries the
  // roll-up count (see isPanelOffWorkspace).
  const fetchMelroseCounts = useCallback(async (silent: boolean = false) => {
    if (melroseFetchSkipRef.current) {
      if (!silent) setMelroseLoading(false);
      return;
    }
    if (silent && melroseAbortControllerRef.current) return;
    if (melroseAbortControllerRef.current) melroseAbortControllerRef.current.abort();

    const abortController = new AbortController();
    melroseAbortControllerRef.current = abortController;
    if (!silent) setMelroseLoading(true);
    try {
      const response = await api.post('/lc-dashboards/melrose-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setMelroseData(response.data?.rows || []);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch new Melrose pros:', err);
    } finally {
      if (melroseAbortControllerRef.current === abortController) melroseAbortControllerRef.current = null;
      if (!silent) setMelroseLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(MELROSE_PANEL_KEY) === currentWorkspaceId) fetchMelroseCounts();
  }, [fetchMelroseCounts, currentWorkspaceId, filtersReady]);

  // --- Pending EDI Orders panel (Main workspace) — the shared EdiBucketPanel. Fetches
  //     ALL pending buckets, then filters client-side by the dashboard's global LC + Bill
  //     To filters (like every other panel). Clicking a bucket opens the shared
  //     EdiBucketDrill on the page or in a drawer (per drawerView), so the LC gets the
  //     board's accept/reject/delete + mapping without leaving the dashboard. ---
  const [ediBuckets, setEdiBuckets] = useState<EdiBucket[]>([]);
  const [ediLoading, setEdiLoading] = useState(false);
  const [ediSelection, setEdiSelection] = useState<EdiDrillSelection | null>(null);
  // The dashboard's LC filter now rides on the REQUEST rather than being applied to the
  // response. EDI ownership is per-order (an LC can hold a handle narrowed by customer /
  // delivery location / move type), so the filter has to change each bucket's COUNT —
  // 13 of the handle's 100 — and only the server can do that. Group values are passed
  // through as-is; the backend expands the tilde-joined member list.
  const ediLcScopeKey = selectedLCs.join(',');
  const fetchEdiBuckets = useCallback((silent: boolean = false) => {
    if (!silent) setEdiLoading(true);
    return getEdiBuckets(ediLcScopeKey ? ediLcScopeKey.split(',') : undefined)
      .then((resp) => setEdiBuckets(resp.data))
      .catch(() => { /* keep the prior buckets on a transient error */ })
      .finally(() => { if (!silent) setEdiLoading(false); });
  }, [ediLcScopeKey]);
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(EDI_PANEL_KEY) === currentWorkspaceId) fetchEdiBuckets();
  }, [fetchEdiBuckets, currentWorkspaceId, filtersReady]);
  // Bill To stays client-side: it is a property of the bucket, so hiding one is the whole
  // operation and its count is unaffected. The LC half is gone from here — see above.
  const filteredEdiBuckets = useMemo(() => {
    const billSet = new Set(selectedBilltos.map(String));
    if (!billSet.size) return ediBuckets;
    return ediBuckets.filter((b) => billSet.has(String(b.billto)));
  }, [ediBuckets, selectedBilltos]);
  // One drill at a time: opening a count/card drill closes the EDI drill (the EDI panel's
  // onSelect clears the others — this covers the reverse).
  useEffect(() => {
    if (gridSel || isSingleCardSelected) setEdiSelection(null);
  }, [gridSel, isSingleCardSelected]);

  // Build the New Melrose Pros CountPanel from the dynamic rows: one row per customer,
  // label = uppercased name, count-chip red (matches the legacy — only non-zero rows are
  // returned). No header — the tile title bar names the panel. The counts map keys the
  // rows by `lc-melrose-<customercode>` so a click routes to that customer's drill-down.
  const melroseConfig = useMemo<CountPanelConfig>(() => ({
    key: MELROSE_PANEL_KEY,
    title: 'New Melrose Pros',
    columns: [
      {
        rows: (melroseData || []).map((r) => ({
          key: `${MELROSE_KEY_PREFIX}${r.customercode}`,
          label: (r.custname || `Customer ${r.customercode}`).toUpperCase(),
          color: 'red' as const,
        })),
      },
    ],
  }), [melroseData]);
  const melroseCounts = useMemo<{ [key: string]: number }>(() => {
    const m: { [key: string]: number } = {};
    for (const r of melroseData || []) m[`${MELROSE_KEY_PREFIX}${r.customercode}`] = r.count;
    return m;
  }, [melroseData]);

  // Fetch Warehouse Photos counts (per-warehouse un-assigned). No global filters apply
  // (photos aren't order-scoped), so it's a simple GET, fired in parallel like the others.
  const fetchWhsePhotoCounts = useCallback(async (silent: boolean = false) => {
    if (whsePhotoFetchSkipRef.current) {
      if (!silent) setWhsePhotoLoading(false);
      return;
    }
    if (silent && whsePhotoAbortControllerRef.current) return;
    if (whsePhotoAbortControllerRef.current) whsePhotoAbortControllerRef.current.abort();

    const abortController = new AbortController();
    whsePhotoAbortControllerRef.current = abortController;
    if (!silent) setWhsePhotoLoading(true);
    try {
      const response = await api.get('/warehouse-photos/unassigned-counts', { signal: abortController.signal });
      setWhsePhotoData(response.data || { total: 0, warehouses: [] });
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch warehouse photo counts:', err);
    } finally {
      if (whsePhotoAbortControllerRef.current === abortController) whsePhotoAbortControllerRef.current = null;
      if (!silent) setWhsePhotoLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(WHSEPHOTO_PANEL_KEY) === currentWorkspaceId) fetchWhsePhotoCounts();
  }, [fetchWhsePhotoCounts, currentWorkspaceId, filtersReady]);

  // Map warehouse code -> "CODE - Description" label using the warehouse filter list.
  const whseLabelByCode = useMemo<{ [code: string]: string }>(() => {
    const m: { [code: string]: string } = {};
    for (const o of warehouseOptions) m[o.value] = o.label;
    return m;
  }, [warehouseOptions]);

  // Build the Warehouse Photos panel: a clickable dark header total over one row per
  // warehouse with un-assigned photos. Clicks open the Warehouse Photos page (new tab).
  const whsePhotoConfig = useMemo<CountPanelConfig>(() => ({
    key: WHSEPHOTO_PANEL_KEY,
    title: 'Warehouse Photos',
    columns: [
      {
        header: { key: WHSEPHOTO_TOTAL_KEY, label: 'Warehouse Photos' },
        rows: (whsePhotoData?.warehouses || []).map((w) => ({
          key: `${WHSEPHOTO_KEY_PREFIX}${w.warehouse}`,
          label: whseLabelByCode[w.warehouse] || w.warehouse,
        })),
      },
    ],
  }), [whsePhotoData, whseLabelByCode]);
  const whsePhotoCounts = useMemo<{ [key: string]: number }>(() => {
    const m: { [key: string]: number } = {};
    if (whsePhotoData) {
      m[WHSEPHOTO_TOTAL_KEY] = whsePhotoData.total;
      for (const w of whsePhotoData.warehouses) m[`${WHSEPHOTO_KEY_PREFIX}${w.warehouse}`] = w.count;
    }
    return m;
  }, [whsePhotoData]);

  // Fetch the Tracking grid (date-type rows x days-out columns). Same global-filter
  // payload as the bucket counts; silent refreshes skip if one is already in flight.
  const fetchTrackingCounts = useCallback(async (silent: boolean = false) => {
    if (silent && trackingAbortControllerRef.current) return;
    if (trackingAbortControllerRef.current) trackingAbortControllerRef.current.abort();

    const abortController = new AbortController();
    trackingAbortControllerRef.current = abortController;
    if (!silent) setTrackingLoading(true);
    try {
      const response = await api.post('/lc-dashboards/tracking-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setTrackingData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch tracking counts:', err);
    } finally {
      if (trackingAbortControllerRef.current === abortController) trackingAbortControllerRef.current = null;
      if (!silent) setTrackingLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch the tracking grid on mount, on filter change, and on workspace switch — only
  // when this panel belongs to the active workspace.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(TRACKING_PANEL_KEY) === currentWorkspaceId) fetchTrackingCounts();
  }, [fetchTrackingCounts, currentWorkspaceId, filtersReady]);

  const fetchExportsSummaryCounts = useCallback(async (silent: boolean = false) => {
    // Skip when the panel is collapsed (it lives only in the Exports workspace).
    if (exportsSummaryCollapsedRef.current) return;
    if (silent && exportsSummaryAbortControllerRef.current) return;
    if (exportsSummaryAbortControllerRef.current) exportsSummaryAbortControllerRef.current.abort();

    const abortController = new AbortController();
    exportsSummaryAbortControllerRef.current = abortController;
    if (!silent) setExportsSummaryLoading(true);
    try {
      const response = await api.post('/lc-dashboards/exports-summary-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setExportsSummaryData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch exports summary counts:', err);
    } finally {
      if (exportsSummaryAbortControllerRef.current === abortController) exportsSummaryAbortControllerRef.current = null;
      if (!silent) setExportsSummaryLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch the Exports Summary grid on mount, on filter change, and on workspace switch —
  // only when this panel belongs to the active workspace (the Exports workspace).
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(EXPORTS_SUMMARY_PANEL_KEY) === currentWorkspaceId) fetchExportsSummaryCounts();
  }, [fetchExportsSummaryCounts, currentWorkspaceId, filtersReady]);

  const fetchExportsBoard = useCallback(async (silent: boolean = false) => {
    // Skip when the panel is collapsed (it lives only in the Exports workspace).
    if (exportsBoardCollapsedRef.current) return;
    if (silent && exportsBoardAbortControllerRef.current) return;
    if (exportsBoardAbortControllerRef.current) exportsBoardAbortControllerRef.current.abort();

    const abortController = new AbortController();
    exportsBoardAbortControllerRef.current = abortController;
    if (!silent) setExportsBoardLoading(true);
    try {
      const response = await api.post('/lc-dashboards/exports-board', {
        group_by: exportsBoardGroupBy,
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setExportsBoardData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch exports board:', err);
    } finally {
      if (exportsBoardAbortControllerRef.current === abortController) exportsBoardAbortControllerRef.current = null;
      if (!silent) setExportsBoardLoading(false);
    }
  }, [exportsBoardGroupBy, selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch the Exports Board on mount, on filter/group-by change, and on workspace switch —
  // only when this panel belongs to the active workspace (the Exports workspace).
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(EXPORTS_BOARD_PANEL_KEY) === currentWorkspaceId) fetchExportsBoard();
  }, [fetchExportsBoard, currentWorkspaceId, filtersReady]);

  const fetchInvStats = useCallback(async (silent: boolean = false) => {
    // Skip when the panel is collapsed (it lives only in the Accounting workspace).
    if (invStatsCollapsedRef.current) return;
    if (silent && invStatsAbortControllerRef.current) return;
    if (invStatsAbortControllerRef.current) invStatsAbortControllerRef.current.abort();

    const abortController = new AbortController();
    invStatsAbortControllerRef.current = abortController;
    if (!silent) setInvStatsLoading(true);
    try {
      // View-only + filter-independent: no global-filter payload.
      const response = await api.post('/lc-dashboards/invoicing-stats', {}, { signal: abortController.signal });
      setInvStatsData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch invoicing stats:', err);
    } finally {
      if (invStatsAbortControllerRef.current === abortController) invStatsAbortControllerRef.current = null;
      if (!silent) setInvStatsLoading(false);
    }
  }, []);

  // Fetch Invoicing Statistics on mount and on workspace switch — only when its panel
  // belongs to the active workspace (the Accounting workspace).
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(INVSTATS_PANEL_KEY) === currentWorkspaceId) fetchInvStats();
  }, [fetchInvStats, currentWorkspaceId, filtersReady]);

  // Fetch the Warehousing panel counts from its own endpoint, in parallel with the other
  // panels. Runs even while the panel is collapsed — its collapsed header carries the
  // roll-up count (see isPanelOffWorkspace) — and self-skips only when off-workspace. Same
  // global filters as the bucket counts; results merge into bucketCounts (disjoint keys).
  const fetchWarehouseCounts = useCallback(async (silent: boolean = false) => {
    if (warehousingFetchSkipRef.current) {
      if (!silent) setWarehouseCountsLoading(false);
      return;
    }
    if (silent && warehouseAbortControllerRef.current) return;
    if (warehouseAbortControllerRef.current) warehouseAbortControllerRef.current.abort();

    const abortController = new AbortController();
    warehouseAbortControllerRef.current = abortController;
    if (!silent) setWarehouseCountsLoading(true);
    try {
      const response = await api.post('/lc-dashboards/warehouse-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setBucketCounts((prev) => ({ ...prev, ...(response.data.counts || {}) }));
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch warehouse counts:', err);
    } finally {
      if (warehouseAbortControllerRef.current === abortController) warehouseAbortControllerRef.current = null;
      if (!silent) setWarehouseCountsLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch warehouse counts on mount, on filter change, and on workspace switch — only
  // when this panel belongs to the active workspace.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(WAREHOUSING_PANEL_KEY) === currentWorkspaceId) fetchWarehouseCounts();
  }, [fetchWarehouseCounts, currentWorkspaceId, filtersReady]);

  const fetchPendingInvoicing = useCallback(async (silent: boolean = false) => {
    if (pinvFetchSkipRef.current) {
      if (!silent) setPinvCountsLoading(false);
      return;
    }
    if (silent && pinvAbortControllerRef.current) return;
    if (pinvAbortControllerRef.current) pinvAbortControllerRef.current.abort();

    const abortController = new AbortController();
    pinvAbortControllerRef.current = abortController;
    if (!silent) setPinvCountsLoading(true);
    try {
      const response = await api.post('/lc-dashboards/pending-invoicing-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setBucketCounts((prev) => ({ ...prev, ...(response.data.counts || {}) }));
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch pending invoicing counts:', err);
    } finally {
      if (pinvAbortControllerRef.current === abortController) pinvAbortControllerRef.current = null;
      if (!silent) setPinvCountsLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch Pending Invoicing counts on mount, on filter change, and on workspace switch.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(PINV_PANEL_KEY) === currentWorkspaceId) fetchPendingInvoicing();
  }, [fetchPendingInvoicing, currentWorkspaceId, filtersReady]);

  const fetchPerDiem = useCallback(async (silent: boolean = false) => {
    if (perDiemCollapsedRef.current) {
      if (!silent) setPerDiemLoading(false);
      return;
    }
    if (silent && perDiemAbortControllerRef.current) return;
    if (perDiemAbortControllerRef.current) perDiemAbortControllerRef.current.abort();

    const abortController = new AbortController();
    perDiemAbortControllerRef.current = abortController;
    if (!silent) setPerDiemLoading(true);
    try {
      const response = await api.post('/lc-dashboards/per-diem-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setPerDiemData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch per diem counts:', err);
    } finally {
      if (perDiemAbortControllerRef.current === abortController) perDiemAbortControllerRef.current = null;
      if (!silent) setPerDiemLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch Per Diem counts on mount, on filter change, and on workspace switch.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(PERDIEM_PANEL_KEY) === currentWorkspaceId) fetchPerDiem();
  }, [fetchPerDiem, currentWorkspaceId, filtersReady]);

  const fetchAdditionalCost = useCallback(async (silent: boolean = false) => {
    if (addCostCollapsedRef.current) {
      if (!silent) setAddCostLoading(false);
      return;
    }
    if (silent && addCostAbortControllerRef.current) return;
    if (addCostAbortControllerRef.current) addCostAbortControllerRef.current.abort();

    const abortController = new AbortController();
    addCostAbortControllerRef.current = abortController;
    if (!silent) setAddCostLoading(true);
    try {
      const response = await api.post('/lc-dashboards/additional-cost-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setAddCostData(response.data || null);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch additional cost counts:', err);
    } finally {
      if (addCostAbortControllerRef.current === abortController) addCostAbortControllerRef.current = null;
      if (!silent) setAddCostLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch Additional Cost counts on mount, on filter change, and on workspace switch.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(ADDCOST_PANEL_KEY) === currentWorkspaceId) fetchAdditionalCost();
  }, [fetchAdditionalCost, currentWorkspaceId, filtersReady]);

  const fetchTroubleTickets = useCallback(async (silent: boolean = false) => {
    if (ticketFetchSkipRef.current) {
      if (!silent) setTicketLoading(false);
      return;
    }
    if (silent && ticketAbortControllerRef.current) return;
    if (ticketAbortControllerRef.current) ticketAbortControllerRef.current.abort();

    const abortController = new AbortController();
    ticketAbortControllerRef.current = abortController;
    if (!silent) setTicketLoading(true);
    try {
      const response = await api.post('/lc-dashboards/trouble-ticket-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      setTicketCounts(response.data?.counts || {});
      setTicketRecnos(response.data?.recnos || {});
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch trouble ticket counts:', err);
    } finally {
      if (ticketAbortControllerRef.current === abortController) ticketAbortControllerRef.current = null;
      if (!silent) setTicketLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch Tickets counts on mount, on filter change, and on workspace switch.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(TICKETS_PANEL_KEY) === currentWorkspaceId) fetchTroubleTickets();
  }, [fetchTroubleTickets, currentWorkspaceId, filtersReady]);

  // A Tickets bucket was clicked — open the TTs & Service Failures page in a new tab,
  // scoped to that bucket's exact trouble-ticket recnos (no in-dashboard drill).
  const handleTicketSelect = useCallback((key: string) => {
    const recnos = ticketRecnos[key] || [];
    if (recnos.length === 0) return;
    const label = TT_LABELS[key] || '';
    // Hand the recnos to the new tab via localStorage (no URL-length ceiling — recno
    // lists can run into the hundreds) under a unique token; sweep prior tokens first
    // so storage doesn't accumulate. Fall back to the URL if storage is unavailable.
    try {
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const k = localStorage.key(i);
        if (k && k.startsWith('lc-tt-drill-')) localStorage.removeItem(k);
      }
      const token = `${Date.now()}-${Math.round(Math.random() * 1e9)}`;
      localStorage.setItem(`lc-tt-drill-${token}`, JSON.stringify({ recnos, label }));
      window.open(`${TICKETS_PAGE}?ttSel=${token}`, '_blank', 'noopener');
    } catch {
      window.open(`${TICKETS_PAGE}?ttRecnos=${recnos.join(',')}&ttLabel=${encodeURIComponent(label)}`, '_blank', 'noopener');
    }
  }, [ticketRecnos]);

  // Fetch the Tablemaint panel (Missing GPS Coordinates) from its own endpoint, in parallel.
  // Counts locations (not pros); the count merges into bucketCounts and the matching recnos
  // are stashed for the click handler. Self-skips when off-workspace.
  const fetchTablemaintCounts = useCallback(async (silent: boolean = false) => {
    if (tablemaintFetchSkipRef.current) {
      if (!silent) setTablemaintLoading(false);
      return;
    }
    if (silent && tablemaintAbortControllerRef.current) return;
    if (tablemaintAbortControllerRef.current) tablemaintAbortControllerRef.current.abort();

    const abortController = new AbortController();
    tablemaintAbortControllerRef.current = abortController;
    if (!silent) setTablemaintLoading(true);
    try {
      const response = await api.post('/lc-dashboards/tablemaint-counts', {
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
      }, { signal: abortController.signal });
      tablemaintRecnosRef.current = response.data.recnos || [];
      setBucketCounts((prev) => ({ ...prev, [TABLEMAINT_MISSING_COORDS_KEY]: response.data.count ?? 0 }));
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch tablemaint counts:', err);
    } finally {
      if (tablemaintAbortControllerRef.current === abortController) tablemaintAbortControllerRef.current = null;
      if (!silent) setTablemaintLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch tablemaint counts on mount, on filter change, and on workspace switch — only
  // when this panel belongs to the active workspace.
  useEffect(() => {
    if (!filtersReady) return;
    if (workspaceForPanel(TABLEMAINT_PANEL_KEY) === currentWorkspaceId) fetchTablemaintCounts();
  }, [fetchTablemaintCounts, currentWorkspaceId, filtersReady]);

  // Missing GPS Coordinates isn't a pro drill-down — it opens the Locations table filtered
  // to the matching recnos in a new tab. Every other count routes to its drill-down.
  const handleCountSelect = useCallback((key: string) => {
    // Pending Invoicing buckets drill into an invoice-level table (its own endpoint),
    // not the order-level batch-cards path.
    if (key.startsWith('lc-pinv-')) {
      const title = PANEL_CARD_DEFS.find((d) => d.key === key)?.title || 'Pending Invoicing';
      setVisibleCardKeys(new Set());
      setGridSel({ kind: 'pending-invoicing', title: `${title}`, body: { bucket: key } });
      return;
    }
    if (key === TABLEMAINT_MISSING_COORDS_KEY) {
      const recnos = tablemaintRecnosRef.current;
      const url = recnos.length
        ? `/data-management/locations?recnos=${recnos.join(',')}`
        : '/data-management/locations';
      window.open(url, '_blank', 'noopener');
      return;
    }
    toggleCardVisibility(key);
  }, [toggleCardVisibility]);

  // Open a clicked grid cell as an in-dashboard drill-down — the same results screen
  // the bucket counts use (page-swap or drawer per drawerView, Cards or Table per
  // resultsView). Mirrors toggleCardVisibility's open behavior. Both panels funnel
  // through setGridSel so they share the one drill host below.
  const openScheduleCell = useCallback((
    category: ScheduleCategory,
    start: string,
    end: string,
  ) => {
    const mmdd = (iso: string) => { const p = iso.split('-'); return `${p[1]}/${p[2]}`; };
    const catLabel = category === 'not_dispatched' ? 'Not Dispatched'
      : category === 'no_outgate' ? 'No Outgate'
        : category === 'not_delivered' ? 'Not Delivered' : 'Scheduled';
    const range = start === end ? mmdd(start) : `${mmdd(start)} – ${mmdd(end)}`;
    // Opening a grid cell takes over the results area — close any open bucket.
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'schedule', title: `Delivery Schedule · ${catLabel} · ${range}`, body: { category, start, end } });
  }, []);

  const openTrackingCell = useCallback((row: string, bucket: string, title: string) => {
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'tracking', title: `Tracking · ${title}`, body: { row, bucket } });
  }, []);

  const openExportsSummaryCell = useCallback((col: string, bucket: string, title: string) => {
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'exports-summary', title: `Exports Summary · ${title}`, body: { col, bucket } });
  }, []);

  // A board cell was clicked. The drill carries the booking-key rowids (one row, a
  // group's rows, or the whole board) and optional status column; group_by must ride
  // along so the rowids resolve against the same date key the board used.
  const openExportsBoardDrill = useCallback((drill: { rowids: string[]; status?: string; title: string }) => {
    setVisibleCardKeys(new Set());
    setGridSel({
      kind: 'exports-board',
      title: `Exports Board · ${drill.title}`,
      body: { rowids: drill.rowids, status: drill.status ?? null, group_by: exportsBoardGroupBy },
    });
  }, [exportsBoardGroupBy]);

  const openChassisCell = useCallback((category: 'gwi' | 'customer', state: 'drop' | 'rel', title: string) => {
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'chassis', title: `Chassis Charges · ${title}`, body: { category, state } });
  }, []);

  // A Per Diem bucket row was clicked. Drill its PROs through the shared grid host
  // (kind 'per-diem'); the endpoint resolves orderscurrent vs ordersall by bucket.
  const handlePerDiemSelect = useCallback((key: string) => {
    const label = PERDIEM_LABELS[key] || key;
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'per-diem', title: `Per Diem · ${label}`, body: { bucket: key } });
  }, []);

  // The Additional Cost "No Pay Status" row was clicked. Drill its additional-cost-level
  // records (kind 'additional-cost', table-only — its own flat column set).
  const handleAdditionalCostSelect = useCallback(() => {
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'additional-cost', title: 'Additional Cost · No Pay Status', body: {} });
  }, []);

  // A New Melrose Pros row was clicked. Its key is `lc-melrose-<customercode>`; drill to
  // that customer's new pros through the shared grid host (kind 'melrose').
  const handleMelroseSelect = useCallback((key: string) => {
    const customercode = parseInt(key.slice(MELROSE_KEY_PREFIX.length), 10);
    if (Number.isNaN(customercode)) return;
    const row = (melroseData || []).find((r) => r.customercode === customercode);
    const label = (row?.custname || `Customer ${customercode}`).toUpperCase();
    setVisibleCardKeys(new Set());
    setGridSel({ kind: 'melrose', title: `New Melrose Pros · ${label}`, body: { customercode } });
  }, [melroseData]);

  // Warehouse Photos panel click — opens the Warehouse Photos page in a new tab. The
  // header total opens it bare; a per-warehouse row opens it with that warehouse
  // pre-selected in the "Assign Warehouse Photos to a Pro" section (?assignWarehouse=).
  const handleWhsePhotoSelect = useCallback((key: string) => {
    if (key === WHSEPHOTO_TOTAL_KEY) {
      window.open(WHSEPHOTO_PAGE, '_blank', 'noopener');
      return;
    }
    const code = key.slice(WHSEPHOTO_KEY_PREFIX.length);
    window.open(`${WHSEPHOTO_PAGE}?assignWarehouse=${encodeURIComponent(code)}`, '_blank', 'noopener');
  }, []);

  // Fetch the open grid cell's rows for the active results view (Cards = ProSearchData,
  // Table = LC columns). The cell-identity fields live in gridSel.body; the endpoints
  // are chosen by gridSel.kind. Refetches on cell, view, or filter change.
  useEffect(() => {
    if (!gridSel) {
      setGridDrillRows([]);
      setGridDrillTable([]);
      return;
    }
    // Cards view: hold until the saved extra-column set is known, so the first fetch
    // already carries it (this effect re-runs when it lands). Table-only drills have
    // no card view and never wait.
    const tableOnly = gridSel.kind === 'pending-invoicing' || gridSel.kind === 'additional-cost';
    if (resultsView === 'cards' && !tableOnly && !cardExtras.ready) return;
    if (gridDrillAbortRef.current) gridDrillAbortRef.current.abort();
    const abortController = new AbortController();
    gridDrillAbortRef.current = abortController;

    const body = {
      ...gridSel.body,
      // This panel's custom filter. Read through the ref so this effect need not depend
      // on the map; `gridDrillNonce` (bumped on apply) is what re-runs it.
      conditions: bucketFiltersRef.current[gridFilterKey(gridSel.kind)] || [],
      lcs: selectedLCs,
      customers: selectedCustomers,
      billtos: selectedBilltos,
      key_accounts: selectedKeyAccounts,
      dispatch_regions: selectedDispatchRegions,
      warehouses: selectedWarehouses,
    };

    // Pending Invoicing (invoice-level) and Additional Cost (additional-cost-level) are
    // table-only — each has its own endpoint, loaded into the table state.
    const bespokeTable: { [k: string]: string } = {
      'pending-invoicing': '/lc-dashboards/pending-invoicing-invoices',
      'additional-cost': '/lc-dashboards/additional-cost-records',
    };
    if (bespokeTable[gridSel.kind]) {
      // Optional extra display columns (order fields, Phase 1) per view.
      const extra_fields = gridSel.kind === 'pending-invoicing' ? pinvExtraFields
        : gridSel.kind === 'additional-cost' ? addcostExtraFields : [];
      setGridDrillTableLoading(true);
      (async () => {
        try {
          const resp = await api.post(bespokeTable[gridSel.kind], { ...body, extra_fields }, { signal: abortController.signal });
          setGridDrillTable(resp.data?.data || []);
        } catch (err: any) {
          if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
          console.error('Failed to fetch bespoke drill table:', err);
          setGridDrillTable([]);
        } finally {
          if (gridDrillAbortRef.current === abortController) gridDrillAbortRef.current = null;
          setGridDrillTableLoading(false);
        }
      })();
      return;
    }

    const endpoints = GRID_DRILL_ENDPOINTS[gridSel.kind];

    const run = async () => {
      try {
        if (resultsView === 'cards') {
          setGridDrillRowsLoading(true);
          // The card view's own added columns (incl. sf/tt sentinels), attached
          // server-side onto the ProSearchData rows.
          const resp = await api.post(
            endpoints.cards,
            { ...body, additional_fields: cardExtras.additionalFields },
            { signal: abortController.signal },
          );
          setGridDrillRows(resp.data?.data || []);
        } else {
          setGridDrillTableLoading(true);
          // User-added drill columns for THIS panel (incl. sf/tt sentinels). Chassis
          // uses a bespoke column set with no field picker, so it sends none.
          const additional_fields = gridSel.kind === 'chassis' ? [] : (gridDrillExtraByKind[gridSel.kind] || []);
          const resp = await api.post(endpoints.table, { ...body, additional_fields }, { signal: abortController.signal });
          setGridDrillTable(resp.data?.data || []);
        }
      } catch (err: any) {
        if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
        console.error('Failed to fetch grid drill-down:', err);
        if (resultsView === 'cards') setGridDrillRows([]); else setGridDrillTable([]);
      } finally {
        if (gridDrillAbortRef.current === abortController) gridDrillAbortRef.current = null;
        setGridDrillRowsLoading(false);
        setGridDrillTableLoading(false);
      }
    };
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gridSel, resultsView, gridDrillNonce, pinvExtraFields, addcostExtraFields, gridDrillExtraByKind, cardExtras.ready, cardExtraKey, selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Close any open grid cell when switching workspaces (the panels live on Main).
  useEffect(() => {
    setGridSel(null);
  }, [currentWorkspaceId]);

  // Fetch the rich card rows for a bucket (ProSearchData for the Cards view).
  // `extraFields` is the card view's added-column set (incl. sf/tt sentinels),
  // attached server-side onto the rows.
  const fetchCardRows = useCallback(async (bucket: string, extraFields: string[]) => {
    if (cardRowsAbortRef.current) cardRowsAbortRef.current.abort();
    const abortController = new AbortController();
    cardRowsAbortRef.current = abortController;
    setCardRowsLoading(true);
    try {
      const response = await api.post('/lc-dashboards/cards', {
        bucket,
        // The bucket's in-place filter. The Cards view is the same bucket as the Single
        // Row table, so it has to carry the same conditions — without this, flipping the
        // view toggle would quietly hand back the rows the user just filtered out.
        conditions: bucketFiltersRef.current[bucket] || [],
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
        additional_fields: extraFields,
      }, { signal: abortController.signal });
      setCardRows(response.data?.data || []);
    } catch (err: any) {
      if (err.name === 'CanceledError' || err.code === 'ERR_CANCELED') return;
      console.error('Failed to fetch card rows:', err);
      setCardRows([]);
    } finally {
      if (cardRowsAbortRef.current === abortController) cardRowsAbortRef.current = null;
      setCardRowsLoading(false);
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Load card rows when the Cards view is active and a bucket is open; refetch on
  // bucket/filter/added-column changes. Skipped entirely in Table view (no extra
  // query). Holds until the bucket's saved extra-column set is known so the first
  // fetch already carries it (re-runs when it lands).
  useEffect(() => {
    if (resultsView !== 'cards' || visibleCardKeys.size !== 1) {
      setCardRows([]);
      return;
    }
    if (!cardExtras.ready) return;
    const key = Array.from(visibleCardKeys)[0];
    if (key) fetchCardRows(key, cardExtras.additionalFields);
    // `bucketFilters` is in the deps so applying or clearing an in-place filter refetches
    // the Cards view too — fetchCardRows reads the filter through the ref, so nothing else
    // here would tell this effect the query changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resultsView, visibleCardKeys, fetchCardRows, cardExtras.ready, cardExtraKey, bucketFilters]);

  // Fetch filter dropdown options (LCs + LC groups, customers, billing parties).
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await cachedGet<any>('/lc-dashboards/filter-options');
        const { lcs = [], keyAccounts = [], dispatchRegions = [], warehouses = [], customers = [], billtos = [] } = response.data || {};

        // `lcs` is one merged list (LC groups + individual LCs) tagged with isGroup;
        // split it back into individuals + groups for the sectioned dropdown. Each LC
        // covers the order LC (csr) OR warehouse LC (whsecsr); group values are
        // tilde-delimited username lists that expand server-side.
        type LcOption = { value: string; label: string; isGroup?: boolean };
        const lcItems: MultiSelectOption[] = (lcs as LcOption[])
          .filter((l) => !l.isGroup)
          .map((l) => ({ value: l.value, label: l.label }));
        const groupItems: MultiSelectOption[] = (lcs as LcOption[])
          .filter((l) => l.isGroup)
          .map((g) => ({ value: g.value, label: g.label }));
        setLcOptions(
          groupItems.length > 0
            ? [
              { value: '__lcs_header__', label: 'LCs', isHeader: true },
              ...lcItems,
              { value: '__divider__', label: '', isDivider: true },
              { value: '__groups_header__', label: 'Groups', isHeader: true },
              ...groupItems,
            ]
            : lcItems
        );

        setCustomerOptions(
          customers.map((c: { value: string; label: string }) => ({ value: c.value, label: c.label }))
        );
        setBilltoOptions(
          billtos.map((b: { value: string; label: string }) => ({ value: b.value, label: b.label }))
        );
        setKeyAccountOptions(
          keyAccounts.map((k: { value: string; label: string }) => ({ value: k.value, label: k.label }))
        );
        setDispatchRegionOptions(
          dispatchRegions.map((r: { value: string; label: string }) => ({ value: r.value, label: r.label }))
        );
        setWarehouseOptions(
          warehouses.map((w: { value: string; label: string }) => ({ value: w.value, label: w.label }))
        );
      } catch (err: any) {
        console.error('Failed to fetch filter options:', err);
      }
    };
    fetchFilterOptions();
  }, []);

  // Fetch tab counts for a card (lightweight - only gets counts per tab)
  const fetchCardTabCounts = useCallback(async (
    cardKey: string,
    bucket: string,
    tabField: string,
    filterConditions: FilterCondition[] = [],
    silent: boolean = false
  ) => {
    try {
      const response = await api.post(`/lc-dashboards/${bucket}`, {
        conditions: filterConditions,
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
        tab_field: tabField,
        counts_only: true
      });

      if (response.data.tab_counts) {
        setCardTabCounts(prev => ({ ...prev, [cardKey]: response.data.tab_counts }));

        // If no tab is selected yet, default to 'today' if available, otherwise first tab
        // Use functional update to check current state without needing it as dependency
        setCardSelectedTabs(prev => {
          if (prev[cardKey]) return prev; // Already has a selection, don't change

          const todayTab = response.data.tab_counts.find((t: any) => {
            const today = new Date();
            const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
            return t.tab_value === todayStr;
          });
          const defaultTab = todayTab ? todayTab.tab_value : response.data.tab_counts[0].tab_value;
          return { ...prev, [cardKey]: defaultTab };
        });
      }
    } catch (error: any) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch tab counts:', error);
      }
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Fetch data for a card based on its filter configuration
  const fetchCardData = useCallback(async (
    cardKey: string,
    filterConditions: FilterCondition[],
    filterView: string,
    bucket?: string,
    silent: boolean = false,
    additionalFields?: string[],
    tabField?: string,  // Tab optimization: field to filter by
    tabValue?: string,  // Tab optimization: specific tab value to fetch
    // Scope override for the card just created — cardConfigsRef has not caught up to
    // setCardConfigs yet on that path, so the ref lookup below would read undefined
    // and silently fetch current-scope for a card the user marked closed-inclusive.
    includeClosedOverride?: boolean
  ) => {
    // If this is a silent background refresh and there's already a request in progress, skip it
    // This prevents canceling user-initiated requests with background refreshes
    if (silent && abortControllersRef.current[cardKey]) {
      return;
    }

    // Cancel any existing request for this card (only for non-silent/user-initiated requests)
    if (abortControllersRef.current[cardKey]) {
      abortControllersRef.current[cardKey].abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    abortControllersRef.current[cardKey] = abortController;

    // Read from the ref rather than taking a parameter: fetchCardData is intentionally
    // NOT dependent on cardConfigs (that would rebuild it on every card edit and churn
    // the polling effects), and the ref is how the rest of this file reads live config.
    const includeClosed = includeClosedOverride
      ?? cardConfigsRef.current.find(c => c.key === cardKey)?.includeClosed
      ?? false;

    if (!silent) {
      setCardDataLoading(prev => ({ ...prev, [cardKey]: true }));
    }

    try {
      let response: any;

      // The card's own saved filter plus any in-place bucket filter layered on top.
      const effectiveConditions = conditionsForCard(cardKey, filterConditions);

      // If bucket is specified, use the batch-cards endpoint (with a single card)
      if (bucket || cardKey.startsWith('managed:')) {
        // LC column set for this bucket (frontend is the source of truth).
        const baseFields = lcBucketFields(bucket || 'orderscurrent-filter');

        // Build request for batch endpoint with a single card
        const requestBody: any = {
          cards: [{
            card_key: cardKey,
            bucket: bucket || 'orderscurrent-filter',
            base_fields: baseFields,
            additional_fields: additionalFields || [],
            conditions: effectiveConditions,
            // Add tab filtering if specified
            ...(tabField && tabValue ? { tab_field: tabField, tab_value: tabValue } : {})
          }],
          lcs: selectedLCs,
          customers: selectedCustomers,
          billtos: selectedBilltos,
          key_accounts: selectedKeyAccounts,
          dispatch_regions: selectedDispatchRegions,
          warehouses: selectedWarehouses,
        };

        response = await api.post('/lc-dashboards/batch-cards', requestBody, {
          signal: abortController.signal
        });

        // Extract the single card result from the batch response
        const cardResult = response.data.results?.[cardKey];
        if (cardResult) {
          response.data.data = cardResult.data;
          response.data.total = cardResult.total;
          response.data.error = cardResult.error;
        }
      } else {
        // Otherwise, use the lc-dashboards orderscurrent filter endpoint
        if (effectiveConditions.length === 0) {
          if (!silent) {
            setCardDataLoading(prev => ({ ...prev, [cardKey]: false }));
          }
          delete abortControllersRef.current[cardKey];
          return;
        }

        response = await api.post('/lc-dashboards/orderscurrent/filter', {
          conditions: effectiveConditions,
          view: filterView || 'Orders',
          additional_fields: additionalFields || [],
          include_closed: includeClosed
        }, {
          signal: abortController.signal
        });
      }

      setCardData(prev => ({ ...prev, [cardKey]: response.data.data || [] }));
      setCardErrors(prev => ({ ...prev, [cardKey]: response.data.error || null }));
      // Derived from the RESPONSE, not the card config, so the header pill always
      // describes the rows on screen rather than a filter edit that hasn't refetched.
      setCardScopeMeta(prev => ({
        ...prev,
        [cardKey]: {
          includeClosed: Boolean(response.data.include_closed),
          truncated: Boolean(response.data.truncated),
          limit: response.data.limit,
        },
      }));
    } catch (error: any) {
      // Don't log or update state if request was cancelled
      if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED') {
        return;
      }
      console.error('Failed to fetch card data:', error);
      setCardData(prev => ({ ...prev, [cardKey]: [] }));
      setCardErrors(prev => ({ ...prev, [cardKey]: 'Couldn’t load this card.' }));
    } finally {
      // Clean up the abort controller
      delete abortControllersRef.current[cardKey];

      if (!silent) {
        setCardDataLoading(prev => ({ ...prev, [cardKey]: false }));
      }
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses, conditionsForCard]);

  // Save all staged pickup-number edits as individual updateall saves (one per pro), each
  // routing the originate-leg origapptnumber through the saverules engine with history type
  // 'LC Dashboard Edit'. Failed pros stay staged for retry; on success the open bucket + its
  // count refresh (rows that now have a pickup number drop out of the bucket).
  const savePickupEdits = useCallback(async () => {
    const entries = Object.entries(pickupEdits);
    if (entries.length === 0 || savingPickups) return;
    setSavingPickups(true);
    try {
      const items = entries.map(([pronumber, newValue]) => ({
        pronumber,
        changes: {
          order: [],
          invoice: [],
          legs: [{ match: { role: 'originate' as const }, column: 'origapptnumber', newValue }],
        } as ChangeAllChangeSet,
      }));
      const submitNonce =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `pu-${Date.now()}-${Math.random().toString(36).slice(2)}`;
      const res = await ordersService.changeAllApplyPerPro({ items, submitNonce });
      const results = res.results || [];
      const failed = results.filter(r => !r.applied);
      const failedPros = new Set(failed.map(r => String(r.pronumber)));
      if (failed.length > 0) {
        const firstErr = failed[0]?.errors?.[0];
        showToast({
          type: 'error',
          message: `${failed.length} pickup number${failed.length === 1 ? '' : 's'} failed to save${firstErr ? `: ${firstErr}` : '.'}`,
        });
      } else {
        showToast({
          type: 'success',
          message: `Saved ${entries.length} pickup number${entries.length === 1 ? '' : 's'}.`,
        });
      }
      // Refresh the open bucket (the saved rows now carry a pickup number, so they drop out)
      // + its count. We refetch BEFORE clearing the staged edits so the entered values stay
      // on screen until the fresh data replaces those rows — no flash of blank pickups.
      const openKey = visibleCardKeys.size === 1 ? Array.from(visibleCardKeys)[0] : null;
      if (openKey) {
        const cfg = cardConfigs.find(c => c.key === openKey);
        if (cfg) await fetchCardData(openKey, cfg.filterConditions || [], cfg.filterView || 'Orders', cfg.bucket, true, cfg.additionalFields);
      }
      fetchBucketCounts(true);
      // Now drop the succeeded rows' staged edits (their rows are gone); keep any failures
      // staged (still in the bucket, blank) so the user can retry them.
      setPickupEdits(prev => {
        const next: Record<string, string> = {};
        for (const [pro, val] of Object.entries(prev)) if (failedPros.has(pro)) next[pro] = val;
        return next;
      });
    } catch (e) {
      showToast({ type: 'error', message: 'Failed to save pickup numbers.' });
    } finally {
      setSavingPickups(false);
    }
  }, [pickupEdits, savingPickups, showToast, visibleCardKeys, cardConfigs, fetchCardData, fetchBucketCounts]);

  // Helper to convert tab ID (TanTable format) to tab_value (backend format) for API
  const tabIdToTabValue = useCallback((tabId: string): string => {
    if (tabId === 'past-due') return 'past_due';
    if (tabId === 'today') {
      const today = new Date();
      return `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    }
    if (tabId.startsWith('date-')) return tabId.replace('date-', '');
    return tabId;
  }, []);

  // Helper to convert tab_value (backend format) to tab ID (TanTable format)
  const tabValueToTabId = useCallback((tabValue: string): string => {
    if (tabValue === 'past_due') return 'past-due';
    // Check if this is today's date
    const today = new Date();
    const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    if (tabValue === todayStr) return 'today';
    // If it looks like a date, prefix with 'date-'
    if (/^\d{4}-\d{2}-\d{2}$/.test(tabValue)) return `date-${tabValue}`;
    return tabValue;
  }, []);

  // Batch fetch all card data in a single HTTP request
  // This replaces fetchCardsStaggered for bucket-based cards to reduce HTTP connections
  const fetchAllCardData = useCallback(async (
    cards: CardConfig[],
    silent: boolean = false,
    onlyVisible: boolean = true  // Only fetch visible cards by default
  ) => {
    // Hidden cards are fetched only while explicitly revealed in this workspace.
    cards = cards.filter(card => isWorkspaceCardShown(card, showHidden));
    // Filter to only visible cards if requested
    const cardsToFetch = onlyVisible
      ? cards.filter(card => {
        if (visibleCardKeys.size === 0) {
          // Main (default) workspace defaults to no bucket selected: load nothing
          // up front. Counts come from /bucket-counts, so card data isn't needed
          // until the user picks a bucket. Other workspaces have no bucket
          // selector, so an empty selection means show all (unchanged behavior).
          return currentWorkspaceId !== 'default';
        }
        // All buckets selected = show all
        if (visibleCardKeys.size === allCardKeys.length) {
          return true;
        }
        return visibleCardKeys.has(card.key);
      })
      : cards;

    // All cards with a bucket or filter conditions can be batch loaded
    // Non-bucket cards with filters use 'orderscurrent-filter' as a generic bucket
    const batchableCards = cardsToFetch.filter(card =>
      card.bucket || (card.filterConditions && card.filterConditions.length > 0)
    );

    // Skip if no cards to fetch
    if (batchableCards.length === 0) return;

    // If a request is already in progress...
    if (batchCardsAbortControllerRef.current) {
      if (silent) {
        // For silent refreshes (auto-refresh), skip instead of canceling
        // This prevents the 30-second refresh from interrupting a slow load
        return;
      }
      // For user-initiated actions (visibility change, filter change), cancel the old request
      batchCardsAbortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    batchCardsAbortControllerRef.current = abortController;

    // Set loading state for all batchable cards
    if (!silent) {
      const loadingState = batchableCards.reduce((acc: Record<string, boolean>, c: CardConfig) => ({ ...acc, [c.key]: true }), {});
      setCardDataLoading(prev => ({ ...prev, ...loadingState }));
    }

    try {
      // Build batch request - use 'orderscurrent-filter' for cards without a predefined bucket
      const cardRequests = batchableCards.map((card: CardConfig) => {
        const effectiveBucket = card.bucket || 'orderscurrent-filter';
        return {
          card_key: card.key,
          bucket: effectiveBucket,
          base_fields: lcBucketFields(effectiveBucket),
          additional_fields: card.additionalFields || [],
          conditions: conditionsForCard(card.key, card.filterConditions),
          // Custom-filter cards are batched under the synthetic 'orderscurrent-filter'
          // bucket, so this is the path a SAVED closed-inclusive card loads through.
          // Omitting it here would silently reset the card to current scope on load.
          include_closed: Boolean(card.includeClosed)
        };
      });

      const response = await api.post('/lc-dashboards/batch-cards', {
        cards: cardRequests,
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses
      }, {
        signal: abortController.signal
      });

      // Update state for all cards at once
      if (response.data.results) {
        setCardData(prev => {
          const updated = { ...prev };
          for (const [cardKey, result] of Object.entries(response.data.results)) {
            const typedResult = result as { data?: any[]; total?: number };
            updated[cardKey] = typedResult.data || [];
          }
          return updated;
        });
        setCardErrors(prev => {
          const updated = { ...prev };
          for (const [cardKey, result] of Object.entries(response.data.results)) {
            updated[cardKey] = (result as { error?: string }).error || null;
          }
          return updated;
        });
        // Scope/cap metadata per card, straight from each card's own result. The batch
        // mixes bucket cards (never closed-inclusive) with custom-filter cards, so this
        // has to be per-card rather than per-response.
        setCardScopeMeta(prev => {
          const updated = { ...prev };
          for (const [cardKey, result] of Object.entries(response.data.results)) {
            const r = result as { include_closed?: boolean; truncated?: boolean; limit?: number };
            updated[cardKey] = {
              includeClosed: Boolean(r.include_closed),
              truncated: Boolean(r.truncated),
              limit: r.limit,
            };
          }
          return updated;
        });
      }
    } catch (error: any) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch batch card data:', error);
        // Set empty data + error for all cards on whole-batch failure
        setCardData(prev => {
          const updated = { ...prev };
          batchableCards.forEach((card: CardConfig) => {
            updated[card.key] = [];
          });
          return updated;
        });
        setCardErrors(prev => {
          const updated = { ...prev };
          batchableCards.forEach((card: CardConfig) => {
            updated[card.key] = 'Couldn’t load this card.';
          });
          return updated;
        });
      }
    } finally {
      // Clear abort controller ref if it's still ours (not replaced by a new request)
      if (batchCardsAbortControllerRef.current === abortController) {
        batchCardsAbortControllerRef.current = null;
      }
      if (!silent) {
        const loadingOff = batchableCards.reduce((acc: Record<string, boolean>, c: CardConfig) => ({ ...acc, [c.key]: false }), {});
        setCardDataLoading(prev => ({ ...prev, ...loadingOff }));
      }
    }
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses, visibleCardKeys, allCardKeys, currentWorkspaceId, conditionsForCard, showHidden]);

  // Fetch all table settings in a single batch request
  // Called once during initial load to avoid individual API calls per TanTable
  const fetchAllTableSettings = useCallback(async () => {
    try {
      const [settings, defaults] = await Promise.all([
        // No trailing dash: covers BOTH the Single-Row tables (`table_lc-card-*`)
        // and the Multi-Row card view's independent layouts (`table_lc-cards-*`).
        userSettingsService.getUserSettingsByPrefix('table_lc-card'),
        userSettingsService.getDefaultSettingsByPrefix('lc-card-').catch(() => ({})),
      ]);
      setTableSettings(settings);
      setCardDefaults(defaults);

      // Seed a published default's additionalFields + row grouping into shared
      // cards the user hasn't customized, before the ready flag flips. (Tab
      // grouping is code-driven and not seeded.)
      setCardConfigs(prev => prev.map(card => {
        if (!systemBucketDefaults.has(card.key)) return card;
        const key = `lc-card-${card.key}`;
        const hasUserTable = !!settings[`table_${key}`];
        const hasUserAdditional = Array.isArray(card.additionalFields) && card.additionalFields.length > 0;
        const def = (defaults as Record<string, any>)[key];
        if (!def || hasUserTable || hasUserAdditional) return card;
        const patch: Partial<CardConfig> = {};
        if (Array.isArray(def.additionalFields) && def.additionalFields.length > 0) patch.additionalFields = def.additionalFields;
        if (Array.isArray(def.grouping)) patch.grouping = def.grouping;
        return Object.keys(patch).length ? { ...card, ...patch } : card;
      }));

      setTableSettingsLoadFailed(false); // success (incl. empty) clears protected mode
    } catch (error) {
      console.error('Failed to fetch batch table settings:', error);
      setTableSettingsLoadFailed(true); // DB/network failure -> protect & retry
    } finally {
      setTableSettingsLoaded(true);
    }
  }, [systemBucketDefaults]);

  // Keep tableSettings cache in sync when TanTable saves settings
  const handleTableSettingsSaved = useCallback((settingsKey: string, settings: any) => {
    setTableSettings(prev => ({ ...prev, [settingsKey]: settings }));
  }, []);

  // TanTable composes the full default blob; we just persist + cache it.
  const handleSaveDefaults = useCallback(async (settingsKey: string, stored: any) => {
    await userSettingsService.saveDefaultSetting(settingsKey, stored);
    setCardDefaults(prev => ({ ...prev, [settingsKey]: stored }));
  }, []);

  // Grouping field(s) a bucket groups on by default — non-removable columns in
  // the editor (mirrors the code-level default tab grouping).
  const getMandatoryGroupingFields = useCallback((bucket?: string): string[] => {
    switch (bucket) {
      case 'per-diem-not-dispatched': return ['perdiemstartdate'];
      case 'cutoff-not-dispatched':
      case 'cutoff-not-terminated': return ['cutoffdatetime'];
      case 'dispatches-not-accepted': return ['leg_destapptdate'];
      case 'scheduled-not-dispatched': return ['d_legmiles_band'];
      default: return [];
    }
  }, []);

  // Refresh all cards that have filters or buckets (silent background refresh)
  // Only refresh cards in the current workspace, skip collapsed cards
  const refreshAllCards = useCallback(async () => {
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsedRef.current[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
        (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });

    await fetchAllCardData(cardsToRefresh, true); // silent=true, single batch request
  }, [currentWorkspaceId, fetchAllCardData]);

  // Trigger re-fetch of all cards when global filters change
  useEffect(() => {
    // Skip initial mount
    if (!configsLoaded) return;
    // Boot-time filter restores are not user changes — the workspace effect
    // owns the (gated) initial load. Not in deps on purpose: gate-open must
    // not fire this one too.
    if (!filtersReady) return;

    // Fetch all cards with loading indicators (not silent)
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsedRef.current[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
        (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });
    fetchAllCardData(cardsToRefresh, false); // silent=false to show loading, single batch request
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  // Trigger re-fetch when card visibility changes (user selects/deselects a bucket)
  useEffect(() => {
    // Skip if configs not loaded yet
    if (!configsLoaded) return;

    // Fetch only the visible cards
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsedRef.current[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
        (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });
    fetchAllCardData(cardsToRefresh, false, true); // silent=false, onlyVisible=true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleCardKeys, showHidden]);

  // Trigger re-fetch when collapse state changes (user expands/collapses cards)
  // This ensures the batch-cards request includes the correct set of visible cards
  const prevCollapsedRef = useRef<{ [key: string]: boolean }>({});
  useEffect(() => {
    // Skip if configs not loaded yet
    if (!configsLoaded) return;

    // Check if any cards were expanded (collapsed -> not collapsed)
    const expandedCards: string[] = [];
    Object.keys(collapsed).forEach(key => {
      if (prevCollapsedRef.current[key] === true && collapsed[key] === false) {
        expandedCards.push(key);
      }
    });

    // Update the previous collapsed state
    prevCollapsedRef.current = { ...collapsed };

    // If no cards were expanded, don't re-fetch (collapsing doesn't need a re-fetch)
    if (expandedCards.length === 0) return;

    // Cancel any in-progress batch request and fetch the updated set of visible cards
    if (batchCardsAbortControllerRef.current) {
      batchCardsAbortControllerRef.current.abort();
      batchCardsAbortControllerRef.current = null;
    }

    // Fetch data for all non-collapsed cards in the current workspace
    const cardsToRefresh = cardConfigsRef.current.filter((card: CardConfig) => {
      if (collapsed[card.key]) return false;
      return card.workspaceId === currentWorkspaceId &&
        (card.bucket || (card.filterConditions && card.filterConditions.length > 0));
    });
    fetchAllCardData(cardsToRefresh, false, true); // silent=false, onlyVisible=true
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsed]);

  // Tab optimization disabled - loading full card data instead
  // const prevSelectedTabsRef = useRef<{ [cardKey: string]: string }>({});

  // Memoized callbacks for TanTable props - prevents unnecessary re-renders
  const handleEditFilters = useCallback((cardKey: string) => {
    if (cardKey.startsWith('managed:')) return;
    // A system bucket's query stays read-only — the cog opens the in-place bucket filter,
    // which ANDs conditions onto that canonical WHERE rather than editing it.
    // Custom cards keep the full card-settings modal (name, stat button, scope, delete).
    if (systemBucketDefaults.has(cardKey)) {
      const cfg = cardConfigsRef.current.find(c => c.key === cardKey);
      setFilterTarget({ key: cardKey, label: cfg?.title || 'Bucket' });
      return;
    }
    setEditingCardFiltersKey(cardKey);
  }, [systemBucketDefaults]);

  /**
   * Apply (or, with an empty list, clear) the in-place filter on one bucket and refetch it.
   *
   * The ref is written BEFORE the refetch on purpose: fetchAllCardData reads the filter
   * through bucketFiltersRef, and setState has not committed by the time we call it — the
   * same ordering the card-settings modal uses for cardConfigsRef. Without it the refetch
   * would run the PREVIOUS filter and the grid would lag one apply behind.
   *
   * Counts are deliberately not refreshed: they track the header filters only.
   */
  const handleApplyBucketFilter = useCallback((cardKey: string, conditions: FilterCondition[]) => {
    const next = { ...bucketFiltersRef.current };
    if (conditions.length > 0) next[cardKey] = conditions;
    else delete next[cardKey];
    bucketFiltersRef.current = next;
    setBucketFilters(next);
    setFilterTarget(null);

    if (cardKey.startsWith('grid:')) {
      // A grid-panel drill has no card config and no fetcher of its own — the effect that
      // owns it re-runs on this nonce, and reads the new filter through the ref.
      setGridDrillNonce((n) => n + 1);
      return;
    }
    const cfg = cardConfigsRef.current.find(c => c.key === cardKey);
    // onlyVisible=false: the drill is open on this card, and the visibility test is about
    // which cards the board shows, not whether this one asked to be refetched.
    if (cfg) fetchAllCardData([cfg], false, false);
  }, [fetchAllCardData, setGridDrillNonce]);

  /**
   * The TanTable props that put a custom filter on a drill: the cog, its lit state, the
   * Clear button and the "Custom Filtered - N" badge.
   *
   * One helper rather than the same eight props repeated at each host. Every drill on
   * this page offers the identical control, and the set has grown twice already (the
   * active state, then the clear button) — spreading it means a third addition cannot
   * land on four of five grids and be missed on the fifth.
   */
  const customFilterProps = useCallback((
    filterKey: string,
    label: string,
    /** Scope/cap metadata, where the host has it (bucket cards). The pill reports all
     *  three facts together; a drill with no scope of its own just omits this. */
    scope?: { includeClosed?: boolean; truncated?: boolean; limit?: number },
  ) => {
    const count = (bucketFilters[filterKey] || []).length;
    return {
      onEditFilters: () => setFilterTarget({ key: filterKey, label }),
      showEditFiltersButton: true,
      editFiltersButtonIcon: 'cog' as const,
      editFiltersButtonTooltip: 'Filter this bucket',
      editFiltersActive: count > 0,
      customFilterCount: count,
      onClearCustomFilters: () => handleApplyBucketFilter(filterKey, []),
      headerExtra: (
        <CardResultPill
          includeClosed={Boolean(scope?.includeClosed)}
          truncated={scope?.truncated}
          limit={scope?.limit}
          filterCount={count}
          filterSummary={describeConditions(bucketFilters[filterKey])}
        />
      ),
    };
  }, [bucketFilters, describeConditions, handleApplyBucketFilter]);

  const handleGroupingChange = useCallback((cardKey: string, grouping: string[]) => {
    setCardConfigs(prev => prev.map(card =>
      card.key === cardKey ? { ...card, grouping } : card
    ));
  }, []);

  const handleTabGroupingChange = useCallback((cardKey: string, tabGrouping: string) => {
    setCardConfigs(prev => prev.map(card =>
      card.key === cardKey ? { ...card, tabGrouping } : card
    ));
  }, []);

  const handleVisibleColumnsChange = useCallback((cardKey: string, visibleColumns: string[]) => {
    setCardConfigs(prev => prev.map(card =>
      card.key === cardKey ? { ...card, visibleColumns } : card
    ));
  }, []);

  // Handle additional fields change - updates card config and triggers refetch
  const handleAdditionalFieldsChange = useCallback((cardKey: string, additionalFields: string[]) => {
    // Update the card config with new additional fields, and PERSIST it. Column
    // show/hide/reorder + grouping are saved by TanTable itself (via its
    // settingsKey), but additionalFields is not part of TanTable's settings
    // payload (buildSettingsData) — so a user-added column only survives reload
    // if we save it into the dashboard layout ourselves. Without this, added
    // columns on custom cards were lost on refresh (mirrors the Dispatch
    // Dashboard fix; the accounting-drill drills persist their own way). Debounced.
    setCardConfigs(prev => {
      const updated = prev.map(card =>
        card.key === cardKey ? { ...card, additionalFields } : card
      );
      saveLayoutRef.current?.(updated, false);
      return updated;
    });

    // Find the card and trigger a refetch with the new additional fields
    const card = cardConfigs.find(c => c.key === cardKey);
    if (card && (card.bucket || (card.filterConditions && card.filterConditions.length > 0))) {
      fetchCardData(
        cardKey,
        card.filterConditions || [],
        card.filterView || 'Orders',
        card.bucket,
        false,
        additionalFields
      );
    }
  }, [cardConfigs, fetchCardData]);

  const handleDuplicate = useCallback((cardConfig: CardConfig) => {
    setDuplicateCardConfig(cardConfig);
  }, []);

  const handleMultiTabChange = useCallback((cardKey: string, tabIds: string[]) => {
    setCardActiveTabsMulti(prev => ({ ...prev, [cardKey]: tabIds }));
  }, []);

  // Check if any modal is open (to pause auto-refresh)
  const isAnyModalOpen = isFirstCardModalOpen || !!editingCardFiltersKey || !!filterTarget || !!duplicateCardConfig || showNewWorkspaceModal || !!selectedProNumber || addCostModal.isOpen;

  // Set up auto-refresh interval (every 30 seconds) - pause when modals are open.
  // Refreshes the open bucket's data; the counts queries only run when nothing is
  // drilled in (the counts panels aren't visible/focused then, so no need to hit
  // them) — that's a bucket OR a grid cell being open.
  const REFRESH_INTERVAL = 30; // seconds

  // A bucket or grid cell is open → the counts panels are hidden/unfocused.
  const isDrillOpen = isSingleCardSelected || !!gridSel || !!ediSelection;

  // A collapsed panel shows only its header, so its body isn't visible — pause that
  // panel's background refresh until the user expands it again.
  // currentPanelItems holds only the current workspace's panels. A panel is "inactive"
  // (don't fetch / pause refresh) when it's ABSENT (belongs to another workspace) OR
  // present-and-collapsed. A present panel with no saved collapsed flag is expanded.
  const isPanelInactive = (key: string): boolean => {
    const item = currentPanelItems.find((i) => i.key === key);
    return !item || !!item.collapsed;
  };
  // COUNT panels are the exception: a collapsed one still shows its roll-up count and
  // severity tone in its header (summarizeCountPanel), and a panel that has been collapsed
  // since page load would have nothing to show if we skipped its query. So their fetches
  // pause only when the panel is OFF-WORKSPACE. The grid/table panels (schedule, tracking,
  // exports, invoicing stats, per diem, additional cost, chassis) have no header roll-up,
  // so they stay paused while collapsed.
  const isPanelOffWorkspace = (key: string): boolean => !currentPanelItems.some((i) => i.key === key);
  const schedulePanelCollapsed = isPanelInactive(SCHEDULE_PANEL_KEY);
  const trackingPanelCollapsed = isPanelInactive(TRACKING_PANEL_KEY);
  const exportsSummaryPanelCollapsed = isPanelInactive(EXPORTS_SUMMARY_PANEL_KEY);
  exportsSummaryCollapsedRef.current = exportsSummaryPanelCollapsed; // let fetchExportsSummaryCounts self-skip when collapsed/off-workspace
  const exportsBoardPanelCollapsed = isPanelInactive(EXPORTS_BOARD_PANEL_KEY);
  exportsBoardCollapsedRef.current = exportsBoardPanelCollapsed; // let fetchExportsBoard self-skip when collapsed/off-workspace
  const invStatsPanelCollapsed = isPanelInactive(INVSTATS_PANEL_KEY);
  invStatsCollapsedRef.current = invStatsPanelCollapsed; // let fetchInvStats self-skip when collapsed/off-workspace
  // Count panels — off-workspace only (see isPanelOffWorkspace).
  const warehousingFetchSkip = isPanelOffWorkspace(WAREHOUSING_PANEL_KEY);
  warehousingFetchSkipRef.current = warehousingFetchSkip;
  const pinvFetchSkip = isPanelOffWorkspace(PINV_PANEL_KEY);
  pinvFetchSkipRef.current = pinvFetchSkip;
  const ticketFetchSkip = isPanelOffWorkspace(TICKETS_PANEL_KEY);
  ticketFetchSkipRef.current = ticketFetchSkip;
  const tablemaintFetchSkip = isPanelOffWorkspace(TABLEMAINT_PANEL_KEY);
  tablemaintFetchSkipRef.current = tablemaintFetchSkip;
  const melroseFetchSkip = isPanelOffWorkspace(MELROSE_PANEL_KEY);
  melroseFetchSkipRef.current = melroseFetchSkip;
  const whsePhotoFetchSkip = isPanelOffWorkspace(WHSEPHOTO_PANEL_KEY);
  whsePhotoFetchSkipRef.current = whsePhotoFetchSkip;
  const ediFetchSkip = isPanelOffWorkspace(EDI_PANEL_KEY);
  // Grid/table panels — paused while collapsed as well as off-workspace.
  const perDiemPanelCollapsed = isPanelInactive(PERDIEM_PANEL_KEY);
  perDiemCollapsedRef.current = perDiemPanelCollapsed; // let fetchPerDiem self-skip when collapsed/off-workspace
  const addCostPanelCollapsed = isPanelInactive(ADDCOST_PANEL_KEY);
  addCostCollapsedRef.current = addCostPanelCollapsed; // let fetchAdditionalCost self-skip when collapsed/off-workspace
  const chassisPanelCollapsed = isPanelInactive(CHASSIS_PANEL_KEY);
  chassisCollapsedRef.current = chassisPanelCollapsed; // let fetchChassisCharges self-skip when collapsed
  // Whether this workspace has any count panel at all — collapsed ones included, since
  // their headers still need the numbers (absent → belongs to another workspace).
  const anyCountPanelPresent = LC_COUNT_PANELS.some((p) => currentPanelItems.some((i) => i.key === p.key));

  useVisibilityAwareInterval(
    () => {
      // Pause the open drill's silent refresh while pickup edits are staged/saving so it
      // can't clobber in-progress work (the No Pickup Number bucket's inline editor).
      if (!pickupDirtyRef.current) refreshAllCards();
      if (!isDrillOpen) {
        if (anyCountPanelPresent) fetchBucketCounts(true);
        if (!schedulePanelCollapsed) fetchScheduleCounts(true);
        if (!trackingPanelCollapsed) fetchTrackingCounts(true);
        if (!exportsSummaryPanelCollapsed) fetchExportsSummaryCounts(true);
        if (!exportsBoardPanelCollapsed) fetchExportsBoard(true);
        if (!invStatsPanelCollapsed) fetchInvStats(true);
        if (!warehousingFetchSkip) fetchWarehouseCounts(true);
        if (!pinvFetchSkip) fetchPendingInvoicing(true);
        if (!perDiemPanelCollapsed) fetchPerDiem(true);
        if (!addCostPanelCollapsed) fetchAdditionalCost(true);
        if (!ticketFetchSkip) fetchTroubleTickets(true);
        if (!tablemaintFetchSkip) fetchTablemaintCounts(true);
        if (!chassisPanelCollapsed) fetchChassisCharges(true);
        if (!melroseFetchSkip) fetchMelroseCounts(true);
        if (!whsePhotoFetchSkip) fetchWhsePhotoCounts(true);
        if (!ediFetchSkip) fetchEdiBuckets(true);
      }
    },
    initialLoadComplete && !isAnyModalOpen ? REFRESH_INTERVAL * 1000 : null
  );

  // The interval above is paused while a modal/drawer is open and restarts from
  // zero on unpause, so an edit made in the PRO drawer (LFD, invoice hold, …)
  // could sit stale in the open bucket for up to a full tick after closing.
  // Fire one immediate silent refresh as the last modal/drawer closes.
  useFallingEdge(isAnyModalOpen, () => {
    if (!initialLoadComplete) return;
    if (!pickupDirtyRef.current) refreshAllCards();
    if (!isDrillOpen && anyCountPanelPresent) fetchBucketCounts(true);
  });

  // When a collapsed panel is expanded, refresh its data once so the user doesn't
  // see counts that went stale while the refresh was paused.
  const prevPanelCollapsedRef = useRef<{ [key: string]: boolean }>({});
  useEffect(() => {
    const prev = prevPanelCollapsedRef.current;
    const next: { [key: string]: boolean } = {};
    let countExpanded = false;
    let scheduleExpanded = false;
    let trackingExpanded = false;
    let exportsSummaryExpanded = false;
    let exportsBoardExpanded = false;
    let invStatsExpanded = false;
    let warehouseExpanded = false;
    let pinvExpanded = false;
    let perDiemExpanded = false;
    let addCostExpanded = false;
    let ticketExpanded = false;
    let tablemaintExpanded = false;
    let chassisExpanded = false;
    let melroseExpanded = false;
    let whsePhotoExpanded = false;
    currentPanelItems.forEach((i) => {
      next[i.key] = !!i.collapsed;
      // collapsed (true) -> expanded (false) transition
      if (prev[i.key] === true && !i.collapsed) {
        if (i.key === SCHEDULE_PANEL_KEY) scheduleExpanded = true;
        else if (i.key === TRACKING_PANEL_KEY) trackingExpanded = true;
        else if (i.key === EXPORTS_SUMMARY_PANEL_KEY) exportsSummaryExpanded = true;
        else if (i.key === EXPORTS_BOARD_PANEL_KEY) exportsBoardExpanded = true;
        else if (i.key === INVSTATS_PANEL_KEY) invStatsExpanded = true;
        else if (i.key === WAREHOUSING_PANEL_KEY) warehouseExpanded = true;
        else if (i.key === PINV_PANEL_KEY) pinvExpanded = true;
        else if (i.key === PERDIEM_PANEL_KEY) perDiemExpanded = true;
        else if (i.key === ADDCOST_PANEL_KEY) addCostExpanded = true;
        else if (i.key === TICKETS_PANEL_KEY) ticketExpanded = true;
        else if (i.key === TABLEMAINT_PANEL_KEY) tablemaintExpanded = true;
        else if (i.key === CHASSIS_PANEL_KEY) chassisExpanded = true;
        else if (i.key === MELROSE_PANEL_KEY) melroseExpanded = true;
        else if (i.key === WHSEPHOTO_PANEL_KEY) whsePhotoExpanded = true;
        else countExpanded = true;
      }
    });
    prevPanelCollapsedRef.current = next;
    if (!configsLoaded || isDrillOpen) return;
    if (countExpanded) fetchBucketCounts(true);
    if (scheduleExpanded) fetchScheduleCounts(true);
    if (trackingExpanded) fetchTrackingCounts(true);
    if (exportsSummaryExpanded) fetchExportsSummaryCounts(true);
    if (exportsBoardExpanded) fetchExportsBoard(true);
    if (invStatsExpanded) fetchInvStats(true);
    if (warehouseExpanded) fetchWarehouseCounts(true);
    if (pinvExpanded) fetchPendingInvoicing(true);
    if (perDiemExpanded) fetchPerDiem(true);
    if (addCostExpanded) fetchAdditionalCost(true);
    if (ticketExpanded) fetchTroubleTickets(true);
    if (tablemaintExpanded) fetchTablemaintCounts(true);
    if (chassisExpanded) fetchChassisCharges(true);
    if (melroseExpanded) fetchMelroseCounts(true);
    if (whsePhotoExpanded) fetchWhsePhotoCounts(true);
  }, [currentPanelItems, configsLoaded, isDrillOpen, fetchBucketCounts, fetchScheduleCounts, fetchTrackingCounts, fetchExportsSummaryCounts, fetchExportsBoard, fetchInvStats, fetchWarehouseCounts, fetchPendingInvoicing, fetchPerDiem, fetchAdditionalCost, fetchTroubleTickets, fetchTablemaintCounts, fetchChassisCharges, fetchMelroseCounts, fetchWhsePhotoCounts]);

  // Counts refresh is paused while a drill-down is open (above). When it closes,
  // refresh once so the counts the user returns to aren't stale.
  const prevDrillOpenRef = useRef(isDrillOpen);
  useEffect(() => {
    const wasOpen = prevDrillOpenRef.current;
    prevDrillOpenRef.current = isDrillOpen;
    if (wasOpen && !isDrillOpen && configsLoaded) {
      // Only refresh the panels active in the current workspace (the *Collapsed /
      // *FetchSkip flags are true for panels that belong to another workspace).
      if (anyCountPanelPresent) fetchBucketCounts(true);
      if (!schedulePanelCollapsed) fetchScheduleCounts(true);
      if (!trackingPanelCollapsed) fetchTrackingCounts(true);
      if (!exportsSummaryPanelCollapsed) fetchExportsSummaryCounts(true);
      if (!exportsBoardPanelCollapsed) fetchExportsBoard(true);
      if (!invStatsPanelCollapsed) fetchInvStats(true);
      if (!warehousingFetchSkip) fetchWarehouseCounts(true);
      if (!pinvFetchSkip) fetchPendingInvoicing(true);
      if (!perDiemPanelCollapsed) fetchPerDiem(true);
      if (!addCostPanelCollapsed) fetchAdditionalCost(true);
      if (!ticketFetchSkip) fetchTroubleTickets(true);
      if (!tablemaintFetchSkip) fetchTablemaintCounts(true);
      if (!chassisPanelCollapsed) fetchChassisCharges(true);
      if (!melroseFetchSkip) fetchMelroseCounts(true);
      if (!whsePhotoFetchSkip) fetchWhsePhotoCounts(true);
    }
  }, [isDrillOpen, configsLoaded, fetchBucketCounts, fetchScheduleCounts, fetchTrackingCounts, fetchExportsSummaryCounts, fetchExportsBoard, fetchInvStats, fetchWarehouseCounts, fetchPendingInvoicing, fetchPerDiem, fetchAdditionalCost, fetchTroubleTickets, fetchTablemaintCounts, fetchChassisCharges, fetchMelroseCounts, fetchWhsePhotoCounts]);

  // Load card configurations from database on mount
  useEffect(() => {
    const loadCardConfigs = async () => {
      try {
        const layout = await userSettingsService.getDashboardLayoutNew('lc');
        const defaultCards = getDefaultCardConfigs();

        // Note: We no longer reset on version mismatch to preserve user data.
        // If migration is needed for a new version, it should be done incrementally below.
        if (layout && (layout.cardConfigs?.length > 0 || layout.workspaces?.length > 0)) {
          // Get the set of default locked card keys for comparison
          const defaultLockedKeys = new Set(defaultCards.filter(dc => dc.locked).map(dc => dc.key));

          // Migrate and validate loaded configs (including locked cards)
          const migrated = layout.cardConfigs
            .filter((card: any) => card.key !== 'testCard')  // Only exclude testCard
            .map((card: any) => {
              const migratedCard: any = { ...card };
              if (typeof card.column === 'string') {
                migratedCard.column = parseInt(card.column) || 0;
              }
              if (!card.span) {
                migratedCard.span = 1;
              }
              // Ensure workspaceId is set (default to 'default' for old configs)
              if (!card.workspaceId) {
                migratedCard.workspaceId = 'default';
              }
              // Preserve the saved locked state (user may have toggled it).
              // For cards with no saved locked field, default to true if it's a default card, false otherwise.
              if (typeof card.locked === 'boolean') {
                migratedCard.locked = card.locked;
              } else {
                migratedCard.locked = defaultLockedKeys.has(card.key);
              }
              // Heal legacy data: older builds persisted the collapsed visual
              // height (h=1) into gsH, which left cards tiny after returning to
              // the page. Restore the expanded height when we find it.
              if (typeof card.gsH === 'number' && card.gsH < 2) {
                if (typeof card.expandedGsH === 'number' && card.expandedGsH >= 2) {
                  migratedCard.gsH = card.expandedGsH;
                } else {
                  migratedCard.gsH = 6;
                }
              }
              // Scrub overrides on system buckets. Before in-place editing was
              // removed, users could open the filter cog and change a locked
              // bucket's query, title, and stat button; those edits got persisted
              // into their saved layout. Force every field the cog could touch back
              // to the canonical code definition so these buckets are identical for
              // everyone — same query (bucket / conditions / view), same title, same
              // stat button. Presentation the user legitimately controls (visible
              // columns, grouping, height, grid position) is left intact.
              // Idempotent: clean layouts are unchanged, the next save persists this.
              const systemDefault = systemBucketDefaults.get(card.key);
              if (systemDefault) {
                migratedCard.bucket = systemDefault.bucket;
                // A system bucket belongs to its panel's workspace (code-defined, not
                // user-controlled) — heal any stale saved workspace.
                migratedCard.workspaceId = systemDefault.workspaceId;
                migratedCard.filterConditions = systemDefault.filterConditions ?? [];
                migratedCard.filterView = systemDefault.filterView;
                migratedCard.title = systemDefault.title;
                migratedCard.showStatButton = systemDefault.showStatButton;
                migratedCard.statButtonLabel = systemDefault.statButtonLabel;
                migratedCard.statButtonCountField = systemDefault.statButtonCountField;
                migratedCard.isShared = systemDefault.isShared;
              }
              return migratedCard;
            });

          // Check for missing locked cards and add them with default positioning
          // (defaultCards is already defined above)

          // Create a set of ALL saved card keys (not just locked ones)
          const savedCardKeys = new Set(migrated.map((c: any) => c.key));

          // Only add locked cards that don't exist at all in the saved layout
          const missingLockedCards = defaultCards.filter(dc => dc.locked && !savedCardKeys.has(dc.key));

          // Combine all cards (missing locked cards + existing saved cards)
          const allCards = missingLockedCards.length > 0
            ? [...missingLockedCards, ...migrated]
            : migrated;

          setCardConfigs(allCards);

          // Load workspaces if saved
          if (layout.workspaces && layout.workspaces.length > 0) {
            // Ensure all workspaces have a columnCount (default to 2 if missing for backwards compatibility)
            // Also ensure default workspace is named "Main"
            const workspacesWithColumnCount = layout.workspaces.map((ws: Workspace) => ({
              ...ws,
              columnCount: ws.columnCount || 2,
              name: ws.id === 'default' ? 'Main' : ws.name
            }));

            // Add any missing default workspaces (e.g., new workspaces added in code updates)
            const defaultWorkspaces = getDefaultWorkspaces();
            const savedWorkspaceIds = new Set(workspacesWithColumnCount.map((ws: Workspace) => ws.id));
            const missingWorkspaces = defaultWorkspaces.filter(ws => !savedWorkspaceIds.has(ws.id));
            const mergedWorkspaces = [...workspacesWithColumnCount, ...missingWorkspaces];

            setWorkspaces(mergedWorkspaces);

            // Restore the active board: this tab's own choice first, then the
            // server-saved one for a brand-new tab, then 'default'. The saved
            // layout is one shared per-user record, so without the per-tab value
            // a second app tab's board became this tab's board on every mount.
            const restoredWorkspaceId = resolveWorkspaceId({
              tabId: readTabWorkspaceId('lc'),
              savedId: layout.currentWorkspaceId,
              available: mergedWorkspaces.map((ws: Workspace) => ws.id),
            });
            setCurrentWorkspaceId(restoredWorkspaceId);

            // Set initial columnCount from the restored active workspace
            const activeWorkspace = workspacesWithColumnCount.find((ws: Workspace) => ws.id === restoredWorkspaceId) || workspacesWithColumnCount[0];
            if (activeWorkspace) {
              setColumnCount(activeWorkspace.columnCount);
            }
          } else {
            const defaults = getDefaultWorkspaces();
            setWorkspaces(defaults);
            setCurrentWorkspaceId(resolveWorkspaceId({
              tabId: readTabWorkspaceId('lc'),
              savedId: layout.currentWorkspaceId,
              available: defaults.map(ws => ws.id),
            }));
            // Load global column count as fallback for backwards compatibility
            if (layout.columnCount) {
              setColumnCount(layout.columnCount);
            }
          }

          // Load collapsed state if saved
          if (layout.collapsed) {
            setCollapsed(layout.collapsed);
          }
          // Load visible card keys (selected bucket) if saved
          if (layout.visibleCardKeys && Array.isArray(layout.visibleCardKeys)) {
            setVisibleCardKeys(new Set(layout.visibleCardKeys));
          }
          // Load filters if saved
          if (layout.selectedLCs && Array.isArray(layout.selectedLCs)) {
            setSelectedLCs(layout.selectedLCs);
          }
          if (layout.selectedCustomers && Array.isArray(layout.selectedCustomers)) {
            setSelectedCustomers(layout.selectedCustomers);
          }
          if (layout.selectedBilltos && Array.isArray(layout.selectedBilltos)) {
            setSelectedBilltos(layout.selectedBilltos);
          }
          if (layout.selectedKeyAccounts && Array.isArray(layout.selectedKeyAccounts)) {
            setSelectedKeyAccounts(layout.selectedKeyAccounts);
          }
          if (layout.selectedDispatchRegions && Array.isArray(layout.selectedDispatchRegions)) {
            setSelectedDispatchRegions(layout.selectedDispatchRegions);
          }
          if (layout.selectedWarehouses && Array.isArray(layout.selectedWarehouses)) {
            setSelectedWarehouses(layout.selectedWarehouses);
          }
          // Load workspace-specific visible cards if saved
          if (layout.workspaceVisibleCards && typeof layout.workspaceVisibleCards === 'object') {
            setWorkspaceVisibleCards(layout.workspaceVisibleCards);
          }
          // (Panel layouts persist in their own user setting — loaded separately.)
          // Note: Data fetching is handled by the workspace switch effect when configsLoaded becomes true
        } else {
          // No saved layout, use defaults (which includes locked cards)
          setCardConfigs(defaultCards);
          setWorkspaces(getDefaultWorkspaces());
          // Note: Data fetching is handled by the workspace switch effect when configsLoaded becomes true
        }
      } catch (error) {
        console.error('Failed to load dashboard layout from database:', error);
        // Show defaults so the dashboard still renders, but mark the load as failed so
        // saveLayout refuses to write them back — otherwise a transient GET failure
        // silently overwrites the user's saved arrangement with the code default.
        setLayoutLoadFailed(true);
        const defaultCards = getDefaultCardConfigs();
        setCardConfigs(defaultCards);
        setWorkspaces(getDefaultWorkspaces());
        // Note: Data fetching is handled by the workspace switch effect when configsLoaded becomes true
      } finally {
        setConfigsLoaded(true);
      }
    };

    loadCardConfigs();
    // Fetch all table settings in parallel (batch request instead of per-table calls)
    fetchAllTableSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Retry the batch settings fetch on backoff while it is in a failed state.
  // On success, fetchAllTableSettings clears tableSettingsLoadFailed, which flows down
  // to each TanTable (settingsLoadFailed=false + fresh initialSettings) to re-apply + unblock saving.
  useEffect(() => {
    if (!tableSettingsLoadFailed) return;
    let cancelled = false;
    let attempt = 0;
    let timer: ReturnType<typeof setTimeout>;
    const schedule = () => {
      const delay = Math.min(15000 * 2 ** attempt, 60000);
      timer = setTimeout(() => {
        attempt += 1;
        if (!cancelled) fetchAllTableSettings(); // clears the flag on success
      }, delay);
    };
    schedule();
    return () => { cancelled = true; clearTimeout(timer); };
  }, [tableSettingsLoadFailed, fetchAllTableSettings]);

  // Mark initial load as complete after configs are loaded and a short delay for data fetching
  useEffect(() => {
    if (!configsLoaded) return;

    // Wait a bit for initial card data to start loading, then enable refresh
    const timer = setTimeout(() => {
      setInitialLoadComplete(true);
    }, 2000); // 2 second delay after configs load

    return () => clearTimeout(timer);
  }, [configsLoaded]);

  // Remember this tab's board. The layout record (which carries currentWorkspaceId)
  // is shared by every open app tab and rewritten in full on any change, so without
  // a per-tab copy another tab's board wins the next time this page mounts.
  useEffect(() => {
    if (!configsLoaded) return;
    writeTabWorkspaceId('lc', currentWorkspaceId);
  }, [currentWorkspaceId, configsLoaded]);

  // Sync columnCount with current workspace when switching workspaces
  useEffect(() => {
    if (!configsLoaded) return;

    const currentWorkspace = workspaces.find(ws => ws.id === currentWorkspaceId);
    if (currentWorkspace) {
      // Set flag to prevent the save effect from overwriting the workspace's stored columnCount
      isRestoringColumnCountRef.current = true;
      setColumnCount(currentWorkspace.columnCount);
      // Clear flag after React has processed the state update
      setTimeout(() => {
        isRestoringColumnCountRef.current = false;
      }, 0);
    }
    // Only fire when workspace changes, not when columnCount changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId, configsLoaded]);

  // Check if all loading is complete (matches the loading condition in render)
  const isFullyLoaded = configsLoaded && !fieldDefinitionsLoading && (fieldDefinitions.length > 0 || fieldDefinitionsError);

  // Initialize/reinitialize GridStack when workspace changes or all loading is complete
  useEffect(() => {
    // Don't initialize until everything is loaded (grid-stack div won't exist during loading screen)
    if (!isFullyLoaded) return;

    // Destroy existing grid
    if (gridInstanceRef.current) {
      gridInstanceRef.current.destroy(false);
      gridInstanceRef.current = null;
    }

    // Mark that we're initializing (to skip change events during init)
    isGridInitializing.current = true;

    // Delay to ensure DOM is ready - ref check moved inside timeout because
    // React may not have rendered the grid-stack div yet when loading completes
    const timer = setTimeout(() => {
      if (!gridRef.current) return;

      const grid = GridStack.init({
        column: 12,
        cellHeight: 80,
        margin: 4,
        float: false,
        animate: true,
        disableDrag: false,
        disableResize: false,
        resizable: {
          handles: 'se',
        },
      }, gridRef.current);

      gridInstanceRef.current = grid;

      // Listen for changes and update cardConfigs with new positions
      grid.on('change', () => {
        // Skip change events during initialization (GridStack fires change on init)
        if (isGridInitializing.current) {
          return;
        }

        const nodes = grid.getGridItems();

        // Build a map of card positions from GridStack
        const positionMap: { [id: string]: { x: number; y: number; w: number; h: number } } = {};
        nodes.forEach((el) => {
          const node = el.gridstackNode;
          // Also read from DOM attributes as fallback
          const domId = el.getAttribute('gs-id');
          const domX = parseInt(el.getAttribute('gs-x') || '0', 10);
          const domY = parseInt(el.getAttribute('gs-y') || '0', 10);
          const domW = parseInt(el.getAttribute('gs-w') || '4', 10);
          const domH = parseInt(el.getAttribute('gs-h') || '5', 10);

          const id = node?.id || domId;
          if (id) {
            // Prefer gridstackNode values if they look valid (w >= 1), otherwise use DOM
            const useNode = node && node.w && node.w >= 1;
            const w = useNode ? (node.w ?? domW) : domW;
            const h = useNode ? (node.h ?? domH) : domH;

            // Skip saving if w or h are invalid (less than 1)
            if (w < 1 || h < 1) {
              return; // Don't add to positionMap - will keep existing valid values
            }

            positionMap[id] = {
              x: useNode ? (node.x ?? domX) : domX,
              y: useNode ? (node.y ?? domY) : domY,
              w,
              h,
            };
          }
        });

        // Update cardConfigs with GridStack positions directly
        // Only update cards that are in the positionMap (visible in current grid)
        const updated = cardConfigsRef.current.map(card => {
          const pos = positionMap[card.key];
          if (!pos) return card; // Card not in current grid, keep unchanged

          // h === 1 is the collapsed visual state — don't persist it as the
          // card's expanded height. Keep the previous gsH so the next expand
          // restores the user's chosen size.
          const nextH = pos.h === 1 ? (card.gsH ?? pos.h) : pos.h;

          // Store GridStack positions directly for exact persistence
          return {
            ...card,
            gsX: pos.x,
            gsY: pos.y,
            gsW: pos.w,
            gsH: nextH,
          };
        });

        setCardConfigs(updated);
        // Persist the new positions/sizes (debounced) so a bare drag/resize sticks
        // across reloads — not only when another action later triggers a save.
        saveLayout(updated);
      });

      // Apply lock state to widgets (locked cards can't be moved or resized)
      cardConfigsRef.current.forEach(card => {
        if (card.locked && gridRef.current) {
          const el = gridRef.current.querySelector(`[gs-id="${card.key}"]`);
          if (el) {
            grid.update(el as HTMLElement, { noMove: true, noResize: true });
          }
        }
      });

      // gsH always holds the expanded height. Visually collapse widgets whose
      // saved state is collapsed by shrinking them to h=1 (header only).
      cardConfigsRef.current.forEach(card => {
        if (collapsedRef.current[card.key] && gridRef.current) {
          const el = gridRef.current.querySelector(`[gs-id="${card.key}"]`);
          if (el) {
            grid.update(el as HTMLElement, { h: 1 });
          }
        }
      });

      // After a short delay, allow change events (initialization is complete)
      setTimeout(() => {
        isGridInitializing.current = false;
      }, 200);
    }, 100);

    return () => {
      clearTimeout(timer);
      // Clear any pending save to prevent stale saves when switching workspaces
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
      if (gridInstanceRef.current) {
        gridInstanceRef.current.destroy(false);
        gridInstanceRef.current = null;
      }
    };
  }, [currentWorkspaceId, isFullyLoaded]);

  // Track card keys to detect when new cards are added
  const prevCardKeysRef = useRef<Set<string>>(new Set());

  // Handle new cards being added - use GridStack's makeWidget API
  useEffect(() => {
    if (!gridInstanceRef.current || !isFullyLoaded) return;

    const currentKeys = new Set(currentGridItems.map(item => item.id));
    const prevKeys = prevCardKeysRef.current;

    // React removes hidden tiles from the DOM; remove their GridStack nodes too
    // so they no longer reserve space or affect the remaining cards' positions.
    const grid = gridInstanceRef.current;
    for (const node of [...grid.engine.nodes]) {
      if (node.id && !currentKeys.has(String(node.id)) && node.el) {
        grid.removeWidget(node.el, false);
      }
    }

    // Find newly added cards
    const newKeys = [...currentKeys].filter(key => !prevKeys.has(key));

    if (newKeys.length > 0) {
      // Give React time to render the new DOM elements, then make them widgets
      setTimeout(() => {
        if (!gridInstanceRef.current) return;

        newKeys.forEach(key => {
          const element = document.querySelector(`[gs-id="${key}"]`) as HTMLElement;
          if (element && !element.classList.contains('ui-draggable')) {
            gridInstanceRef.current?.makeWidget(element);
          }
        });
      }, 50);
    }

    prevCardKeysRef.current = currentKeys;
  }, [currentGridItems, isFullyLoaded]);

  // Reset initial load state and fetch fresh data when switching workspaces
  // Track previous workspace to detect actual workspace changes (not initial mount)
  const prevWorkspaceIdRef = React.useRef<string | null>(null);

  useEffect(() => {
    if (!configsLoaded) return;
    // Initial mount also waits for the saved-filter restore so the first
    // batch-cards request carries the user's real filters (prevWorkspaceIdRef
    // stays null until we proceed, so this still counts as initial mount).
    if (!filtersReady) return;

    // Track if this is initial mount
    const isInitialMount = prevWorkspaceIdRef.current === null;

    // Only proceed if workspace changed OR this is initial mount
    if (!isInitialMount && prevWorkspaceIdRef.current === currentWorkspaceId) {
      return;
    }

    const previousWorkspaceId = prevWorkspaceIdRef.current;

    // Only cancel existing requests on workspace SWITCH, not initial mount
    if (!isInitialMount && previousWorkspaceId) {
      // Save current layout immediately before switching (preserves grouping, filters, etc.)
      const currentCards = cardConfigsRef.current;
      userSettingsService.saveDashboardLayoutNew('lc', {
        cardConfigs: currentCards,
        columnCount: saveStateRef.current.columnCount,
        workspaces: saveStateRef.current.workspaces,
        collapsed: saveStateRef.current.collapsed,
        currentWorkspaceId: saveStateRef.current.currentWorkspaceId,
        visibleCardKeys: saveStateRef.current.visibleCardKeys,
        workspaceVisibleCards: {
          ...saveStateRef.current.workspaceVisibleCards,
          [previousWorkspaceId]: Array.from(visibleCardKeys)
        },
        selectedLCs: saveStateRef.current.selectedLCs,
        selectedCustomers: saveStateRef.current.selectedCustomers,
        selectedBilltos: saveStateRef.current.selectedBilltos,
        selectedKeyAccounts: saveStateRef.current.selectedKeyAccounts,
        selectedDispatchRegions: saveStateRef.current.selectedDispatchRegions,
        selectedWarehouses: saveStateRef.current.selectedWarehouses,
        version: CURRENT_VERSION
      }).catch(err => console.error('Failed to save layout on workspace switch:', err));

      // Save current visible card keys for the previous workspace before switching
      setWorkspaceVisibleCards(prev => ({
        ...prev,
        [previousWorkspaceId]: Array.from(visibleCardKeys)
      }));

      // Cancel card data requests
      Object.keys(abortControllersRef.current).forEach(cardKey => {
        abortControllersRef.current[cardKey]?.abort();
        delete abortControllersRef.current[cardKey];
      });

      // Cancel bucket counts request
      if (bucketCountsAbortControllerRef.current) {
        bucketCountsAbortControllerRef.current.abort();
        bucketCountsAbortControllerRef.current = null;
      }

      // Restore visible card keys for the new workspace (or clear if none saved)
      setWorkspaceVisibleCards(prev => {
        const savedKeys = prev[currentWorkspaceId];
        if (savedKeys && savedKeys.length > 0) {
          setVisibleCardKeys(new Set(savedKeys));
        } else {
          setVisibleCardKeys(new Set());
        }
        return prev;
      });
    }

    // Update the ref for next comparison
    prevWorkspaceIdRef.current = currentWorkspaceId;

    // Reset initial load state to prevent background refresh until fresh data is loaded
    setInitialLoadComplete(false);

    // Use cardConfigsRef to get the latest configs (handles race condition when card is duplicated just before workspace switch)
    const latestCardConfigs = cardConfigsRef.current;

    // Get all card keys for the current workspace
    const workspaceCardKeys = latestCardConfigs
      .filter(card => card.workspaceId === currentWorkspaceId)
      .map(card => card.key);

    // Get card keys that will actually be loaded (non-collapsed)
    const cardsToLoadKeys = latestCardConfigs
      .filter(card => card.workspaceId === currentWorkspaceId && !collapsedRef.current[card.key])
      .map(card => card.key);

    // MEMORY OPTIMIZATION: Clear data from cards not in the current workspace
    // This prevents holding onto massive datasets from other workspaces
    setCardData(prev => {
      const newData: { [key: string]: any[] } = {};
      workspaceCardKeys.forEach(key => {
        if (prev[key]) {
          newData[key] = prev[key];
        }
      });
      return newData;
    });

    // The Main (default) workspace loads data only for the bucket the user selects.
    // Skip pre-marking cards as loading there; fetchAllCardData sets/clears loading
    // for the selected card itself, so doing it here would leave stuck spinners on
    // the cards that are never fetched while no bucket is selected.
    const isDefaultWorkspace = currentWorkspaceId === 'default';

    // Only set loading state for non-collapsed cards that will be fetched
    if (!isDefaultWorkspace) {
      setCardDataLoading(prev => {
        const updated = { ...prev };
        cardsToLoadKeys.forEach(key => {
          updated[key] = true;
        });
        return updated;
      });
    }

    // Fetch fresh data for all cards in the current workspace (skip collapsed cards).
    // On the Main workspace this is filtered down by fetchAllCardData to just the
    // selected bucket (nothing when no bucket is selected).
    const cardsToLoad = latestCardConfigs.filter((card: CardConfig) =>
      card.workspaceId === currentWorkspaceId &&
      !collapsedRef.current[card.key] &&
      (card.bucket || (card.filterConditions && card.filterConditions.length > 0))
    );
    fetchAllCardData(cardsToLoad, false); // single batch request
    // Set initial load complete after a brief delay for the batch request to complete
    if (cardsToLoad.length === 0) {
      setInitialLoadComplete(true);
    } else {
      // Give batch request time to complete (single request is much faster than staggered)
      setTimeout(() => setInitialLoadComplete(true), 500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentWorkspaceId, configsLoaded, filtersReady]);

  // Cleanup: Cancel all pending requests on unmount
  useEffect(() => {
    return () => {
      // Cancel card data requests
      Object.keys(abortControllersRef.current).forEach(cardKey => {
        abortControllersRef.current[cardKey]?.abort();
      });

      // Cancel bucket counts request
      if (bucketCountsAbortControllerRef.current) {
        bucketCountsAbortControllerRef.current.abort();
      }
    };
  }, []);

  // Update workspace columnCount when layout mode changes (user manually changes columns)
  useEffect(() => {
    if (!configsLoaded) return;

    // Skip if we're restoring columnCount from a workspace switch
    // This prevents overwriting the workspace's stored columnCount with the previous workspace's value
    if (isRestoringColumnCountRef.current) return;

    setWorkspaces(prev => {
      const updated = prev.map(ws =>
        ws.id === currentWorkspaceId
          ? { ...ws, columnCount }
          : ws
      );
      return updated;
    });

    // Wrap cards that are now out of bounds to available columns
    setCardConfigs(prev => prev.map(card => {
      if (card.workspaceId === currentWorkspaceId && card.column >= columnCount) {
        return { ...card, column: card.column % columnCount };
      }
      return card;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnCount, configsLoaded, currentWorkspaceId]);

  // Drag state
  const [draggedCard, setDraggedCard] = useState<string | null>(null);
  const [dragOverCard, setDragOverCard] = useState<string | null>(null);
  const [dropPosition, setDropPosition] = useState<'above' | 'below'>('below');
  const [dragOverColumn, setDragOverColumn] = useState<number | null>(null);

  // Debounced save function - only called explicitly when cards are moved/resized/added/deleted
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Track workspace IDs that have been explicitly deleted by the user
  const deletedWorkspaceIdsRef = useRef<string[]>([]);

  // Use ref to always have access to latest state in callbacks
  const saveStateRef = useRef({
    columnCount, workspaces, collapsed, currentWorkspaceId, cardConfigs,
    visibleCardKeys: Array.from(visibleCardKeys),
    workspaceVisibleCards, panelLayouts,
    selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses
  });
  useEffect(() => {
    saveStateRef.current = {
      columnCount, workspaces, collapsed, currentWorkspaceId, cardConfigs,
      visibleCardKeys: Array.from(visibleCardKeys),
      workspaceVisibleCards, panelLayouts,
      selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses
    };
  }, [columnCount, workspaces, collapsed, currentWorkspaceId, cardConfigs, visibleCardKeys, workspaceVisibleCards, panelLayouts, selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses]);

  const saveLayout = useCallback((updatedCardConfigs: CardConfig[], immediate = false, onComplete?: () => void) => {
    // Clear any pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    const doSave = async () => {
      // Refuse to persist when the layout load failed: cardConfigs holds
      // getDefaultCardConfigs(), not the user's saved arrangement, so writing now would
      // overwrite their real layout with defaults. Checked here (not just at schedule
      // time) so a failure landing mid-debounce still blocks the write.
      if (layoutLoadFailedRef.current) {
        console.warn('Skipping dashboard layout save — the layout failed to load, so state holds defaults.');
        if (onComplete) onComplete();
        return;
      }
      try {
        const {
          columnCount: cols, workspaces: ws, collapsed: coll, currentWorkspaceId: wsId,
          visibleCardKeys: visibleKeys, workspaceVisibleCards: wsVisibleCards,
          selectedLCs: lcs, selectedCustomers: customers, selectedBilltos: billtos,
          selectedKeyAccounts: keyAccounts, selectedDispatchRegions: dispatchRegions,
          selectedWarehouses: warehouses
        } = saveStateRef.current;
        const deletedIds = deletedWorkspaceIdsRef.current;
        deletedWorkspaceIdsRef.current = [];
        await userSettingsService.saveDashboardLayoutNew('lc', {
          cardConfigs: updatedCardConfigs,
          columnCount: cols,
          workspaces: ws,
          collapsed: coll,
          currentWorkspaceId: wsId,
          visibleCardKeys: visibleKeys,
          workspaceVisibleCards: wsVisibleCards,
          selectedLCs: lcs,
          selectedCustomers: customers,
          selectedBilltos: billtos,
          selectedKeyAccounts: keyAccounts,
          selectedDispatchRegions: dispatchRegions,
          selectedWarehouses: warehouses,
          deletedWorkspaceIds: deletedIds.length > 0 ? deletedIds : undefined,
          version: CURRENT_VERSION
        });
      } catch (error) {
        console.error('Failed to save dashboard layout to database:', error);
      } finally {
        if (onComplete) onComplete();
      }
    };

    if (immediate) {
      // Save immediately (for edit mode exit)
      doSave();
    } else {
      // Debounce save by 1 second
      saveTimeoutRef.current = setTimeout(doSave, 1000);
    }
  }, []); // No deps - uses refs for latest state

  // Expose saveLayout to the column handlers defined above it (see saveLayoutRef).
  useEffect(() => {
    saveLayoutRef.current = saveLayout;
  }, [saveLayout]);

  const handleToggleCardLock = useCallback((cardKey: string, locked: boolean) => {
    if (cardKey.startsWith('managed:')) return;
    setCardConfigs(prev => {
      const updated = prev.map(card =>
        card.key === cardKey ? { ...card, locked } : card
      );
      // Save layout with the updated configs
      saveLayout(updated, true);
      return updated;
    });

    // Update GridStack widget noMove/noResize
    const grid = gridInstanceRef.current;
    if (grid && gridRef.current) {
      const el = gridRef.current.querySelector(`[gs-id="${cardKey}"]`);
      if (el) {
        grid.update(el as HTMLElement, { noMove: locked, noResize: locked });
      }
    }
  }, [saveLayout]);

  // ---------------------------------------------------------------------------
  // Count-panel GridStack (second instance, counts view)
  // ---------------------------------------------------------------------------
  // The counts area is rendered only when the panels are visible (drawer view, or
  // the Main board with no bucket selected). Initialize a dedicated GridStack over
  // it then, and tear it down when it unmounts or the workspace changes. Dragging
  // uses the panel header as the handle so clicking a count still drills down.
  const panelsVisible = drawerView || isMainBoardUnselected;

  // Snap one panel tile to fit its content, and the grow-only variant for count panels.
  // Both measure in components/panelGrid/panelFit, shared with the Accounting Dashboards —
  // this is where they were written, but the shared hook needed them too.
  const fitPanelElement = useCallback((itemEl: HTMLElement, heightOnly = false) => {
    fitPanelElementShared(panelGridInstanceRef.current, panelGridRef.current, itemEl, heightOnly);
  }, []);

  const growPanelToFitContent = useCallback((itemEl: HTMLElement) => {
    growPanelToFitContentShared(panelGridInstanceRef.current, itemEl);
  }, []);

  // First-load default: panels with no saved size shouldn't show their (arbitrary)
  // default footprint — once their content has rendered, snap them to fit and let
  // the 'change' handler persist it, so the saved default is the snapped size.
  const autoFittedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!panelGridInstanceRef.current || !panelsVisible || !panelsLoaded) return;
    const savedForWs = panelLayoutsRef.current[currentWorkspaceId] || [];
    panelsForWorkspace(currentWorkspaceId).forEach((panel) => {
      if (autoFittedRef.current.has(panel.key)) return; // already auto-fitted this session
      // Melrose & Warehouse Photos own their own height-only auto-fit (below) and keep
      // the default width, so they're excluded from the full (width+height) first-load snap.
      if (panel.type === 'melrose' || panel.type === 'whsephotos' || panel.type === 'edi-buckets') return;
      // The Exports Board is a large scrollable table with its own overflow; fitting it
      // to content would size the tile to every row. Keep its default footprint.
      if (panel.type === 'exports-board') return;
      // The Invoicing Statistics grid is a fixed-size table behind a permission wrapper;
      // auto-fit measurement is unreliable through the wrapper, so keep its default.
      if (panel.type === 'invoicing-stats') return;
      // Auto-fit when the panel has never been sized — no saved entry, or a saved
      // entry that still matches the arbitrary default footprint (i.e. it was only
      // ever persisted incidentally, never deliberately resized). A panel the user
      // sized themselves is left alone.
      const saved = savedForWs.find((i) => i.key === panel.key);
      // A locked panel is never auto-fitted, even if its footprint still looks default —
      // the user has pinned it deliberately (same rule as growPanelToFitContent).
      if (saved?.locked) return;
      const def = PANEL_GRID_DEFAULTS[panel.key] || { w: PANEL_DEFAULT_W, h: PANEL_DEFAULT_H };
      const neverSized = !saved || (!saved.collapsed && saved.w === def.w && saved.h === def.h);
      if (!neverSized) return;
      // Wait until the panel's content is populated so we measure the real size.
      if (panel.type === 'schedule' && !scheduleData) return;
      if (panel.type === 'tracking' && !trackingData) return;
      if (panel.type === 'exports-summary' && !exportsSummaryData) return;
      if (panel.type === 'chassis' && !chassisData) return;
      if (panel.type === 'counts' && Object.keys(bucketCounts).length === 0) return;
      const el = panelGridRef.current?.querySelector(`[gs-id="${panel.key}"]`) as HTMLElement | null;
      if (!el) return;
      autoFittedRef.current.add(panel.key);
      fitPanelElement(el);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scheduleData, trackingData, chassisData, melroseData, bucketCounts, panelsVisible, panelsLoaded, currentWorkspaceId, fitPanelElement]);

  // The New Melrose Pros panel's row count swings with every filter (an LC with 1
  // customer vs. an all-up view of 50), so a sticky height makes no sense — refit its
  // HEIGHT to content on every data change, keeping the user's width and placement.
  // A collapsed tile is a no-op inside fitPanelElement (no body to measure), and
  // expanding re-renders the body, which re-runs this and resizes it then.
  useEffect(() => {
    if (!panelGridInstanceRef.current || !panelsVisible || !panelsLoaded) return;
    if (melroseData === null) return;
    const el = panelGridRef.current?.querySelector(`[gs-id="${MELROSE_PANEL_KEY}"]`) as HTMLElement | null;
    if (el) fitPanelElement(el, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [melroseData, panelsVisible, panelsLoaded, fitPanelElement]);

  // Same height-only auto-fit for the Warehouse Photos panel (variable warehouse rows).
  useEffect(() => {
    if (!panelGridInstanceRef.current || !panelsVisible || !panelsLoaded) return;
    if (whsePhotoData === null) return;
    const el = panelGridRef.current?.querySelector(`[gs-id="${WHSEPHOTO_PANEL_KEY}"]`) as HTMLElement | null;
    if (el) fitPanelElement(el, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whsePhotoData, panelsVisible, panelsLoaded, fitPanelElement]);

  // Same height-only auto-fit for the Pending EDI Orders panel (its bucket count swings
  // with the LC/Bill To filters, like Melrose), keyed on the FILTERED set that's shown.
  useEffect(() => {
    if (!panelGridInstanceRef.current || !panelsVisible || !panelsLoaded) return;
    const el = panelGridRef.current?.querySelector(`[gs-id="${EDI_PANEL_KEY}"]`) as HTMLElement | null;
    if (el) fitPanelElement(el, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredEdiBuckets, panelsVisible, panelsLoaded, fitPanelElement]);

  // Keep every count-panel tile tall enough for its content as its WIDTH changes. A
  // CountPanel reflows into fewer, taller columns as its tile narrows (the window
  // shrinking narrows all tiles proportionally, since GridStack sizes them in % of the
  // grid), so a tile that fit at 3 columns clips badly once it drops to 1. A
  // ResizeObserver on each tile re-measures and grows it on any size change.
  //
  // Only count panels are watched — the other panel types are fixed grids/large tables
  // that own their own scrolling (see COUNT_PANEL_TYPES). Growth is one-way, so this
  // never fights a height the user dragged.
  useEffect(() => {
    if (!panelGridReady || !panelsVisible) return;
    const gridEl = panelGridRef.current;
    if (!gridEl) return;

    const countTiles = panelsForWorkspace(currentWorkspaceId)
      .filter((p) => COUNT_PANEL_TYPES.has(p.type))
      .map((p) => gridEl.querySelector(`[gs-id="${p.key}"]`) as HTMLElement | null)
      .filter((el): el is HTMLElement => !!el);
    if (countTiles.length === 0) return;

    // Coalesce the burst of callbacks a drag/window-resize produces into one pass per
    // frame, and never measure mid-resize — GridStack is still writing the tile's size,
    // so we'd grow against a transient width. The 'resizestop' handler covers that case.
    let frame = 0;
    const pending = new Set<HTMLElement>();
    const flush = () => {
      frame = 0;
      const tiles = Array.from(pending);
      pending.clear();
      tiles.forEach((el) => {
        if (el.classList.contains('ui-resizable-resizing')) return;
        growPanelToFitContent(el);
      });
    };
    const observer = new ResizeObserver((entries) => {
      entries.forEach((entry) => pending.add(entry.target as HTMLElement));
      if (!frame) frame = requestAnimationFrame(flush);
    });
    countTiles.forEach((el) => observer.observe(el));

    return () => {
      observer.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
    // panelCollapseSig re-runs this so an expanding tile is re-measured once its body mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [panelGridReady, panelsVisible, currentWorkspaceId, panelKeysSig, panelCollapseSig, growPanelToFitContent]);

  // Push the saved layout into the live GridStack instance.
  //
  // GridStack reads a tile's gs-* attributes only when it ADOPTS the tile, and never
  // again. So a layout that arrives after init — a slow lc_dashboard_panels GET, or the
  // 15s retry after a failed one — never reaches the grid on its own: React re-renders
  // the new gs-* values, GridStack ignores them, and the default footprints stay on
  // screen. The next 'change' event (a drag, a resizestop, or just growPanelToFitContent
  // firing as bucket data lands) then reads those defaults back off the grid and the
  // auto-save persists them over the user's real arrangement. That is #33552.
  //
  // Idempotent by design: only tiles whose geometry actually differs are touched, so once
  // the grid and the saved layout agree this is a no-op and it is safe to run on every
  // panelLayouts change (including the ones the 'change' handler itself produces).
  const applyPanelGeometry = useCallback(() => {
    const grid = panelGridInstanceRef.current;
    const gridEl = panelGridRef.current;
    if (!grid || !gridEl) return;

    // Suppress the 'change' events our own grid.update calls emit, so reconciling can't
    // itself trigger a save of half-applied geometry. Restore rather than clear: during
    // init the flag is still owned by the init effect's 200ms timer.
    const wasInitializing = isPanelGridInitializing.current;
    isPanelGridInitializing.current = true;
    grid.batchUpdate();
    try {
      currentPanelItems.forEach((item) => {
        const el = gridEl.querySelector(`[gs-id="${item.key}"]`) as HTMLElement | null;
        if (!el) return; // not rendered (e.g. a permission-gated panel) — nothing to place
        const node = (el as GridItemHTMLElement).gridstackNode;
        const target = panelGeometryTarget(item);
        if (!panelGeometryNeedsUpdate(node, target)) return;
        grid.update(el, target);
      });
    } finally {
      grid.batchUpdate(false); // commits + dispatches 'change' while still suppressed
      isPanelGridInitializing.current = wasInitializing;
    }
    // Only now may the 'change' handler persist what it reads off this grid.
    panelGeometryAppliedRef.current = currentWorkspaceId;
  }, [currentPanelItems, currentWorkspaceId]);

  // Reconcile the live grid with the saved layout whenever either side changes.
  // applyPanelGeometry closes over currentPanelItems, so this re-runs when a late
  // lc_dashboard_panels load lands and pushes the real geometry into the grid.
  useEffect(() => {
    if (!panelGridReady || !panelsVisible) return;
    applyPanelGeometry();
  }, [panelGridReady, panelsVisible, applyPanelGeometry]);

  useEffect(() => {
    if (!isFullyLoaded || !panelsVisible) return;
    // Wait for the saved panel layout before building the grid: isFullyLoaded only
    // covers the CARD layout + field definitions, so without this the grid adopts every
    // tile at getDefaultPanelLayouts() and applyPanelGeometry has to undo it. On a hard
    // load failure build from defaults anyway so the board still renders — the
    // panelsLoaded gate on the auto-save keeps that from being written back.
    if (!panelsLoaded && !panelsLoadFailed) return;
    // Geometry from the previous grid no longer describes this one.
    panelGeometryAppliedRef.current = null;

    if (panelGridInstanceRef.current) {
      panelGridInstanceRef.current.destroy(false);
      panelGridInstanceRef.current = null;
    }
    isPanelGridInitializing.current = true;

    const timer = setTimeout(() => {
      if (!panelGridRef.current) return;

      const grid = GridStack.init({
        column: PANEL_COLUMNS,
        cellHeight: PANEL_CELL_HEIGHT,
        margin: PANEL_MARGIN,
        float: false,
        animate: true,
        handle: `.${PANEL_DRAG_HANDLE_CLASS}`,
        resizable: { handles: 'se' },
      }, panelGridRef.current);

      panelGridInstanceRef.current = grid;

      grid.on('change', () => {
        if (isPanelGridInitializing.current) return;
        // Refuse to persist geometry read off a grid that has not been reconciled with
        // this workspace's saved layout. Without this, any stray change — a content-driven
        // growPanelToFitContent, a compaction pass — writes the on-screen defaults over
        // the saved arrangement (#33552).
        if (panelGeometryAppliedRef.current !== currentWorkspaceId) return;

        const prevList = panelLayoutsRef.current[currentWorkspaceId] || currentPanelItems;
        const prevByKey = new Map(prevList.map((i) => [i.key, i]));

        const items: PanelLayoutItem[] = [];
        grid.getGridItems().forEach((el) => {
          const node = el.gridstackNode;
          const id = node?.id || el.getAttribute('gs-id') || '';
          if (!id) return;
          const x = node?.x ?? 0;
          const y = node?.y ?? 0;
          const w = node?.w ?? PANEL_DEFAULT_W;
          const h = node?.h ?? PANEL_DEFAULT_H;
          if (w < 1 || h < 1) return;

          const prev = prevByKey.get(id);
          const item: PanelLayoutItem = { key: id, x, y, w, h };
          if (prev?.locked) item.locked = true; // lock is toggled elsewhere; preserve it
          if (prev?.collapsed) {
            // Collapsed: keep the header-only height, preserve the restore height.
            item.collapsed = true;
            item.h = 1;
            item.expandedH = prev.expandedH ?? (h > 1 ? h : PANEL_DEFAULT_H);
          } else {
            // Expanded: track the live height so a resize is remembered.
            item.expandedH = h;
          }
          items.push(item);
        });

        panelLayoutsRef.current = { ...panelLayoutsRef.current, [currentWorkspaceId]: items };
        setPanelLayouts((p) => ({ ...p, [currentWorkspaceId]: items }));
        // Persistence is handled by the panelLayouts auto-save effect.
      });

      // Apply the saved geometry (position, size, collapsed height, lock) to the tiles
      // GridStack just adopted, and mark this workspace reconciled so the 'change'
      // handler above is allowed to persist. Replaces an inline collapsed/locked pass
      // that read the init-time snapshot of currentPanelItems — which was the
      // pre-load default whenever the panels GET hadn't landed yet.
      // Deliberately not in this effect's deps: applyPanelGeometry changes identity on
      // every panelLayouts change and re-initialising the whole grid that often would
      // thrash. The dedicated effect above handles later changes.
      applyPanelGeometry();

      // Dragging a count panel narrower reflows its blocks into fewer, taller columns.
      // The grow-to-fit ResizeObserver deliberately skips mid-drag widths, so top the
      // tile up once the resize settles.
      grid.on('resizestop', (_e, el) => {
        const itemEl = el as HTMLElement;
        const key = itemEl?.getAttribute('gs-id') || '';
        const meta = LC_PANELS.find((p) => p.key === key);
        if (!meta || !COUNT_PANEL_TYPES.has(meta.type)) return;
        requestAnimationFrame(() => growPanelToFitContent(itemEl));
      });

      // Double-clicking the resize handle (se corner) snaps a tile to fit its content
      // in both dimensions (shared with the first-load auto-fit).
      panelGridCleanupRef.current = attachFitOnDoubleClick(
        panelGridRef.current, () => panelGridInstanceRef.current);

      setPanelGridReady(true);

      setTimeout(() => {
        isPanelGridInitializing.current = false;
      }, 200);
    }, 100);

    return () => {
      clearTimeout(timer);
      setPanelGridReady(false);
      // The grid this marker referred to is going away; a stale "reconciled" flag
      // would authorise the next grid's change events before it has been placed.
      panelGeometryAppliedRef.current = null;
      if (panelGridCleanupRef.current) {
        panelGridCleanupRef.current();
        panelGridCleanupRef.current = null;
      }
      if (panelGridInstanceRef.current) {
        panelGridInstanceRef.current.destroy(false);
        panelGridInstanceRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullyLoaded, panelsVisible, currentWorkspaceId, panelKeysSig, panelsLoaded, panelsLoadFailed]);

  // Collapse/expand a single count panel: resize its GridStack tile to a header
  // strip (h=1) or back to its remembered height, and persist the change. Updating
  // panelLayoutsRef synchronously keeps the grid 'change' handler (fired by
  // grid.update) from clobbering the new collapsed state.
  const togglePanelCollapse = useCallback((panelKey: string) => {
    const base = currentPanelItems.map((i) => ({ ...i }));
    const target = base.find((i) => i.key === panelKey);
    if (!target) return;

    const grid = panelGridInstanceRef.current;
    const el = panelGridRef.current?.querySelector(`[gs-id="${panelKey}"]`) as HTMLElement | null;

    if (!target.collapsed) {
      target.expandedH = target.h >= 2 ? target.h : (target.expandedH ?? PANEL_DEFAULT_H);
      target.collapsed = true;
      target.h = 1;
      if (grid && el) grid.update(el, { h: 1 });
    } else {
      const restore = target.expandedH ?? PANEL_DEFAULT_H;
      target.collapsed = false;
      target.h = restore;
      target.expandedH = undefined;
      if (grid && el) grid.update(el, { h: restore });
    }

    panelLayoutsRef.current = { ...panelLayoutsRef.current, [currentWorkspaceId]: base };
    setPanelLayouts((p) => ({ ...p, [currentWorkspaceId]: base }));
    // Persistence is handled by the panelLayouts auto-save effect.
  }, [currentPanelItems, currentWorkspaceId]);

  // Collapse or expand EVERY count panel at once (the toolbar's Collapse/Expand All).
  // Same per-panel mechanics as togglePanelCollapse, applied in one batch with one save;
  // panels already in the target state are left untouched (preserving their expandedH).
  const setAllPanelsCollapsed = useCallback((collapse: boolean) => {
    const grid = panelGridInstanceRef.current;
    const base = currentPanelItems.map((i) => {
      const item = { ...i };
      const el = panelGridRef.current?.querySelector(`[gs-id="${item.key}"]`) as HTMLElement | null;
      if (collapse && !item.collapsed) {
        item.expandedH = item.h >= 2 ? item.h : (item.expandedH ?? PANEL_DEFAULT_H);
        item.collapsed = true;
        item.h = 1;
        if (grid && el) grid.update(el, { h: 1 });
      } else if (!collapse && item.collapsed) {
        const restore = item.expandedH ?? PANEL_DEFAULT_H;
        item.collapsed = false;
        item.h = restore;
        item.expandedH = undefined;
        if (grid && el) grid.update(el, { h: restore });
      }
      return item;
    });
    panelLayoutsRef.current = { ...panelLayoutsRef.current, [currentWorkspaceId]: base };
    setPanelLayouts((p) => ({ ...p, [currentWorkspaceId]: base }));
    // Persistence is handled by the panelLayouts auto-save effect.
  }, [currentPanelItems, currentWorkspaceId]);

  // Lock/unlock a single count panel: pin its GridStack tile and persist. (Collapse
  // still works while locked — it's a deliberate header action, not a drag/resize.)
  const togglePanelLock = useCallback((panelKey: string) => {
    const base = currentPanelItems.map((i) => ({ ...i }));
    const target = base.find((i) => i.key === panelKey);
    if (!target) return;

    const locked = !target.locked;
    target.locked = locked;

    const grid = panelGridInstanceRef.current;
    const el = panelGridRef.current?.querySelector(`[gs-id="${panelKey}"]`) as HTMLElement | null;
    // `locked` pins the tile against the packing algorithm; noMove/noResize only take
    // away the user's handles. Setting just the latter two meant a locked panel still
    // got shoved around by a growing neighbour under float:false — see applyPanelGeometry.
    if (grid && el) grid.update(el, { locked, noMove: locked, noResize: locked });

    panelLayoutsRef.current = { ...panelLayoutsRef.current, [currentWorkspaceId]: base };
    setPanelLayouts((p) => ({ ...p, [currentWorkspaceId]: base }));
    // Persistence is handled by the panelLayouts auto-save effect.
  }, [currentPanelItems, currentWorkspaceId]);

  // Save layout when filters or selected card changes (debounced)
  const filterSaveInitialized = useRef(false);
  useEffect(() => {
    if (!configsLoaded) return;

    // Skip the initial render to avoid saving on page load
    if (!filterSaveInitialized.current) {
      filterSaveInitialized.current = true;
      return;
    }

    // Debounced save when filters or visible card selection changes
    const cardsToSave = saveStateRef.current.cardConfigs;
    saveLayout(cardsToSave, false); // debounced save
  }, [visibleCardKeys, workspaceVisibleCards, selectedLCs, selectedCustomers, selectedBilltos, selectedKeyAccounts, selectedDispatchRegions, selectedWarehouses, configsLoaded, saveLayout]);

  // Keyboard shortcuts for workspace switching (Alt/Option + 1-9)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check if Alt/Option key is pressed along with a number 1-9
      // Use e.code to handle Mac's Alt key which produces special characters
      if (e.altKey) {
        const codeMatch = e.code.match(/^Digit([1-9])$/);
        if (codeMatch) {
          const workspaceIndex = parseInt(codeMatch[1]) - 1;
          const sortedWorkspaces = [...workspaces].sort((a, b) => a.order - b.order);
          if (workspaceIndex < sortedWorkspaces.length) {
            e.preventDefault();
            setCurrentWorkspaceId(sortedWorkspaces[workspaceIndex].id);
          }
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [workspaces]);

  // Sync columnCount changes to current workspace
  useEffect(() => {
    if (!configsLoaded) return;

    setWorkspaces(prev => prev.map(ws =>
      ws.id === currentWorkspaceId
        ? { ...ws, columnCount }
        : ws
    ));
  }, [columnCount, currentWorkspaceId, configsLoaded]);

  // Workspace management functions (no cap on the number of workspaces)
  const handleCreateWorkspace = () => {
    setNewWorkspaceName('');
    setShowNewWorkspaceModal(true);
  };

  const handleConfirmCreateWorkspace = () => {
    const name = newWorkspaceName.trim() || `Workspace ${workspaces.length + 1}`;
    const newId = `workspace-${Date.now()}`;
    const newOrder = workspaces.length;
    setWorkspaces([...workspaces, { id: newId, name, order: newOrder, columnCount: 2 }]);
    setCurrentWorkspaceId(newId);
    setShowNewWorkspaceModal(false);
    setNewWorkspaceName('');
  };

  // Reset layout to default positions for current workspace
  const handleResetLayout = () => {
    if (currentWorkspaceId.startsWith('managed:')) { setShowResetLayoutConfirm(false); return; }
    // Destroy current GridStack instance to force re-initialization
    if (gridInstanceRef.current) {
      gridInstanceRef.current.destroy(false);
      gridInstanceRef.current = null;
    }

    // Clear gsX/gsY/gsW/gsH values for cards in current workspace
    // This will cause currentGridItems to recompute positions from column/order/span defaults
    setCardConfigs(prev => prev.map(card => {
      if (card.workspaceId !== currentWorkspaceId) return card;

      // Remove GridStack position overrides
      const { gsX, gsY, gsW, gsH, ...rest } = card;
      return rest;
    }));

    // Also drop the saved panel layout for this workspace so the movable panels
    // (schedule/tracking/exports/invoicing-stats…) fall back to their default
    // footprints — but keep it in the trash so the reset can be undone. The live panel
    // grid is re-laid-out by applyPanelGeometry, which reacts to this change; before
    // that existed the tiles stayed put on screen and the next 'change' event
    // re-persisted them, silently undoing the reset (#33552).
    const reset = trashPanelLayout(
      panelLayoutsRef.current,
      panelTrashRef.current,
      currentWorkspaceId,
      'reset',
      new Date().toISOString(),
    );
    applyPanelTrashResult(reset);

    setShowResetLayoutConfirm(false);
    showToast({
      type: 'success',
      message: reset.trashed
        ? 'Layout reset to default — use Restore Panel Layout to undo'
        : 'Layout reset to default',
    });
  };

  // The most recent reset or copy this user can undo, and the label for it. A reset
  // displaces one workspace; copying a colleague's configuration displaces the whole
  // board, and both have to undo in a single step.
  const restorableBatch = useMemo(() => latestRestorableBatch(panelTrash), [panelTrash]);
  const restorePanelLabel = !restorableBatch
    ? ''
    : restorableBatch.reason === 'copied-over'
      ? `Restore My Panel Layout${restorableBatch.workspaceIds.length > 1 ? 's' : ''}`
      : 'Restore Panel Layout';

  const handleRestorePanelLayout = () => {
    if (!restorableBatch) {
      showToast({ type: 'error', message: 'No previous panel layout to restore' });
      return;
    }
    const restored = restorePanelBatch(
      panelLayoutsRef.current,
      panelTrashRef.current,
      restorableBatch.batchId,
    );
    // applyPanelGeometry pushes the restored positions into the live grid.
    applyPanelTrashResult(restored);
    setShowGlobalControls(false);
    showToast({
      type: 'success',
      message: restored.restored.length > 1
        ? `Panel layouts restored across ${restored.restored.length} workspaces`
        : 'Panel layout restored',
    });
  };

  const handleCopyPanelLayout = (source: LcPanelLayoutSource) => {
    const now = new Date().toISOString();
    const copied = applyCopiedPanelLayouts(
      panelLayoutsRef.current,
      panelTrashRef.current,
      source.panel_layouts as { [workspaceId: string]: PanelLayoutItem[] },
      now,
      `copy:${source.user_email}:${now}`,
    );
    // applyPanelGeometry pushes the borrowed positions into the live grid.
    applyPanelTrashResult(copied);
    setShowCopyPanelsModal(false);
    setShowGlobalControls(false);
    showToast({
      type: 'success',
      message: copied.replaced.length
        ? `Copied ${source.display_name}'s panel layout — use Restore My Panel Layouts to undo`
        : `Copied ${source.display_name}'s panel layout`,
    });
  };

  const PROTECTED_WORKSPACE_IDS = ['default', 'exports', 'accounting'];

  const handleDeleteWorkspace = (workspaceId: string) => {
    // Can't delete protected workspaces
    if (PROTECTED_WORKSPACE_IDS.includes(workspaceId) || workspaceId.startsWith('managed:')) return;

    // Track for explicit backend deletion
    deletedWorkspaceIdsRef.current = [...deletedWorkspaceIdsRef.current, workspaceId];

    // Remove workspace
    setWorkspaces(workspaces.filter(w => w.id !== workspaceId));

    // Remove all cards from this workspace
    const deletedCardKeys = cardConfigs.filter(c => c.workspaceId === workspaceId).map(c => c.key);
    setCardConfigs(cardConfigs.filter(c => c.workspaceId !== workspaceId));

    // MEMORY OPTIMIZATION: Clear data for all deleted cards
    setCardData(prev => {
      const newData = { ...prev };
      deletedCardKeys.forEach(key => delete newData[key]);
      return newData;
    });

    // Move the workspace's panel layout to the trash. The cards are soft-deleted
    // server-side and restorable from the trashcan, so the panel positions have to go
    // somewhere restorable too — they used to just linger in panelLayouts forever,
    // orphaned once the 30-day purge removed the cards.
    applyPanelTrashResult(trashPanelLayout(
      panelLayoutsRef.current,
      panelTrashRef.current,
      workspaceId,
      'workspace-deleted',
      new Date().toISOString(),
    ));

    // Switch to default if deleting current workspace
    if (currentWorkspaceId === workspaceId) {
      setCurrentWorkspaceId('default');
    }
  };

  const loadTrashItems = async () => {
    setTrashLoading(true);
    try {
      const items = await userSettingsService.getDashboardTrash('lc');
      setTrashItems(items);
    } catch (error) {
      console.error('Failed to load trash items:', error);
    } finally {
      setTrashLoading(false);
    }
  };

  const handleOpenTrash = () => {
    setShowTrashModal(true);
    loadTrashItems();
  };

  const handleRestoreWorkspace = async (workspaceId: string) => {
    try {
      await userSettingsService.restoreFromTrash('lc', workspaceId);
      // Bring the workspace's panel positions back with its cards, and write them
      // immediately: this path reloads the page, which would discard the auto-save's
      // 500ms debounce and leave the restored workspace on auto-flowed defaults.
      const restored = restorePanelLayout(
        panelLayoutsRef.current,
        panelTrashRef.current,
        workspaceId,
      );
      if (restored.restored) {
        applyPanelTrashResult(restored);
        try {
          await savePanelSetting(restored.layouts, restored.trash);
        } catch (err) {
          // Don't fail the restore over this — the cards are already back. The trash
          // entry survives untouched server-side, so after the reload the workspace
          // still offers Restore Panel Layout and the user can retry. (No toast: the
          // reload below would wipe it before it could be read.)
          console.error('Failed to restore panel layout with the workspace — retry via Restore Panel Layout:', err);
        }
      }
      showToast({ type: 'success', message: 'Workspace restored. Refreshing...' });
      setShowTrashModal(false);
      // Reload the page to pick up restored data
      window.location.reload();
    } catch (error) {
      console.error('Failed to restore workspace:', error);
      showToast({ type: 'error', message: 'Failed to restore workspace' });
    }
  };

  const handleRenameWorkspace = (workspaceId: string, newName: string) => {
    if (workspaceId.startsWith('managed:')) return;
    setWorkspaces(workspaces.map(w =>
      w.id === workspaceId ? { ...w, name: newName } : w
    ));
  };

  const handleReorderWorkspaces = (reorderedTabs: Tab[]) => {
    // Update workspace order based on the new tab positions
    setWorkspaces(prevWorkspaces => {
      return reorderedTabs.map((tab, index) => {
        const workspace = prevWorkspaces.find(w => w.id === tab.id);
        if (workspace) {
          return { ...workspace, order: index };
        }
        // Fallback - shouldn't happen
        return { id: tab.id, name: tab.label, order: index, columnCount: 2 };
      });
    });
  };

  // Get card count per workspace
  const getWorkspaceCardCount = (workspaceId: string) => {
    return countWorkspaceCards(cardConfigs, workspaceId);
  };

  // Numbered workspace icon component
  const WorkspaceIcon = ({ number }: { number: number }) => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor">
      <path d="M19,24H5c-2.757,0-5-2.243-5-5V5C0,2.243,2.243,0,5,0h14c2.757,0,5,2.243,5,5v14c0,2.757-2.243,5-5,5ZM5,2c-1.654,0-3,1.346-3,3v14c0,1.654,1.346,3,3,3h14c1.654,0,3-1.346,3-3V5c0-1.654-1.346-3-3-3H5Z" />
      <text x="12" y="17" textAnchor="middle" fontSize="12" fontWeight="600" fill="currentColor">{number}</text>
    </svg>
  );

  // Create workspace tabs for TabBar
  const workspaceTabs: Tab[] = useMemo(() => {
    return workspaces.sort((a, b) => a.order - b.order).map((workspace, index) => ({
      id: workspace.id,
      label: workspace.name,
      required: workspace.required,
      current: workspace.id === currentWorkspaceId,
      icon: <WorkspaceIcon number={index + 1} />
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaces, currentWorkspaceId]);

  // Handle duplicating a card with modal
  const handleDuplicateCard = (newName: string, workspaceId: string, createNewWorkspace: boolean) => {
    if (!duplicateCardConfig || workspaceId.startsWith('managed:')) return;

    try {
      let targetWorkspaceId = workspaceId;

      // Create new workspace if requested
      if (createNewWorkspace) {
        const newId = `workspace-${Date.now()}`;
        const newOrder = workspaces.length;
        const newWorkspaceName = `Workspace ${workspaces.length}`;
        const updatedWorkspaces = [...workspaces, { id: newId, name: newWorkspaceName, order: newOrder, columnCount: 2 }];
        setWorkspaces(updatedWorkspaces);
        targetWorkspaceId = newId;
        setCurrentWorkspaceId(newId); // Switch to the new workspace
        // Update ref immediately so saveLayout sees the new workspace
        saveStateRef.current = { ...saveStateRef.current, workspaces: updatedWorkspaces, currentWorkspaceId: newId };
      } else {
        // Check if target workspace already has the maximum visible cards (max limit)
        const visibleCardsInWorkspace = cardConfigs.filter(
          c => c.workspaceId === workspaceId && isWorkspaceCardShown(c)
        ).length;

        if (visibleCardsInWorkspace >= LC_WORKSPACE_CARD_LIMIT) {
          const workspaceName = workspaces.find(w => w.id === workspaceId)?.name || 'workspace';
          showToast({
            message: `Cannot duplicate to ${workspaceName} - workspace is full (${LC_WORKSPACE_CARD_LIMIT}/${LC_WORKSPACE_CARD_LIMIT} cards). Choose a different workspace or create a new one.`,
            type: 'error',
            duration: 5000
          });
          return;
        }
      }

      // Create a unique key for the duplicated card
      const newKey = `card-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

      // Check if a card with this key already exists (extremely unlikely but just in case)
      if (cardConfigs.some(c => c.key === newKey)) {
        showToast({
          message: `Cannot create new card - card key collision`,
          type: 'error',
          duration: 5000
        });
        return;
      }

      // Check if a card with the same title already exists
      if (cardConfigs.some(c => c.title === newName)) {
        showToast({
          message: `Cannot create new card - card "${newName}" already exists`,
          type: 'error',
          duration: 5000
        });
        return;
      }

      // Find the highest order in the target workspace (column 0 by default)
      const cardsInTargetWorkspace = cardConfigs.filter(
        c => c.workspaceId === targetWorkspaceId && c.column === 0
      );
      const maxOrder = Math.max(...cardsInTargetWorkspace.map(c => c.order), -1);

      // Calculate GridStack position for the new card in target workspace
      const targetWorkspaceCards = cardConfigs.filter(c => c.workspaceId === targetWorkspaceId && isWorkspaceCardShown(c));
      let maxBottom = 0;
      targetWorkspaceCards.forEach(card => {
        // Only use gs* values if they're valid (w and h >= 1)
        if (card.gsY !== undefined && card.gsH !== undefined && card.gsW !== undefined && card.gsW >= 1 && card.gsH >= 1) {
          maxBottom = Math.max(maxBottom, card.gsY + card.gsH);
        } else {
          // Estimate from order if no valid gs* values
          const estimatedY = card.order * 6;
          maxBottom = Math.max(maxBottom, estimatedY + 6);
        }
      });

      // Create new card config with same settings but new key, title, workspace, and fresh grid position
      const newCard: CardConfig = {
        ...duplicateCardConfig,
        hidden: false,
        key: newKey,
        title: newName,
        workspaceId: targetWorkspaceId,
        column: 0, // Place in first column of target workspace
        order: maxOrder + 1,
        locked: true, // New cards start locked (clean view); unlock to arrange the tile
        // Fresh GridStack position (don't copy from original card)
        gsX: 0,
        gsY: maxBottom,
        gsW: 4,
        gsH: 6
      };

      // Add the new card to configs
      const updatedConfigs = [...cardConfigs, newCard];

      // IMPORTANT: Update refs BEFORE triggering state changes
      // The workspace switch effect uses cardConfigsRef.current, so it must be updated first
      cardConfigsRef.current = updatedConfigs;

      // Now trigger state updates (React will batch these)
      setCardConfigs(updatedConfigs);

      // Save the layout immediately so duplicate persists
      saveLayout(updatedConfigs, true);

      // Fetch data for the new card (same pattern as FirstCardModal)
      fetchCardData(
        newKey,
        newCard.filterConditions || [],
        newCard.filterView || 'Orders',
        newCard.bucket,
        false,
        newCard.additionalFields
      );

      // Close modal
      setDuplicateCardConfig(null);

      // Show success toast
      showToast({
        message: `Successfully duplicated "${duplicateCardConfig.title}" to ${createNewWorkspace ? 'new workspace' : workspaces.find(w => w.id === targetWorkspaceId)?.name}`,
        type: 'success',
        duration: 3000
      });
    } catch (error) {
      console.error('Failed to duplicate card:', error);
      showToast({
        message: `Failed to duplicate card "${duplicateCardConfig.title}"`,
        type: 'error',
        duration: 5000
      });
    }
  };

  // Handle creating a new card via modal
  const handleCreateBlankCard = useCallback(() => {
    setIsFirstCardModalOpen(true);
  }, []);

  // Clear custom actions for the toolbar (GridStack doesn't need Add Container)
  useEffect(() => {
    setCustomActions([]);
    return () => {
      setCustomActions([]);
    };
  }, [setCustomActions]);

  // Helper to calculate next available Y position for new cards in a workspace
  const getNextGridPosition = (workspaceId: string): { gsX: number; gsY: number; gsW: number; gsH: number } => {
    const workspaceCards = cardConfigs.filter(c => c.workspaceId === workspaceId && isWorkspaceCardShown(c));

    // Find the maximum Y + H (bottom edge) of all existing cards
    let maxBottom = 0;
    workspaceCards.forEach(card => {
      // Only use gs* values if they're valid (w and h >= 1)
      if (card.gsY !== undefined && card.gsH !== undefined && card.gsW !== undefined && card.gsW >= 1 && card.gsH >= 1) {
        maxBottom = Math.max(maxBottom, card.gsY + card.gsH);
      } else {
        // Estimate from column/order if no valid gs* values
        // Use a default height of 6 for each card stacked vertically
        const estimatedY = card.order * 6;
        maxBottom = Math.max(maxBottom, estimatedY + 6);
      }
    });

    // Default: place at x=0, below all existing cards, width=4 (1/3 of 12-col grid), height=6
    return { gsX: 0, gsY: maxBottom, gsW: 4, gsH: 6 };
  };

  // Handle creating a new card from modal
  const handleCreateCard = () => {
    if (!newCardTitle.trim()) {
      alert('Please enter a card title');
      return;
    }

    // Generate a unique key for the new card
    const newKey = `card-${Date.now()}`;

    // Get the highest order number in the target column
    const columnCards = cardConfigs.filter(c => c.column === newCardColumn && c.workspaceId === currentWorkspaceId);
    const maxOrder = columnCards.length > 0 ? Math.max(...columnCards.map(c => c.order)) : -1;

    // Calculate GridStack position for new card
    const gridPos = getNextGridPosition(currentWorkspaceId);

    // Create the new card config with GridStack position
    const newCard: CardConfig = {
      key: newKey,
      title: newCardTitle,
      column: newCardColumn,
      order: maxOrder + 1,
      visible: true,
      span: 1,
      workspaceId: currentWorkspaceId,
      gsX: gridPos.gsX,
      gsY: gridPos.gsY,
      gsW: gridPos.gsW,
      gsH: gridPos.gsH
    };

    // Add the card to the config
    setCardConfigs([...cardConfigs, newCard]);

    // Reset the form and close the modal
    setNewCardTitle('');
    setNewCardColumn(0);
    setIsAddCardModalOpen(false);
  };

  // Check if current workspace has reached the card limit
  const isCurrentWorkspaceFull = useMemo(() => {
    return getWorkspaceCardCount(currentWorkspaceId) >= LC_WORKSPACE_CARD_LIMIT;
  }, [currentWorkspaceId, cardConfigs]);

  // Collect all component data for bulk permissions. The ids must match the
  // `lc-${cardKey}` componentIds the card frames register with (this page was
  // originally copied from DispatchDashboard, whose cards use `dispatch-`).
  const allComponents = useMemo(() => {
    return cardConfigs
      .map(card => ({
        id: `lc-${card.key}`,
        name: card.title,
      }));
  }, [cardConfigs]);

  useEffect(() => {
    if (permissionsMode && setPageComponents) {
      setPageComponents(allComponents);
    }
  }, [permissionsMode, allComponents, setPageComponents]);

  // Register Create Card button in footer
  useEffect(() => {
    const currentCardCount = getWorkspaceCardCount(currentWorkspaceId);
    const cardsRemaining = LC_WORKSPACE_CARD_LIMIT - currentCardCount;

    // The Exports workspace is a fixed two-table board (Exports Summary + Exports
    // Board), not a build-your-own-cards surface — no Create Card / Cards Remaining.
    if (currentWorkspaceId === 'exports' || currentWorkspaceId.startsWith('managed:')) {
      setDispatchButton(null);
    } else {
      setDispatchButton(
        <div className="inline-flex items-center gap-3">
          <span className="text-sm text-gray-600 dark:text-gray-400">
            Cards Remaining: <span className="font-semibold text-heading">{cardsRemaining}</span>
          </span>
          <button
            onClick={handleCreateBlankCard}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-accent-fg bg-accent rounded-lg shadow-sm hover:bg-accent-hover transition-colors"
          >
            <PlusIcon className="h-5 w-5" />
            Create Card
          </button>
        </div>
      );
    }

    // Clear button when unmounting
    return () => {
      setDispatchButton(null);
    };
  }, [isCurrentWorkspaceFull, setDispatchButton, handleCreateBlankCard, currentWorkspaceId, cardConfigs]);


  // Toggle collapse state for a section
  // When expanding, the useEffect watching `collapsed` will trigger a batch fetch
  // When collapsing, clear data to free memory
  const toggleCollapse = (sectionKey: string) => {
    const isCurrentlyCollapsed = collapsed[sectionKey];
    const card = cardConfigs.find(c => c.key === sectionKey);

    // If we're expanding the card (currently collapsed -> becoming expanded)
    if (isCurrentlyCollapsed) {
      // Cancel any in-progress batch request - the useEffect will trigger a new one
      // that includes this newly expanded card
      if (batchCardsAbortControllerRef.current) {
        batchCardsAbortControllerRef.current.abort();
        batchCardsAbortControllerRef.current = null;
      }

      // Restore the GridStack height when expanding. Prefer the height captured at
      // collapse (expandedGsH), but fall back to the saved gsH — which the change
      // handler keeps as the expanded height even while collapsed (see its nextH).
      // The fallback is essential: the grid 'change' handler that fires on the
      // collapse's grid.update reads a not-yet-synced cardConfigsRef and can clobber
      // expandedGsH before it persists (more likely on LC, which also refetches card
      // data on collapse). Without it the card would expand into h=1 and look shrunk.
      const restoreH = card?.expandedGsH ?? card?.gsH;
      if (gridInstanceRef.current && restoreH && restoreH >= 2) {
        const element = document.querySelector(`[gs-id="${sectionKey}"]`) as HTMLElement;
        if (element) {
          gridInstanceRef.current.update(element, { h: restoreH });
        }
        // Clear the stored expanded height
        setCardConfigs(prev => prev.map(c =>
          c.key === sectionKey ? { ...c, expandedGsH: undefined } : c
        ));
      }
    } else {
      // MEMORY OPTIMIZATION: When collapsing, clear the card's data to free memory
      setCardData(prev => {
        const newData = { ...prev };
        delete newData[sectionKey];
        return newData;
      });

      // Collapse the GridStack item to just show the header (height = 1)
      if (gridInstanceRef.current) {
        const element = document.querySelector(`[gs-id="${sectionKey}"]`) as HTMLElement;
        if (element) {
          // Get current height from GridStack node or DOM
          const node = (element as any).gridstackNode;
          const currentHeight = node?.h || parseInt(element.getAttribute('gs-h') || '6', 10);

          // Store the current height before collapsing
          setCardConfigs(prev => prev.map(c =>
            c.key === sectionKey ? { ...c, expandedGsH: currentHeight } : c
          ));

          // Update GridStack to collapsed height (1 unit = just header)
          gridInstanceRef.current.update(element, { h: 1 });
        }
      }
    }

    setCollapsed(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }));
  };

  // Collapse all cards in the current workspace
  const collapseAllCards = useCallback(() => {
    const currentWorkspaceCards = cardConfigs.filter(c => c.workspaceId === currentWorkspaceId && isWorkspaceCardShown(c, showHidden));
    const cardsToCollapse = currentWorkspaceCards.filter(card => !collapsed[card.key]);

    if (cardsToCollapse.length === 0) return;

    // Cancel any in-progress batch request since we're collapsing cards
    if (batchCardsAbortControllerRef.current) {
      batchCardsAbortControllerRef.current.abort();
      batchCardsAbortControllerRef.current = null;
    }

    // Clear data for all cards being collapsed
    setCardData(prev => {
      const newData = { ...prev };
      cardsToCollapse.forEach(card => {
        delete newData[card.key];
      });
      return newData;
    });

    // Store heights and collapse GridStack items
    if (gridInstanceRef.current) {
      const heightUpdates: { [key: string]: number } = {};

      cardsToCollapse.forEach(card => {
        const element = document.querySelector(`[gs-id="${card.key}"]`) as HTMLElement;
        if (element) {
          const node = (element as any).gridstackNode;
          const currentHeight = node?.h || parseInt(element.getAttribute('gs-h') || '6', 10);
          heightUpdates[card.key] = currentHeight;
          gridInstanceRef.current!.update(element, { h: 1 });
        }
      });

      // Store expanded heights in card configs
      setCardConfigs(prev => prev.map(c =>
        heightUpdates[c.key] ? { ...c, expandedGsH: heightUpdates[c.key] } : c
      ));
    }

    // Set all as collapsed
    setCollapsed(prev => {
      const newCollapsed = { ...prev };
      cardsToCollapse.forEach(card => {
        newCollapsed[card.key] = true;
      });
      return newCollapsed;
    });
  }, [cardConfigs, currentWorkspaceId, collapsed, showHidden]);

  // Expand all cards in the current workspace
  const expandAllCards = useCallback(() => {
    const currentWorkspaceCards = cardConfigs.filter(c => c.workspaceId === currentWorkspaceId && isWorkspaceCardShown(c, showHidden));
    const cardsToExpand = currentWorkspaceCards.filter(card => collapsed[card.key]);

    if (cardsToExpand.length === 0) return;

    // Cancel any in-progress batch request - the useEffect will trigger a new one
    if (batchCardsAbortControllerRef.current) {
      batchCardsAbortControllerRef.current.abort();
      batchCardsAbortControllerRef.current = null;
    }

    // Restore GridStack heights
    if (gridInstanceRef.current) {
      const keysToRestore: string[] = [];

      cardsToExpand.forEach(card => {
        if (card.expandedGsH) {
          const element = document.querySelector(`[gs-id="${card.key}"]`) as HTMLElement;
          if (element) {
            gridInstanceRef.current!.update(element, { h: card.expandedGsH });
            keysToRestore.push(card.key);
          }
        }
      });

      // Clear expanded heights from card configs
      if (keysToRestore.length > 0) {
        setCardConfigs(prev => prev.map(c =>
          keysToRestore.includes(c.key) ? { ...c, expandedGsH: undefined } : c
        ));
      }
    }

    // Set all as expanded - the useEffect watching `collapsed` will trigger the batch fetch
    setCollapsed(prev => {
      const newCollapsed = { ...prev };
      cardsToExpand.forEach(card => {
        newCollapsed[card.key] = false;
      });
      return newCollapsed;
    });
  }, [cardConfigs, currentWorkspaceId, collapsed, showHidden]);

  // Drag handlers
  const handleDragStart = (e: React.DragEvent, cardKey: string) => {
    setDraggedCard(cardKey);
    setDragOverCard(null);
  };

  const handleDragOver = (e: React.DragEvent, cardKey: string) => {
    if (!draggedCard || draggedCard === cardKey) return;
    e.preventDefault();

    const rect = e.currentTarget.getBoundingClientRect();
    const midpoint = rect.top + rect.height / 2;
    const position = e.clientY < midpoint ? 'above' : 'below';

    setDragOverCard(cardKey);
    setDropPosition(position);
  };

  const handleDrop = (e: React.DragEvent, targetCardKey: string) => {
    e.preventDefault();
    if (!draggedCard || draggedCard === targetCardKey) return;

    const draggedCardConfig = cardConfigs.find(c => c.key === draggedCard);
    const targetCardConfig = cardConfigs.find(c => c.key === targetCardKey);

    if (!draggedCardConfig || !targetCardConfig) return;

    setCardConfigs(prev => {
      const newConfigs = prev.map(card => {
        if (card.key === draggedCard) {
          return {
            ...card,
            column: targetCardConfig.column,
          };
        }
        return card;
      });

      const sameColumnCards = newConfigs.filter(c =>
        c.column === targetCardConfig.column
      );

      const targetIndex = sameColumnCards.findIndex(c => c.key === targetCardKey);
      const draggedIndex = sameColumnCards.findIndex(c => c.key === draggedCard);

      if (targetIndex === -1 || draggedIndex === -1) return newConfigs;

      const insertIndex = dropPosition === 'above' ? targetIndex : targetIndex + 1;
      const adjustedInsertIndex = draggedIndex < targetIndex && dropPosition === 'below'
        ? insertIndex - 1
        : draggedIndex > targetIndex && dropPosition === 'above'
          ? insertIndex + 1
          : insertIndex;

      sameColumnCards.splice(draggedIndex, 1);
      sameColumnCards.splice(adjustedInsertIndex, 0, newConfigs.find(c => c.key === draggedCard)!);

      sameColumnCards.forEach((card, index) => {
        const originalCard = newConfigs.find(c => c.key === card.key);
        if (originalCard) {
          originalCard.order = index;
        }
      });

      return newConfigs;
    });

    setDraggedCard(null);
    setDragOverCard(null);
  };

  // Get col-span class based on span value
  const getColSpanClass = (span: number) => {
    switch (span) {
      case 1: return 'lg:col-span-1';
      case 2: return 'lg:col-span-2';
      case 3: return 'lg:col-span-3';
      case 4: return 'lg:col-span-4';
      default: return 'lg:col-span-1';
    }
  };

  // Get grid column start class based on column number
  const getGridColumnStartClass = (column: number) => {
    return column + 1;
  };

  // Get table header background color based on urgency of data in the card
  // Red = has Past Due or Today items, Orange = has Tomorrow items (no red), Slate = default
  const getTableHeaderBgColor = (cardKey: string, cardData: any[]): string | undefined => {
    // Special case: Drop Released Not Dispatched - never show color
    if (cardKey === 'drop-released-not-dispatched') {
      return undefined;
    }

    // Special case: Past Due Per Diem - red if has data
    if (cardKey === 'past-due') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: Live Load/Unload, No Terminate - red if has data
    if (cardKey === 'live-load-unload-no-terminate') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: Dispatched Driver OOS - red if has data
    if (cardKey === 'dispatched-driver-oos') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: Terminated Not Complete - red if has data
    if (cardKey === 'terminated-not-complete') {
      return cardData.length > 0 ? 'bg-brand-red dark:bg-brand-red' : undefined;
    }

    // Special case: No Outgate - red if any row meets time threshold for its region
    if (cardKey === 'no-outgate' && cardData.length > 0) {
      const now = new Date();
      const currentHour = now.getHours();

      // Check if any row meets the time threshold
      const hasUrgentRow = cardData.some((row: any) => {
        const region = row.region?.toLowerCase() || '';
        let thresholdHour = 14; // Default: 2pm

        if (region === 'mobile') {
          thresholdHour = 13; // 1pm for Mobile
        } else if (region === 'dallas' || region === 'memphis') {
          thresholdHour = 15; // 3pm for Dallas/Memphis
        }

        return currentHour >= thresholdHour;
      });

      if (hasUrgentRow) {
        return 'bg-brand-red dark:bg-brand-red';
      }
    }

    // Get the tab grouping field for this card
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    const tabGroupingField = cardConfig?.tabGrouping;

    if (!tabGroupingField || cardData.length === 0) return undefined;

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayStr = today.toISOString().split('T')[0];

    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    const tomorrowStr = tomorrow.toISOString().split('T')[0];

    let hasPastDueOrToday = false;
    let hasTomorrow = false;

    // Check each row's date to determine urgency
    cardData.forEach((row: any) => {
      const value = row[tabGroupingField];
      if (!value) return;

      // Extract date string (handle datetime formats)
      let dateStr = String(value);
      if (dateStr.includes('T')) {
        dateStr = dateStr.split('T')[0];
      } else if (dateStr.includes(' ')) {
        dateStr = dateStr.split(' ')[0];
      }

      // Check if valid date format
      if (!/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return;
      dateStr = dateStr.substring(0, 10);

      if (dateStr <= todayStr) {
        hasPastDueOrToday = true;
      } else if (dateStr === tomorrowStr) {
        hasTomorrow = true;
      }
    });

    // Priority: Red > Orange > Slate
    if (hasPastDueOrToday) {
      return 'bg-brand-red dark:bg-brand-red';
    }
    if (hasTomorrow) {
      return 'bg-orange-500 dark:bg-orange-600';
    }

    // Default slate
    return 'bg-slate-700 dark:bg-slate-800';
  };


  // =============================================================================
  // DYNAMIC COLUMN GENERATION
  // =============================================================================

  // Shared PRO# column definition. Used by the dynamic bucket path (via
  // customColumnDefs.pronumber) and by every locked-down bucket whose row
  // shape forces a hand-rolled column array (mismatched-drivers,
  // invalid-container-numbers, missing-waiting-time). Coerces value via
  // String(...) so numeric pronumbers from buckets that select o.pronumber
  // as an int render the same as string-typed ones.
  const proNumberColumn = useMemo<ColumnDef<any>>(() => ({
    accessorKey: 'pronumber',
    header: 'PRO#',
    enableSorting: true,
    enableColumnFilter: true,
    filterFn: 'arrIncludes',
    cell: (info) => {
      const pronumber = String(info.getValue() ?? '');
      if (!pronumber) return '';
      const legacyCmsUrl = config.LEGACY_CMS_URL;

      // Viewed/last-viewed highlight + click-to-mark is handled by TanTable's
      // trackViewedColumnId="pronumber" wrapper on each table below.
      return (
        <div className="flex items-center gap-2">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedProNumber(pronumber);
            }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
          >
            {pronumber}
          </button>
          <a
            href={`${legacyCmsUrl}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Open in legacy CMS"
          >
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        </div>
      );
    },
    aggregatedCell: () => null,
  }), []);

  // Additional Cost drill columns: PRO# + the AC-level set, with the "Invoice #" cell
  // made clickable to open the same AdditionalCostItemModal the pro page uses (keyed on
  // the row's pronumber + ac.recno). A blank invoice number shows "none" but still opens.
  const addCostDrillColumns = useMemo<ColumnDef<any>[]>(() => {
    const invoiceCol: ColumnDef<any> = {
      accessorKey: 'invoicenumber',
      header: 'Invoice #',
      cell: (c) => {
        const row = c.row.original as { pronumber?: any; ac_recno?: any; invoicenumber?: any };
        const inv = String(c.getValue() ?? '').trim();
        const recno = row.ac_recno != null ? Number(row.ac_recno) : null;
        const pronumber = Number(row.pronumber);
        return (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (recno != null && pronumber) setAddCostModal({ isOpen: true, pronumber, recno });
            }}
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
          >
            {inv || 'none'}
          </button>
        );
      },
    };
    return [
      proNumberColumn,
      ...ADDCOST_COLUMNS.map((col) =>
        (col as { accessorKey?: string }).accessorKey === 'invoicenumber' ? invoiceCol : col,
      ),
    ];
  }, [proNumberColumn]);

  // Custom column definitions for fields that need special rendering
  // These are keyed by the API field name (after mapping from db field name)
  const customColumnDefs = useMemo<Record<string, ColumnDef<any>>>(() => ({
    pronumber: proNumberColumn,
    // Service Failure (SF) + Trouble Ticket (TT) computed columns — the TT badge
    // click opens the order drawer.
    ...buildComputedColumnDefs({ onProClick: setSelectedProNumber }),
    perdiemstartdate: {
      accessorKey: 'perdiemstartdate',
      header: 'Per Diem Start',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { highlightDateProximity: true },
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00') return '';
        const dateValue = new Date(value + 'T00:00:00');
        if (isNaN(dateValue.getTime())) return value;
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      },
    },
    lastfreedaydate: {
      accessorKey: 'lastfreedaydate',
      header: 'LFD',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { highlightDateProximity: true },
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00') return '';
        const dateValue = new Date(value + 'T00:00:00');
        if (isNaN(dateValue.getTime())) return value;
        const month = String(dateValue.getMonth() + 1).padStart(2, '0');
        const day = String(dateValue.getDate()).padStart(2, '0');
        return `${month}/${day}`;
      },
    },
    cutoffdatetime: {
      accessorKey: 'cutoffdatetime',
      header: 'Cutoff',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      meta: { highlightDateProximity: true },
      cell: (info) => {
        const value = info.getValue() as string;
        if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') return '';
        let dateObj: Date;
        if (value.includes('T')) {
          dateObj = new Date(value);
        } else if (value.includes(' ')) {
          const [datePart, timePart] = value.split(' ');
          dateObj = new Date(`${datePart}T${timePart}`);
        } else {
          dateObj = new Date(value + 'T00:00:00');
        }
        if (isNaN(dateObj.getTime())) return value;
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        let hours = dateObj.getHours();
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12;
        return `${month}/${day} ${hours}:${minutes} ${ampm}`;
      },
    },
    // OOS Driver column for dispatched-driver-oos bucket
    oos_driver: {
      accessorKey: 'oos_driver',
      header: 'OOS Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    // Driver column for dispatches-not-accepted bucket (leg.driver, extra select)
    dispatched_driver: {
      accessorKey: 'dispatched_driver',
      header: 'Driver',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
    // Leg dspdriverdatetime column for dispatches-not-accepted bucket (leg.dspdriverdatetime, extra select)
    dispatched_datetime: {
      accessorKey: 'dispatched_datetime',
      header: 'Dispatched',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const value = info.getValue() as string | null | undefined;
        if (!value || value === '0000-00-00 00:00:00' || value === '0000-00-00') return '';
        let dateObj: Date;
        if (value.includes('T')) {
          dateObj = new Date(value);
        } else if (value.includes(' ')) {
          const [datePart, timePart] = value.split(' ');
          dateObj = new Date(`${datePart}T${timePart}`);
        } else {
          dateObj = new Date(value + 'T00:00:00');
        }
        if (isNaN(dateObj.getTime())) return value;
        const currentYear = new Date().getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, '0');
        const day = String(dateObj.getDate()).padStart(2, '0');
        const hours = String(dateObj.getHours()).padStart(2, '0');
        const minutes = String(dateObj.getMinutes()).padStart(2, '0');
        if (dateObj.getFullYear() !== currentYear) {
          return `${month}/${day}/${String(dateObj.getFullYear()).slice(-2)} ${hours}:${minutes}`;
        }
        return `${month}/${day} ${hours}:${minutes}`;
      },
    },
    // Per diem days column for per-diem-not-dispatched and past-due buckets
    perdiemdays: {
      accessorKey: 'perdiemdays',
      header: 'PD Days',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() ?? '',
    },
    // Street turn opportunities count + clickable link to gwi360 multi-pro search
    streetturnopportunity: {
      accessorKey: 'streetturnopportunity',
      header: 'ST Opps',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => {
        const list = info.getValue() as string | null | undefined;
        if (!list) return null;
        const count = list.split(',').filter(Boolean).length;
        if (count === 0) return null;
        return (
          <button
            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              window.open(`/searchresults?q=${encodeURIComponent(list)}&multi=true`, '_blank', 'noopener,noreferrer');
            }}
            title={list}
          >
            {count}
          </button>
        );
      },
      aggregatedCell: () => null,
    },
    // Computed columns for scheduled-not-dispatched bucket
    d_legmiles_band: {
      accessorKey: 'd_legmiles_band',
      header: 'Mile Band',
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue() || '',
    },
  }), [proNumberColumn]);

  /**
   * Generate column definitions for a bucket based on its field list.
   * Uses custom column definitions for special fields, falls back to generateColumnDef for others.
   */
  const generateBucketColumns = useCallback((
    bucket: string,
    additionalFields: string[] = [],
    // A per-row trace finished on the SINGLE ROW view (the ➡️ decorateTraceColumn
    // puts on the SCAC / Terminal cells). These rows are the dynamic
    // field-definition payload, not ProSearchData, so there is no single-row
    // re-read to merge the way the card view does — the caller refetches the table
    // the row belongs to, which is what every other in-place edit here already does
    // after a change-all. Scoped per caller rather than "the open bucket" so it
    // also works from a board tile, where no single card is selected.
    onTraced?: (pronumber: number) => void,
  ): ColumnDef<any>[] => {
    // Get the ordered field list for this bucket (Import buckets include extras)
    const bucketFields = lcBucketFields(bucket);
    let allFields = [...bucketFields, ...additionalFields.filter(f => !bucketFields.includes(f))];

    // For scheduled-not-dispatched, exclude d_legmiles from display columns
    // (it's still fetched for computing the mile band, but not shown as a column)
    if (bucket === 'scheduled-not-dispatched') {
      allFields = allFields.filter(f => f !== 'd_legmiles');
    }

    const columns: ColumnDef<any>[] = [];

    for (const fieldname of allFields) {
      const apiFieldName = getApiFieldName(fieldname);

      // Check if we have a custom column definition
      if (customColumnDefs[apiFieldName]) {
        columns.push(decorateMuleDriverColumn(customColumnDefs[apiFieldName], apiFieldName));
        continue;
      }

      // Find the field definition and generate column
      const fieldDef = fieldDefinitions.find(f => f.fieldname === fieldname && f.tablename === 'orderscurrent')
        || fieldDefinitions.find(f => f.fieldname === fieldname);

      if (fieldDef) {
        // Location columns get an "ALIAS - Full Name" hover tooltip (shared with
        // the Dispatch dashboard); non-location columns pass through unchanged.
        // SCAC / Rail-Terminal additionally carry the per-row run-trace ➡️ (the
        // Single Row twin of the same control on the card view's carrier cells).
        columns.push(decorateTraceColumn(
          decorateLocationColumn(generateColumnDef(fieldDef), fieldname),
          fieldname,
          onTraced,
        ));
      } else {
        // Fallback: create a basic column if no field definition found
        columns.push(decorateMuleDriverColumn({
          accessorKey: apiFieldName,
          header: fieldname,
          enableSorting: true,
          enableColumnFilter: true,
          filterFn: 'arrIncludes',
          cell: (info) => info.getValue() || '',
        }, apiFieldName));
      }
    }

    // Add computed columns for scheduled-not-dispatched bucket (used for grouping)
    if (bucket === 'scheduled-not-dispatched') {
      if (customColumnDefs['d_legmiles_band']) {
        columns.push(customColumnDefs['d_legmiles_band']);
      }
    }

    // Missing / Out-of-Order Events: paint each milestone cell red when it's missing or
    // out of sequence vs the next expected milestone (legacy fv_problemresolutiontracing).
    // Merge into a fresh column object so the shared customColumnDefs aren't mutated.
    if (bucket === 'lc-tracing-missing-events') {
      return columns.map((col) => {
        const key = ((col as any).accessorKey || col.id) as string | undefined;
        const test = key ? MOE_HIGHLIGHT[key] : undefined;
        if (!test) return col;
        return {
          ...col,
          meta: {
            ...((col as any).meta || {}),
            cellBgFn: (_v: any, row: any) => (test(row) ? 'bg-brand-red text-white' : ''),
          },
        } as ColumnDef<any>;
      });
    }

    // No Pickup Number: make the Pickup Number (PU#) column inline-editable — staged until
    // Save, right-click + drag to bulk-edit a range. Read-only text (the fall-through below)
    // for users without the LC-fields edit gate. Merge into a fresh column so the shared
    // customColumnDefs aren't mutated.
    if (bucket === NO_PICKUP_BUCKET && canEditOrderLcFields) {
      return columns.map((col) => {
        const key = ((col as any).accessorKey || col.id) as string | undefined;
        if (key !== 'o_origapptnumber') return col;
        return {
          ...col,
          meta: { ...((col as any).meta || {}), bulkEdit: { type: 'text' } as BulkEditMeta },
          cell: (info: any) => {
            const row = info.row.original;
            const pro = String(row?.pronumber ?? '');
            const original = String(row?.o_origapptnumber ?? '');
            const staged = pickupEdits[pro];
            const effective = staged !== undefined ? staged : original;
            return (
              <PickupCell
                pro={pro}
                value={effective}
                edited={staged !== undefined}
                onCommit={(v) => setPickupEdit(pro, v, original)}
              />
            );
          },
        } as ColumnDef<any>;
      });
    }

    return columns;
  }, [customColumnDefs, fieldDefinitions, canEditOrderLcFields, pickupEdits, setPickupEdit]);

  // Column defs for the Chassis Charges drill-down TanTable — the bespoke custom layout
  // (not LC/field-definition columns). Rows come from /chassis-charge-table keyed by these
  // exact accessors. Standard sort/filter like every other drill table; pronumber reuses
  // the shared clickable PRO column, Charges sorts numerically (parse the "$" string) and
  // renders red (it's always a positive charge for these rows).
  const generateChassisColumns = useCallback((): ColumnDef<any>[] => {
    const text = (key: string, header: string): ColumnDef<any> => ({
      accessorKey: key,
      header,
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: 'arrIncludes',
      cell: (info) => (info.getValue() as any) ?? '',
    });
    const parseMoney = (v: any) => parseFloat(String(v ?? '').replace(/[$,]/g, '')) || 0;
    return [
      proNumberColumn,
      text('container', 'Container'),
      text('movetype', 'Move Type'),
      text('ssline', 'SSLine'),
      text('csrname', 'CSR'),
      text('whsecsrname', 'Whse CSR'),
      text('dispatcher', 'Dispatcher'),
      text('d_destname', 'Deliver To'),
      text('dropreleasedatetime', 'Drop Release Date'),
      {
        accessorKey: 'chassischargesdaysout',
        header: 'Days Out',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        cell: (info) => (info.getValue() as any) ?? '',
      },
      {
        accessorKey: 'chassischargesamount',
        header: 'Charges',
        enableSorting: true,
        enableColumnFilter: true,
        filterFn: 'arrIncludes',
        sortingFn: (a, b, id) => parseMoney(a.getValue(id)) - parseMoney(b.getValue(id)),
        cell: (info) => {
          const v = info.getValue() as string;
          if (!v) return '';
          return <span className="inline-block px-2 rounded bg-red-600 text-white tabular-nums">{v}</span>;
        },
      },
    ];
  }, [proNumberColumn]);

  // Render card content - placeholder for dynamic cards
  // isFullScreen is used when rendering single card in full-screen mode
  /**
   * The documents table for `tableKey`, or null when that table is showing orders.
   *
   * A helper rather than inline JSX per host: every action-bar host needs the same
   * early return, and they are spread across card bodies and grid-cell drills.
   */
  const renderDocumentsFor = useCallback((tableKey: string) => {
    const docs = docsViewCards[tableKey];
    if (!docs) return null;
    return (
      <CardDocumentsTable
        pros={docs.pros}
        cardTitle={docs.title}
        settingsKey="lc-card-documents"
        onProClick={setSelectedProNumber}
        defaultRecipient={currentUserEmail}
        onBackToOrders={() => closeCardDocuments(tableKey)}
        initialSettings={tableSettings['table_lc-card-documents']}
        settingsReady={tableSettingsLoaded}
        settingsLoadFailed={tableSettingsLoadFailed}
        onSettingsSaved={handleTableSettingsSaved}
      />
    );
  }, [docsViewCards, currentUserEmail, closeCardDocuments, tableSettings,
      tableSettingsLoaded, tableSettingsLoadFailed, handleTableSettingsSaved]);

  const renderCardContent = (cardKey: string, isFullScreen: boolean = false) => {
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    if (!cardConfig) return null;

    // A code-defined bucket backed by canonical server SQL, as opposed to a card the
    // user built. Decides which filter affordance the header offers (see the TanTable
    // props below) — the two are mutually exclusive.
    const isSystemBucket = systemBucketDefaults.has(cardKey);

    // Documents mode: the SAME single table, repainted with document rows/columns.
    // The card keeps its own chrome (title, toolbar, action bar) — only the contents
    // change — so this never reads as a table nested inside the card.
    const cardDocs = renderDocumentsFor(cardKey);
    if (cardDocs) return cardDocs;

    // Check if card has filter configuration or is a bucket-based card
    const hasFilters = (cardConfig.filterConditions && cardConfig.filterConditions.length > 0) || cardConfig.bucket;
    const data = cardData[cardKey] || [];  // Backend now handles filtering
    const loading = cardDataLoading[cardKey] || false;

    // If card has filters or bucket, show DispatchTable with results
    if (hasFilters) {
      const currentHeight = cardConfig.cardHeight || 'lg';

      // Use persisted grouping/tabGrouping, or fall back to defaults for bucket cards
      let defaultGrouping: string[] | undefined = cardConfig.grouping;

      // Determine tab grouping field - use persisted value, or fall back to defaults for bucket cards
      // Note: 'none' is a special marker that means the user explicitly disabled tab grouping
      let tabGroupingField = cardConfig.tabGrouping;
      // Migrate stale 'destapptdate' tab grouping (early dispatches-not-accepted draft used that alias
      // before the bucket was switched to the field-def `leg_destapptdate`).
      if (cardConfig.bucket === 'dispatches-not-accepted' && tabGroupingField === 'destapptdate') {
        tabGroupingField = 'leg_destapptdate';
        setCardConfigs(prev => prev.map(card =>
          card.key === cardKey ? { ...card, tabGrouping: 'leg_destapptdate' } : card
        ));
      }
      const userExplicitlyDisabledTabs = tabGroupingField === 'none';

      // If tabGrouping is not set (undefined or empty string, but NOT 'none') and this is a bucket card, use defaults
      // Note: dispatched-driver-oos and drop-released-not-dispatched should NOT have default tab grouping
      if (!tabGroupingField && cardConfig.bucket && cardConfig.bucket !== 'dispatched-driver-oos' && cardConfig.bucket !== 'drop-released-not-dispatched' && cardConfig.bucket !== 'scheduled-not-dispatched') {
        if (cardConfig.bucket === 'per-diem-not-dispatched') {
          tabGroupingField = 'perdiemstartdate';
        } else if (cardConfig.bucket === 'cutoff-not-dispatched' || cardConfig.bucket === 'cutoff-not-terminated') {
          tabGroupingField = 'cutoffdatetime';
        } else if (cardConfig.bucket === 'dispatches-not-accepted') {
          tabGroupingField = 'leg_destapptdate';
        }

        // Persist the default tab grouping to the card config so it's saved
        if (tabGroupingField) {
          setCardConfigs(prev => prev.map(card =>
            card.key === cardKey && !card.tabGrouping ? { ...card, tabGrouping: tabGroupingField } : card
          ));
        }
      }

      // Custom grouping for scheduled-not-dispatched: Add computed fields for mile bands and past-due grouping
      let enhancedData = data;
      if (cardConfig.bucket === 'scheduled-not-dispatched') {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        // Add computed fields to each row
        enhancedData = data.map((row: any) => {
          const miles = Number(row.d_legmiles) || 0;
          let mileBand = '';
          if (miles < 300) {
            mileBand = '0-300 Miles';
          } else if (miles < 550) {
            mileBand = '300-550 Miles';
          } else if (miles < 850) {
            mileBand = '550-850 Miles';
          } else {
            mileBand = '850+ Miles';
          }

          return {
            ...row,
            d_legmiles_band: mileBand
          };
        });
      }

      // Set default grouping to mile bands if not already set for scheduled-not-dispatched
      if (cardConfig.bucket === 'scheduled-not-dispatched' && !tabGroupingField) {
        tabGroupingField = 'd_legmiles_band';

        // Persist the default tab grouping and table grouping (use grouped date field)
        setCardConfigs(prev => prev.map(card =>
          card.key === cardKey && !card.tabGrouping ? { ...card, tabGrouping: 'd_legmiles_band' } : card
        ));
      }

      // If user explicitly disabled tabs, clear the field
      if (userExplicitlyDisabledTabs) {
        tabGroupingField = undefined;
      }

      // Show tabs only if tabGroupingField is set (either from user selection or from defaults)
      let showTabs = !!tabGroupingField;

      // Columns are generated dynamically from the bucket's required fields plus
      // any additional fields the user selected.
      const columnsToUse = generateBucketColumns(
        cardConfig.bucket || cardKey,
        cardConfig.additionalFields || [],
        // Silent refetch of THIS card so the traced row picks up what the trace
        // wrote (Trace Date, LFD, Available, …). Silent = no spinner, so the table
        // does not flash for a one-row change.
        () => fetchCardData(
          cardKey,
          cardConfig.filterConditions || [],
          cardConfig.filterView || 'Orders',
          cardConfig.bucket,
          true,
          cardConfig.additionalFields,
        ),
      );

      return (
        <div className="w-full h-full">
          <TanTable
            data={enhancedData}
            columns={columnsToUse}
            trackViewedColumnId="pronumber"
            // Mass actions (Change All / Change Template for All / Trace All +
            // any bucket-specific sends) live in this table's action bar, each
            // scoped to the table's visible/filtered rows. Board tiles and the
            // open drill's Single-Row view both get it from here.
            bulkActions={drillBulkActions(cardConfig.bucket || null, { docsKey: cardKey, docsTitle: cardConfig.title })}
            // No Pickup Number bucket only: right-click + drag the Pickup Number column to
            // bulk-edit a range (staged until Save). Every other bucket leaves this unset.
            onBulkEdit={
              (cardConfig.bucket || cardKey) === NO_PICKUP_BUCKET && canEditOrderLcFields
                ? handlePickupBulkEdit
                : undefined
            }
            // Only the open drill (full-screen / drawer) reports its filtered
            // rows, so a Send All honors the user's in-grid filtering.
            onFilteredRowsChange={isFullScreen ? handleDrillFilteredRows : undefined}
            loading={loading}
            errorMessage={cardErrors[cardKey]}
            onRetry={() => fetchCardData(cardKey, cardConfig.filterConditions || [], cardConfig.filterView || 'Orders', cardConfig.bucket, false, cardConfig.additionalFields)}
            settingsKey={`lc-card-${cardKey}`}
            {...(customCardScopeKey(cardKey)
              ? { templateScopeKey: customCardScopeKey(cardKey) }
              : {})}
            initialSettings={tableSettings[`table_lc-card-${cardKey}`]}
            settingsReady={tableSettingsLoaded}
            settingsLoadFailed={tableSettingsLoadFailed}
            onSettingsSaved={handleTableSettingsSaved}
            searchPlaceholder="Search orders..."
            fieldName={cardKey}
            enableGrouping={true}
            showTabs={showTabs}
            allowTabFieldSelection={false}
            defaultGrouping={defaultGrouping}
            tabGroupingField={tabGroupingField}
            enableColumnFilters={true}
            cardFilterConditions={cardConfig.filterConditions || []}
            cardTitle={cardConfig.required ? `${cardConfig.title} · Required` : `${cardConfig.title}${cardConfig.hidden ? ' · Hidden' : ''}`}
            // A system bucket gets the in-place filter control; a user's own card keeps
            // the card-settings modal behind the same cog (handleEditFilters routes it,
            // and the tooltip says which). The cog, not a funnel: the funnel two buttons
            // to the left is the column-filter toggle, and a second one here would read
            // as a duplicate of the control beside it.
            {...(cardConfig.required ? { showEditFiltersButton: false } : isSystemBucket
              ? customFilterProps(cardKey, cardConfig.title, cardScopeMeta[cardKey])
              : {
                showEditFiltersButton: true,
                editFiltersButtonIcon: 'cog' as const,
                editFiltersButtonTooltip: 'Card Settings',
                headerExtra: (
                  <CardResultPill
                    includeClosed={Boolean(cardScopeMeta[cardKey]?.includeClosed)}
                    truncated={cardScopeMeta[cardKey]?.truncated}
                    limit={cardScopeMeta[cardKey]?.limit}
                  />
                ),
              })}
            onEditFilters={() => handleEditFilters(cardKey)}
            fillContainer={true}
            onGroupingChange={(grouping) => handleGroupingChange(cardKey, grouping)}
            onTabGroupingChange={(tabGrouping) => handleTabGroupingChange(cardKey, tabGrouping)}
            onVisibleColumnsChange={(visibleColumns) => handleVisibleColumnsChange(cardKey, visibleColumns)}
            availableFields={availableFields}
            onAdditionalFieldsChange={(fields) => handleAdditionalFieldsChange(cardKey, fields)}
            additionalFields={cardConfig.additionalFields || []}
            defaultSettings={cardDefaults[`lc-card-${cardKey}`]}
            canEditDefaults={systemBucketDefaults.has(cardKey)}
            onSaveDefaults={handleSaveDefaults}
            mandatoryGroupingFields={getMandatoryGroupingFields(cardConfig.bucket || cardKey)}
            onDuplicate={cardConfig.required ? undefined : () => handleDuplicate(cardConfig)}
            duplicateTooltip="Duplicate Card"
            cardLocked={cardConfig.locked || false}
            onToggleCardLock={cardConfig.required ? undefined : (locked) => handleToggleCardLock(cardKey, locked)}
            collapsed={collapsed[cardKey] || false}
            multiTabSelect={true}
            activeTabs={cardActiveTabsMulti[cardKey]}
            onActiveTabsChange={(tabIds: string[]) => handleMultiTabChange(cardKey, tabIds)}
            tableHeaderBgColor={getTableHeaderBgColor(cardKey, data)}
            onToggleCollapse={() => toggleCollapse(cardKey)}
            globalResetTrigger={globalResetTrigger}
            globalPageSize={globalPageSize}
            globalShowFilters={globalShowFilters}
            globalSuperCompact={globalSuperCompact}
            globalCardHeight={globalCardHeight}
          />
        </div>
      );
    }

    // This should never happen - cards should only be created through the modal with filters
    // If a card somehow exists without filters, delete it
    return null;
  };

  // Render individual card (GridStack version - no drag handlers, GridStack handles drag/resize)
  const renderCard = (cardKey: string) => {
    const cardConfig = cardConfigs.find(c => c.key === cardKey);
    if (!cardConfig) return null;

    // In locked mode, don't show hidden cards
    if (!isWorkspaceCardShown(cardConfig, showHidden)) return null;

    return (
      <div
        key={cardKey}
        className="h-full w-full"
      >
        <PermissionConfigurable
          componentId={`lc-${cardKey}`}
          componentName={cardConfig.title}
          className="h-full w-full"
        >
          <div className={`overflow-hidden ${!(cardConfig.filterConditions && cardConfig.filterConditions.length > 0) ? 'bg-gray-50 dark:bg-gray-800 shadow rounded-lg border border-gray-300 dark:border-gray-600' : ''}`}>
            {renderCardContent(cardKey)}
          </div>
        </PermissionConfigurable>
      </div>
    );
  };

  // Invoice-/additional-cost-level drills aren't order-based, so the ProResultCard
  // (Cards) view can't render them — they're table-only. Hide the toggle for them.
  const isTableOnlyDrill = gridSel?.kind === 'pending-invoicing' || gridSel?.kind === 'additional-cost';

  // ---- Multi Row View (cards) -------------------------------------------------
  // The results/drill card list is a TanTable in CARD MODE, sharing the PRO card
  // column set with /searchresults: search, column filters, sorting, export and
  // the mass-action bar all work on cards because they operate on the row model,
  // not the markup. Column visibility picks WHICH fields a card shows and column
  // order picks WHERE.
  //
  // Deliberately INDEPENDENT of Single Row View: the bucket tiles keep their LC
  // columns and their `lc-card-*` saved layouts; the card view saves under
  // `lc-cards-*`.
  const handleCardProClick = useCallback((proNumber: string) => {
    setSelectedProNumber(proNumber);
  }, []);

  // A per-row trace finished (the ➡️ on the SSLine / Terminal cells). The trace has
  // already written the order, so the open row is stale in exactly the fields the
  // user ran it to see. Re-read that one order and swap it into whichever row list
  // holds it — a count-panel bucket's cards (cardRows) or a grid drill's
  // (gridDrillRows). Both are mapped unconditionally; the pro is in one of them and
  // the other's map is a no-op, which is cheaper than working out which drill is open.
  //
  // Deliberately NOT refreshOpenBucket(): that refetches the whole bucket for one
  // traced row and resets the table around the user.
  const cardExtraFieldsRef = useRef<string[]>([]);
  cardExtraFieldsRef.current = cardExtras.additionalFields;
  const handleRowTraced = useCallback(async (pronumber: number) => {
    // With the card view's CURRENT extra columns: Trace Date is an added column,
    // not part of the base payload, so a refresh without it left the very field
    // the trace had just written showing its pre-trace value.
    const fresh = await searchService.refreshProRows(pronumber, cardExtraFieldsRef.current);
    if (!fresh.length) return;
    const patch = (rows: ProSearchData[]) => mergeRefreshedProRows(
      rows,
      (r) => isProRow(r.pronumber, pronumber),
      fresh,
      (r, next) => ({ ...r, ...next }),
    );
    setCardRows(patch);
    setGridDrillRows(patch);
  }, []);

  const proCardColumns = useMemo(
    () => buildProCardColumns(handleCardProClick, { onTraced: handleRowTraced }),
    [handleCardProClick, handleRowTraced],
  );

  // The card view's column universe: the static PRO card set plus whatever the user
  // added — registry fields as generated columns, SF/TT as the shared computed
  // columns (the same badges the Single Row view renders). `availableFields` is the
  // Columns picker: the Single Row view's "Add column" list (computed columns +
  // every displayable orders field), with the static card columns listed too so
  // they stay show/hide-able, minus the registry fields a static column already
  // renders (LFD, Terminal, Cutoff, …) so a field is never offered twice.
  const proCardComputedFields = useMemo<ComputedFieldSet<ProSearchData>>(() => ({
    picker: COMPUTED_FIELD_OPTIONS,
    columns: buildComputedColumnDefs({ onProClick: setSelectedProNumber }) as Record<string, ColumnDef<ProSearchData>>,
  }), []);
  const {
    columns: proCardColumnsWithExtras,
    availableFields: proCardAvailableFields,
    fieldAliases: proCardFieldAliases,
  } = useAdditionalFieldColumns<ProSearchData>(cardExtras, {
      baseColumns: proCardColumns,
      coveredFields: PRO_CARD_COVERED_FIELDNAMES,
      allowExpanding: true,
      computedFields: proCardComputedFields,
    });

  // Defaults to every field the classic banded card showed, so a fresh card view
  // matches the pre-card-mode Multi Row View rather than the table's narrower core
  // set. Columns with no card-catalog entry (the service-failure set) start hidden
  // and are available from the Columns menu. Card mode has its own settingsKey, so
  // this cannot widen the Single Row View's column defaults. A just-added field
  // must SHOW, so the user's extras are never defaulted hidden (column ids are API
  // names; the selection holds registry names, hence the map).
  const proCardDefaultVisibility = useMemo<VisibilityState>(() => {
    const core = new Set([...PRO_CARD_FIELD_IDS, ...PRO_CARD_HEADER_COLUMN_IDS]);
    const added = new Set(cardExtras.additionalFields.map(getApiFieldName));
    const vis: VisibilityState = {};
    for (const col of proCardColumnsWithExtras) {
      const id = (col as { id?: string }).id ?? (col as { accessorKey?: string }).accessorKey;
      if (id && !core.has(id) && !added.has(id)) vis[id] = false;
    }
    return vis;
  }, [proCardColumnsWithExtras, cardExtras.additionalFields]);

  // Hoisted out of the render body because TanTable feeds it into the row
  // virtualizer's estimateSize deps — a new closure each render would hand
  // useVirtualizer a new estimator every time.
  //
  // Fields outside the card's own catalog (a user-added column, the SF/TT badges)
  // render through their column's cell renderer via ctx.renderCell — dates
  // formatted, badges intact — exactly as the Single Row view shows them.
  const renderProCard = useCallback(
    (
      d: ProSearchData,
      ctx: { visibleColumns: { id: string; label: string }[]; renderCell: (columnId: string) => React.ReactNode },
    ) => (
      <ProResultCard
        as="div"
        result={{ type: 'pro', value: String(d.pronumber), label: String(d.pronumber), group: 'PROs', data: d }}
        onProClick={handleCardProClick}
        orderedFields={ctx.visibleColumns.map((c) => ({ ...c, render: () => ctx.renderCell(c.id) }))}
      />
    ),
    [handleCardProClick],
  );

  // Mass sends — Send All Haz Docs (Pending Pre-advise set) / Send All Rail Bill
  // (Pending Rail Billing + Ready to Railbill sets). The server re-derives the
  // pro set from the bucket key + the current global filters (never a client row
  // list), so we only post those. Ports V1 sendallhazdocs / sendallrailbill.
  const runMassSend = async (
    endpoint: string,
    bucket: string,
    title: string,
    prosArg?: string[],
  ) => {
    setMassSendBusy(true);
    try {
      // Limit the send to the rows currently visible in the drill-down (after
      // the user's in-grid column filters / search). The action bar always
      // passes that table's own filtered pros; the derivation below is only a
      // fallback for a caller that doesn't. The server intersects with the bucket.
      const visiblePros = prosArg ??
        (resultsView === 'cards'
          ? cardRows.map((r: any) => String(r?.pronumber ?? '')).filter(Boolean)
          : visibleDrillProsRef.current
          ?? (cardData[bucket] || []).map((r: any) => String(r?.pronumber ?? '')).filter(Boolean));
      const { data } = await api.post(endpoint, {
        bucket,
        lcs: selectedLCs,
        customers: selectedCustomers,
        billtos: selectedBilltos,
        key_accounts: selectedKeyAccounts,
        dispatch_regions: selectedDispatchRegions,
        warehouses: selectedWarehouses,
        pronumbers: visiblePros,
      });
      setMassSendResult({
        title,
        sent: data.sent || [],
        skipped: data.skipped || [],
        total: data.total ?? 0,
        message: data.message || '',
      });
    } catch (err: any) {
      const detail = err?.response?.data?.detail;
      showToast({
        type: 'error',
        message: typeof detail === 'string' ? detail : `${title} failed`,
      });
    } finally {
      setMassSendBusy(false);
    }
  };

  // Fallback pro set for a mass action invoked WITHOUT an explicit list (the
  // action bar always passes its own table's filtered rows). Collects the pros
  // of whichever drill is open — a count-panel bucket (cardRows / cardData) OR a
  // grid drill (Per Diem, Schedule, Tracking, Exports, Chassis, Additional Cost,
  // Melrose, Pending Invoicing → gridDrillRows / gridDrillTable) — honoring the
  // active Multi/Single Row view and the open table's in-grid filtering
  // (visibleDrillProsRef). Distinct-ed because the invoice-/addl-cost-level grid
  // drills can list one pro on several rows.
  const collectVisibleDrillPros = (): string[] => {
    const toPros = (rows: any[]) =>
      (rows || []).map((r: any) => String(r?.pronumber ?? '')).filter(Boolean);
    let pros: string[];
    if (gridSel) {
      // Pending-invoicing / additional-cost are table-only regardless of the toggle.
      pros = isTableOnlyDrill
        ? (visibleDrillProsRef.current ?? toPros(gridDrillTable))
        : resultsView === 'cards'
          ? toPros(gridDrillRows)
          : (visibleDrillProsRef.current ?? toPros(gridDrillTable));
    } else {
      const bucket = isSingleCardSelected ? Array.from(visibleCardKeys)[0] : null;
      pros = resultsView === 'cards'
        ? toPros(cardRows)
        : (visibleDrillProsRef.current ?? (bucket ? toPros(cardData[bucket] || []) : []));
    }
    return Array.from(new Set(pros));
  };

  // Change Template for All — opens the bulk modal on the open drill's currently
  // visible pros; drops templates (T-pros, which can't be re-templated).
  const openChangeTemplateForAll = (prosArg?: string[]) => {
    const pros = (prosArg ?? collectVisibleDrillPros()).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (pros.length === 0) {
      showToast({ type: 'info', message: 'No pros in the current view to change.' });
      return;
    }
    setCtBulkPros(pros);
    setCtBulkOpen(true);
  };

  // Change All — opens the pro-detail drawer in change-all mode on the open drill's
  // currently visible pros, anchored on the first. Templates (T-pros) can't be
  // edited as orders, so they're dropped.
  const openChangeAll = (prosArg?: string[]) => {
    const pros = (prosArg ?? collectVisibleDrillPros()).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (pros.length === 0) {
      showToast({ type: 'info', message: 'No pros in the current view to change.' });
      return;
    }
    // Change All and the single-pro drawer are mutually exclusive — close the
    // single-pro drawer first so they never overlap.
    if (selectedProNumber) setSelectedProNumber(null);
    setChangeAllPros(pros);
  };

  // Refresh the open bucket's results after a change-all fan-out closes, so the
  // edits show without a manual reload. View-aware: Cards refetches cardRows,
  // Table refetches cardData (silent, no spinner).
  const refreshOpenBucket = () => {
    const key = isSingleCardSelected ? Array.from(visibleCardKeys)[0] : null;
    if (!key) return;
    if (resultsView === 'cards') {
      fetchCardRows(key, cardExtras.additionalFields);
    } else {
      const cfg = cardConfigs.find((c) => c.key === key);
      if (cfg) {
        fetchCardData(key, cfg.filterConditions || [], cfg.filterView || 'Orders', cfg.bucket, true, cfg.additionalFields);
      }
    }
  };

  // Clear All Leg Appt Dates — fetch the staged-clear preview for the open drill's
  // currently visible (filter-respecting) pros and open the confirm modal. The actual
  // clear runs on confirm (confirmClearApptDates). Templates can't be cleared.
  const openClearApptDates = async (prosArg?: string[]) => {
    const pros = (prosArg ?? collectVisibleDrillPros()).filter((p) => p && !p.toUpperCase().startsWith('T'));
    if (pros.length === 0) {
      showToast({ type: 'info', message: 'No pros in the current view to clear.' });
      return;
    }
    setClearApptBusy(true);
    try {
      const preview = await ordersService.clearApptDatesPreview(pros);
      const hasClears = preview.rows.some((r) => r.clears.length > 0);
      const hasSkips = preview.rows.some(
        (r) => r.clears.length === 0 && (r.skippedLegnums.length > 0 || !!r.error),
      );
      if (!hasClears && !hasSkips) {
        showToast({ type: 'info', message: 'No appointment dates to clear in the current view.' });
        return;
      }
      setClearApptPreview(preview);
    } catch {
      showToast({ type: 'error', message: 'Could not load the appointment-date clear preview.' });
    } finally {
      setClearApptBusy(false);
    }
  };

  // Confirm from the modal: mass-clear the previewed pros (a service failure is created
  // silently server-side for any pro whose delivery appt is removed), toast the summary,
  // and refresh the bucket.
  const confirmClearApptDates = async () => {
    if (!clearApptPreview) return;
    const pros = clearApptPreview.rows.filter((r) => r.clears.length > 0).map((r) => r.pronumber);
    if (pros.length === 0) {
      setClearApptPreview(null);
      return;
    }
    const submitNonce =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `cad-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setClearApptBusy(true);
    try {
      const res = await ordersService.clearApptDatesApply({ pros, submitNonce });
      const results = res.results || [];
      const cleared = results.filter((r) => r.applied);
      const failed = results.filter((r) => !r.applied && !r.skipped);
      if (failed.length > 0) {
        showToast({
          type: 'error',
          message: `Cleared ${cleared.length}; ${failed.length} failed. Check order history.`,
        });
      } else {
        showToast({
          type: 'success',
          message: `Cleared appt dates on ${cleared.length} pro${cleared.length === 1 ? '' : 's'}.`,
        });
      }
      setClearApptPreview(null);
      refreshOpenBucket();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      showToast({
        type: 'error',
        message: typeof detail === 'string' ? detail : 'Failed to clear appointment dates.',
      });
    } finally {
      setClearApptBusy(false);
    }
  };

  // NOTE: Change All / Change Template for All / Trace All / the bucket mass
  // sends / Clear All Leg Appt Dates used to be duplicated as drill-header
  // buttons for the Cards view, because the card list had no toolbar. Both views
  // are TanTables now, so all of them live in the table's action bar
  // (drillBulkActions / bucketExtraActions) and are scoped to that table's
  // currently-filtered rows.

  // Save/Discard for staged inline pickup-number edits — only on the No Pickup Number bucket,
  // only for users with the LC-fields edit gate. Save stays VISIBLE (disabled at zero edits)
  // so the table reads as actionable; Discard appears only when there are pending edits. Each
  // staged pro is saved as its own updateall (history type 'LC Dashboard Edit').
  const renderPickupSaveBar = (bucketKey: string | null) => {
    if (bucketKey !== NO_PICKUP_BUCKET || !canEditOrderLcFields) return null;
    const hasEdits = pickupEditsCount > 0;
    return (
      <>
        {hasEdits && (
          <button
            type="button"
            onClick={discardPickupEdits}
            disabled={savingPickups}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed text-gray-800 dark:text-gray-100 text-xs"
            title="Discard all staged pickup-number edits"
          >
            Discard
          </button>
        )}
        <button
          type="button"
          onClick={savePickupEdits}
          disabled={savingPickups || !hasEdits}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed disabled:opacity-100 text-white text-xs"
          title={hasEdits
            ? "Save staged pickup numbers — each pro saved through saverules with history type 'LC Dashboard Edit'"
            : 'Edit pickup numbers below, then Save'}
        >
          {savingPickups ? 'Saving…' : hasEdits ? `Save ${pickupEditsCount} pickup${pickupEditsCount === 1 ? '' : 's'}` : 'Save'}
        </button>
      </>
    );
  };

  // Bucket-specific mass actions for a table's action bar (both views):
  // Send All Haz Docs (Pending Pre-advise), Send All Rail Bill (Rail Billing),
  // Clear All Leg Appt Dates (Past Due — Not Dispatched). Each runs against the
  // table's own filtered rows, passed in by the action bar.
  const bucketExtraActions = (bucketKey: string | null): BulkAction[] => {
    const extra: BulkAction[] = [];
    if (bucketKey === 'lc-preadvise-pending') {
      extra.push({
        key: 'send-haz',
        label: 'Send All Haz Docs',
        disabled: massSendBusy,
        className: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-orange-600 hover:bg-orange-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm',
        onRun: (rows) => runMassSend('/lc-dashboards/send-all-haz-docs', bucketKey, 'Send All Haz Docs', prosFromRows(rows)),
      });
    }
    if (bucketKey === 'lc-railbill-pending' || bucketKey === 'lc-railbill-ready') {
      extra.push({
        key: 'send-rail',
        label: 'Send All Rail Bill',
        disabled: massSendBusy,
        className: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-indigo-600 hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm',
        onRun: (rows) => runMassSend('/lc-dashboards/send-all-rail-bill', bucketKey, 'Send All Rail Bill', prosFromRows(rows)),
      });
    }
    if (bucketKey === PAST_DUE_NOT_DISPATCHED_BUCKET && canEditOrderLcFields) {
      extra.push({
        key: 'clear-appt',
        label: 'Clear All Leg Appt Dates',
        disabled: clearApptBusy,
        className: 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm',
        onRun: (rows) => openClearApptDates(prosFromRows(rows)),
      });
    }
    return extra;
  };

  // The action-bar descriptor set for a drill/board table: the standard Change
  // All / Change Template for All / Trace All plus this bucket's extras. Every
  // action is scoped to the table's own visible (filtered) rows, so on a
  // workspace of several tables each acts only on its own tile.
  const drillBulkActions = (
    bucketKey: string | null,
    opts?: { trace?: boolean; docsKey?: string; docsTitle?: string },
  ): BulkAction[] =>
    buildProBulkActions({
      canChangeAll: canUseChangeAll,
      canChangeTemplate: canEditOrderLcFields,
      onChangeAll: (pros) => openChangeAll(pros),
      onChangeTemplateAll: (pros) => openChangeTemplateForAll(pros),
      trace: opts?.trace,
      // Documents sits alongside Change All everywhere it appears. It still takes a
      // key rather than being unconditional, because switching repaints the HOST's
      // table — the action is only honest where the host renders that swap.
      onViewDocuments: opts?.docsKey
        ? (pros) => openCardDocuments(opts.docsKey!, pros, opts.docsTitle || '')
        : undefined,
      extra: bucketExtraActions(bucketKey),
    });

  // Cards/Table segmented toggle for an open bucket's results (same pattern as
  // /searchresults). Shown in both the page and drawer result headers. Hidden for
  // table-only drills (Pending Invoicing, Additional Cost).
  const renderViewToggle = () => isTableOnlyDrill ? null : (
    <div className="inline-flex items-center gap-2">
      {/* Trace All (and the other mass actions) live on each table's action bar
          in BOTH views, so there's no duplicate here. */}
      <div className="inline-flex rounded-md overflow-hidden border border-gray-300 dark:border-gray-600 text-xs">
        <button
          type="button"
          onClick={() => setResultsView('cards')}
          className={`inline-flex items-center gap-1 px-2 py-1 ${resultsView === 'cards' ? 'bg-purple-600 text-white' : 'bg-panel text-gray-600 dark:text-gray-300 hover:bg-highlight'}`}
          title="Show each result as a multi-row card"
        >
          <Squares2X2Icon className="h-4 w-4" /> Multi Row View
        </button>
        <button
          type="button"
          onClick={() => setResultsView('table')}
          className={`inline-flex items-center gap-1 px-2 py-1 border-l border-gray-300 dark:border-gray-600 ${resultsView === 'table' ? 'bg-purple-600 text-white' : 'bg-panel text-gray-600 dark:text-gray-300 hover:bg-highlight'}`}
          title="Show each result as a single table row"
        >
          <TableCellsIcon className="h-4 w-4" /> Single Row View
        </button>
      </div>
    </div>
  );

  // Multi Row View's results list: a TanTable in card mode over the PRO card
  // columns. `settingsKey` is `lc-cards-*` — deliberately NOT the `lc-card-*` key
  // Single Row View uses, so the two views keep independent saved layouts.
  // w-full h-full + fillContainer keeps the bounded-height chain intact (an
  // unsized wrapper would make the row virtualizer render EVERY row).
  const renderProCardTable = (opts: {
    rows: ProSearchData[];
    loading: boolean;
    settingsKey: string;
    /** Pool named layouts here instead of per-card — custom cards only. */
    templateScopeKey?: string;
    title: string;
    bulkActions: BulkAction[];
    /** Filter key for this host, when it supports the custom filter (omit for none). */
    filterKey?: string;
    /** Title for the filter dialog — the drill's own name. */
    filterLabel?: string;
  }) => (
    <FieldHighlightProvider>
      <div className="w-full h-full">
        <TanTable<ProSearchData>
          data={opts.rows}
          columns={proCardColumnsWithExtras}
          renderCardRow={renderProCard}
          cardHeaderColumnIds={PRO_CARD_HEADER_COLUMN_IDS}
          trackViewedColumnId="pronumber"
          loading={opts.loading}
          cardTitle={opts.title}
          settingsKey={opts.settingsKey}
          {...(opts.templateScopeKey ? { templateScopeKey: opts.templateScopeKey } : {})}
          initialSettings={tableSettings[`table_${opts.settingsKey}`]}
          settingsReady={tableSettingsLoaded}
          settingsLoadFailed={tableSettingsLoadFailed}
          onSettingsSaved={handleTableSettingsSaved}
          fieldName={opts.settingsKey}
          defaultColumnVisibility={proCardDefaultVisibility}
          // Columns picker = the Single Row view's field list (see
          // proCardAvailableFields). A "(+)" pick refetches the cards with the
          // field attached; the selection is saved per card-view host.
          availableFields={proCardAvailableFields}
          additionalFields={cardExtras.additionalFields}
          onAdditionalFieldsChange={cardExtras.onAdditionalFieldsChange}
          // The static card columns are id'd for what they SHOW (`returnTo`,
          // `lfd`); the bucket grids below id the same fields `t_destname` /
          // `lastfreedaydate`. Declaring what these ids mean is what lets one
          // saved View cross between the two views of the same bucket.
          fieldAliases={proCardFieldAliases}
          searchPlaceholder="Search orders..."
          enableColumnFilters
          enableExcelExport
          // Group By here too (#33653), so a bucket groups the same way in both
          // views. Card mode renders group rows as standard row markup (only leaf
          // rows become cards), so headers and expanders keep working.
          enableGrouping={true}
          // Mass actions live in this table's action bar (open by default in card
          // mode), each scoped to the cards currently visible after search/filters.
          bulkActions={opts.bulkActions}
          // The same custom filter the Single Row view offers, through the same helper.
          // Both views query the same drill, so the control has to be reachable from
          // whichever one you are in — otherwise flipping to Multi Row is a one-way door
          // out of filtering.
          {...(opts.filterKey
            ? customFilterProps(opts.filterKey, opts.filterLabel || opts.title)
            : {})}
          fillContainer
        />
      </div>
    </FieldHighlightProvider>
  );

  // The open bucket's body: the PRO card table (Multi Row) or the LC TanTable
  // (Single Row). Both fill their container.
  const renderBucketBody = (cardKey: string) => {
    if (resultsView !== 'cards') return renderCardContent(cardKey, true);
    const cfg = cardConfigs.find(c => c.key === cardKey);
    // Multi Row shares the card's documents key, so switching in either view and
    // flipping Multi Row <-> Single Row keeps you on documents for the same card.
    const docs = renderDocumentsFor(cardKey);
    if (docs) return docs;
    return renderProCardTable({
      rows: cardRows,
      loading: cardRowsLoading,
      settingsKey: `lc-cards-${cardKey}`,
      templateScopeKey: customCardScopeKey(cardKey),
      title: cfg?.title || '',
      bulkActions: drillBulkActions(cfg?.bucket || cardKey, {
        docsKey: cardKey, docsTitle: cfg?.title || '',
      }),
      // Same key the Single Row table uses, so a filter set in either view IS the
      // filter in the other — not a second, parallel one.
      ...(systemBucketDefaults.has(cardKey)
        ? { filterKey: cardKey, filterLabel: cfg?.title || '' }
        : {}),
    });
  };

  // An open grid cell's body: the same Cards (ProResultCard) / Table (LC TanTable)
  // views the bucket drill-down uses, driven by the grid-cell drill data. Shared by
  // the Delivery Schedule and Tracking cells (both return LC columns).
  const renderGridDrillBody = () => {
    // One documents key per drill KIND (not per row set): a drill is opened from a
    // grid cell and only one is open at a time, so the kind is a stable identity.
    const drillDocsKey = `drill-${gridSel?.kind ?? ''}`;
    const drillDocsTitle = gridSel?.title || '';
    const drillDocs = renderDocumentsFor(drillDocsKey);
    if (drillDocs) return drillDocs;

    // Pending Invoicing drills to an invoice-level table (its own flat column set),
    // table-only regardless of the Cards/Table toggle.
    if (gridSel?.kind === 'pending-invoicing') {
      return (
        <div className="w-full h-full">
          <TanTable
            data={gridDrillTable}
            columns={[proNumberColumn, ...PINV_INVOICE_COLUMNS, ...buildDrillExtraColumns(pinvExtraFields)]}
            trackViewedColumnId="pronumber"
            // Report in-grid-filtered rows so Change All / Change Template act on the
            // visible pro set (distinct-ed downstream — invoices are multi-row per pro).
            onFilteredRowsChange={handleDrillFilteredRows}
            // Change All / Change Template for All in the action bar, scoped to
            // this invoice drill's visible pros. No Trace All (invoice rows).
            bulkActions={drillBulkActions(null, { trace: false, docsKey: drillDocsKey, docsTitle: drillDocsTitle })}
            loading={gridDrillTableLoading}
            cardTitle={gridSel.title}
            settingsKey={`lc-card-${PINV_DRILL_KEY}`}
            initialSettings={tableSettings[`table_lc-card-${PINV_DRILL_KEY}`]}
            settingsReady={tableSettingsLoaded}
            settingsLoadFailed={tableSettingsLoadFailed}
            onSettingsSaved={handleTableSettingsSaved}
            searchPlaceholder="Search invoices..."
            fieldName={PINV_DRILL_KEY}
            enableColumnFilters={true}
            enableExcelExport={true}
            availableFields={pinvAvailableFields}
            additionalFields={pinvExtraFields}
            onAdditionalFieldsChange={setPinvExtraFields}
            enableGrouping={true}
            // These rows are invoices, not orders, but the filter is an orders filter —
            // "the invoices whose order matches" — which is how V1 behaved and how the
            // clerks think about it.
            {...customFilterProps(gridFilterKey('pending-invoicing'), gridSel.title)}
            fillContainer={true}
          />
        </div>
      );
    }
    // Additional Cost drills to an additional-cost-level table (its own flat column set),
    // table-only regardless of the Cards/Table toggle.
    if (gridSel?.kind === 'additional-cost') {
      return (
        <div className="w-full h-full">
          <TanTable
            data={gridDrillTable}
            columns={[...addCostDrillColumns, ...buildDrillExtraColumns(addcostExtraFields)]}
            trackViewedColumnId="pronumber"
            // Report in-grid-filtered rows so Change All / Change Template act on the
            // visible pro set (distinct-ed downstream — addl-cost is multi-row per pro).
            onFilteredRowsChange={handleDrillFilteredRows}
            // Change All / Change Template for All in the action bar, scoped to
            // this additional-cost drill's visible pros. No Trace All (cost rows).
            bulkActions={drillBulkActions(null, { trace: false, docsKey: drillDocsKey, docsTitle: drillDocsTitle })}
            loading={gridDrillTableLoading}
            cardTitle={gridSel.title}
            settingsKey={`lc-card-${ADDCOST_DRILL_KEY}`}
            initialSettings={tableSettings[`table_lc-card-${ADDCOST_DRILL_KEY}`]}
            settingsReady={tableSettingsLoaded}
            settingsLoadFailed={tableSettingsLoadFailed}
            onSettingsSaved={handleTableSettingsSaved}
            searchPlaceholder="Search additional costs..."
            fieldName={ADDCOST_DRILL_KEY}
            enableColumnFilters={true}
            enableExcelExport={true}
            availableFields={addcostAvailableFields}
            additionalFields={addcostExtraFields}
            onAdditionalFieldsChange={setAddcostExtraFields}
            enableGrouping={true}
            // One row per additional-cost line, filtered by the order it belongs to. The
            // subquery resolves against ordersall here: a cost record routinely outlives
            // its order in the current tables.
            {...customFilterProps(gridFilterKey('additional-cost'), gridSel.title)}
            fillContainer={true}
          />
        </div>
      );
    }
    // Chassis cells use the bespoke custom column set; every other grid cell uses the
    // shared LC drill columns. The settings key AND the added-field list are keyed per
    // panel kind so each drill (Per Diem, Schedule, Tracking, …) is customized
    // independently even though they render the same base column set.
    const isChassis = gridSel?.kind === 'chassis';
    const kind = gridSel?.kind ?? '';
    const drillKey = isChassis ? CHASSIS_DRILL_KEY : `${SCHEDULE_DRILL_KEY}-${kind}`;
    const extraForKind = gridDrillExtraByKind[kind] || [];
    return (
      resultsView === 'cards'
        ? renderProCardTable({
            rows: gridDrillRows,
            loading: gridDrillRowsLoading,
            settingsKey: `lc-cards-${drillKey}`,
            title: gridSel?.title || '',
            // Trace All stays on for the chassis drill here: unlike the chassis
            // TABLE (chassis-level rows), the cards endpoint returns PRO rows.
            bulkActions: drillBulkActions(null, { docsKey: drillDocsKey, docsTitle: drillDocsTitle }),
            filterKey: gridFilterKey(kind),
            filterLabel: gridSel?.title || '',
          })
        : (
          <div className="w-full h-full">
            <TanTable
              data={gridDrillTable}
              columns={isChassis ? generateChassisColumns() : generateBucketColumns(
                SCHEDULE_DRILL_KEY,
                extraForKind,
                // SCAC / Terminal are addable columns here too, so a traced row must
                // refresh the same way it does on a bucket table. The drill has no
                // standalone fetch — bumping its nonce re-runs the effect that owns it.
                () => setGridDrillNonce((n) => n + 1),
              )}
              trackViewedColumnId="pronumber"
              // Report in-grid-filtered rows so Change All / Change Template act on the
              // visible pro set (Per Diem, Schedule, Tracking, Exports, Chassis, Melrose).
              onFilteredRowsChange={handleDrillFilteredRows}
              loading={gridDrillTableLoading}
              settingsKey={`lc-card-${drillKey}`}
              initialSettings={tableSettings[`table_lc-card-${drillKey}`]}
              settingsReady={tableSettingsLoaded}
              settingsLoadFailed={tableSettingsLoadFailed}
              onSettingsSaved={handleTableSettingsSaved}
              searchPlaceholder="Search orders..."
              fieldName={drillKey}
              enableColumnFilters={true}
              availableFields={isChassis ? [] : availableFields}
              additionalFields={isChassis ? undefined : extraForKind}
              onAdditionalFieldsChange={isChassis ? undefined : (fields) => setGridDrillExtraByKind(prev => ({ ...prev, [kind]: fields }))}
              // Group By on every grid drill (#33653) — Per Diem, Schedule, Tracking,
              // Exports, Chassis and Melrose all share this table, and it was the only
              // drill host that left grouping off (Pending Invoicing and Additional
              // Cost already group). The toolbar picker lists the visible columns, and
              // TanTable persists the choice under this drill's own settingsKey, so
              // each panel's grouping is remembered independently.
              enableGrouping={true}
              // Mass actions in the action bar (Change All / Change Template for
              // All, plus Trace All for the PRO-based grid drills). Chassis rows
              // aren't PROs, so Trace All is skipped there.
              bulkActions={drillBulkActions(null, { trace: !isChassis, docsKey: drillDocsKey, docsTitle: drillDocsTitle })}
              // Custom filter on every panel drill, keyed per panel kind (see
              // gridFilterKey). Chassis included: its bespoke table takes the filter as a
              // pronumber subquery on the candidate query, same as its Cards view.
              {...customFilterProps(gridFilterKey(kind), gridSel?.title || '')}
              fillContainer={true}
            />
          </div>
        )
    );
  };

  // Show loading indicator while configs or field definitions are being loaded
  // Also wait if field definitions are empty (still loading or need refetch)
  if (!configsLoaded || fieldDefinitionsLoading || (fieldDefinitions.length === 0 && !fieldDefinitionsError)) {
    const mins = Math.floor(dashboardLoadSeconds / 60);
    const secs = dashboardLoadSeconds % 60;
    const timeStr = `${mins}:${secs.toString().padStart(2, '0')}`;
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 dark:border-blue-400 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard... {timeStr}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${styles.pageContainer} ${showGridBorders ? styles.pageContainerBorders : ''} ${showGridHighlight ? styles.pageContainerHighlight : ''} ${showGridLabels ? styles.pageContainerLabels : ''}`}>
      {/* Tab Bar */}
      <div className={`${styles.tabBar} min-w-0 sticky top-0 z-20 bg-white dark:bg-gray-800 shadow-sm`} style={{ position: 'relative' }}>
        <GridLabel areaName="tabBar" position="top-right" />
        {/* Workspace Bar */}
        <div className="flex items-center min-w-0">
          {/* min-w-0: without it this flex item sizes to the tab strip's content
              and the whole page scrolls sideways instead of the strip itself. */}
          <div className="flex-1 min-w-0">
            <TabBar
              tabs={workspaceTabs}
              onTabChange={(tabId) => setCurrentWorkspaceId(tabId)}
              onAddTab={handleCreateWorkspace}
              onEditTab={handleRenameWorkspace}
              onDeleteTab={handleDeleteWorkspace}
              onReorderTabs={handleReorderWorkspaces}
              maxTabs={Infinity}
              protectedTabIds={['default', 'exports', 'accounting', ...workspaces.filter(w => w.required).map(w => w.id)]}
              editable={true}
              noBorder
              compact
            />
          </div>
          {/* Collapse/Expand All Buttons & Global Controls */}
          <div className="flex items-center gap-1 text-xs px-4 py-1 bg-panel border-b border-gray-300 dark:border-gray-600 self-stretch">
            <PermissionsContainer componentId="lc-manage-required-workspaces" componentName="Manage Required LC Workspaces" defaultHidden>
              <button disabled={manageWorkspacesAccess < 2} onClick={() => setShowManagedWorkspaces(true)} className="px-2 py-1 rounded border">Required Workspaces</button>
            </PermissionsContainer>
            {canCreateOrder && (
              <button
                onClick={openCreateOrder}
                className="flex items-center gap-1 px-2 py-1 mr-1 text-white bg-blue-600 hover:bg-blue-700 rounded font-medium"
                title="Create New Order"
              >
                <PlusIcon className="h-4 w-4" />
                New Order
              </button>
            )}
            <button
              onClick={() => { setAllPanelsCollapsed(true); collapseAllCards(); }}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Collapse All"
            >
              <ChevronUpIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => { setAllPanelsCollapsed(false); expandAllCards(); }}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Expand All"
            >
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            <button
              onClick={handleOpenTrash}
              className="p-1 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
              title="Trashcan - View deleted workspaces and cards"
            >
              <TrashIcon className="h-4 w-4" />
            </button>

            {/* Global Table Controls */}
            <div className="relative" ref={globalControlsRef}>
              <button
                onClick={(e) => {
                  if (!showGlobalControls) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setGlobalControlsPosition({
                      top: rect.bottom + 8,
                      right: window.innerWidth - rect.right,
                    });
                  }
                  setShowGlobalControls(!showGlobalControls);
                }}
                className={`p-1 rounded transition-colors ${showGlobalControls
                  ? 'bg-blue-600 text-white'
                  : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                  }`}
                title="Global Table Controls"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4" />
              </button>

              {showGlobalControls && globalControlsPosition && createPortal(
                <div
                  ref={globalControlsDropdownRef}
                  className="fixed w-64 bg-theme-bg-elevated border border-theme-border-primary rounded-lg shadow-lg z-[9999]"
                  style={{ top: globalControlsPosition.top, right: globalControlsPosition.right }}
                >
                  <div className="px-4 py-2 border-b border-theme-border-primary">
                    <p className="text-sm font-semibold text-heading">Global Table Controls</p>
                    <p className="text-xs text-muted">Apply to all tables</p>
                  </div>

                  {/* Reset All Tables */}
                  <div className="p-2 border-b border-theme-border-primary">
                    <button
                      onClick={() => {
                        setGlobalResetTrigger(prev => prev + 1);
                        setShowGlobalControls(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Reset All Tables
                    </button>
                    <button
                      onClick={() => {
                        setShowGlobalControls(false);
                        setShowResetLayoutConfirm(true);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Reset Grid Layout
                    </button>
                    <button
                      onClick={() => {
                        setShowCopyPanelsModal(true);
                        loadCopySources();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <UserGroupIcon className="h-4 w-4" />
                      Copy Panel Layout From…
                    </button>
                    {/* Only offered when there is actually a displaced layout to put
                        back — a reset or a copy puts one there. */}
                    {restorableBatch && (
                      <button
                        onClick={handleRestorePanelLayout}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                      >
                        <ArrowUturnLeftIcon className="h-4 w-4" />
                        {restorePanelLabel}
                      </button>
                    )}
                  </div>

                  {/* Show/Hide Filters */}
                  <div className="p-2 border-b border-theme-border-primary">
                    <p className="px-3 py-1 text-xs font-semibold text-muted uppercase">Column Filters</p>
                    <div className="flex gap-1 px-3 py-1">
                      <button
                        onClick={() => {
                          setGlobalShowFilters(true);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${globalShowFilters === true
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                          }`}
                      >
                        <FunnelIcon className="h-3 w-3" />
                        Show
                      </button>
                      <button
                        onClick={() => {
                          setGlobalShowFilters(false);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${globalShowFilters === false
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                          }`}
                      >
                        <FunnelIcon className="h-3 w-3" />
                        Hide
                      </button>
                    </div>
                  </div>

                  {/* Super Compact */}
                  <div className="p-2 border-b border-theme-border-primary">
                    <p className="px-3 py-1 text-xs font-semibold text-muted uppercase">Super Compact</p>
                    <p className="px-3 text-xs text-gray-400 dark:text-gray-500 mb-1">Click again to reset to individual</p>
                    <div className="flex gap-1 px-3 py-1">
                      <button
                        onClick={() => {
                          setGlobalSuperCompact(globalSuperCompact === true ? undefined : true);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${globalSuperCompact === true
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                          }`}
                      >
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="4" y1="6" x2="20" y2="6" />
                          <line x1="4" y1="10" x2="20" y2="10" />
                          <line x1="4" y1="14" x2="20" y2="14" />
                          <line x1="4" y1="18" x2="20" y2="18" />
                        </svg>
                        All On
                      </button>
                      <button
                        onClick={() => {
                          setGlobalSuperCompact(globalSuperCompact === false ? undefined : false);
                          setShowGlobalControls(false);
                        }}
                        className={`flex items-center gap-1 px-3 py-1 text-xs font-medium rounded transition-colors ${globalSuperCompact === false
                          ? 'bg-blue-600 text-white'
                          : 'bg-theme-bg-secondary text-label hover:bg-theme-bg-tertiary'
                          }`}
                      >
                        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <line x1="4" y1="5" x2="20" y2="5" />
                          <line x1="4" y1="12" x2="20" y2="12" />
                          <line x1="4" y1="19" x2="20" y2="19" />
                        </svg>
                        All Off
                      </button>
                    </div>
                  </div>

                  {/* Reset Layout */}
                  <div className="p-2">
                    <button
                      onClick={() => {
                        // Reset all cards to single column layout (w=12, h=2)
                        if (gridInstanceRef.current) {
                          const grid = gridInstanceRef.current;
                          const items = grid.getGridItems();
                          items.forEach((el, index) => {
                            grid.update(el, { x: 0, y: index * 2, w: 12, h: 2 });
                          });
                        }
                        setShowGlobalControls(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-left rounded hover:bg-theme-bg-tertiary text-label"
                    >
                      <ArrowPathIcon className="h-4 w-4" />
                      Reset Layout (Single Column)
                    </button>
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>
        </div>

      </div>

      {currentWorkspaceId.startsWith('managed:') && <p className="px-4 py-2 text-sm bg-panel border-b">Required workspace — managed by your manager. Cards use the filters at the top of the page together with their saved card filters.</p>}
      {/* Filter Bar - Shown on all workspaces so users always see active filters */}
      <FilterBar label="Filters:" className={styles.filterBar}>
        <GridLabel areaName="filterBar" position="top-right" />
        <MultiSelect
          options={lcOptions}
          value={selectedLCs}
          onChange={setSelectedLCs}
          placeholder="All LCs"
          showClearOption={true}
          clearOptionLabel="All LCs"
          enableSearch={true}
          searchPlaceholder="Search LCs..."
          size="sm"
          className="min-w-[180px]"
          preserveOrder={true}
        />
        <MultiSelect
          options={customerOptions}
          value={selectedCustomers}
          onChange={setSelectedCustomers}
          placeholder="All Customers"
          showClearOption={true}
          clearOptionLabel="All Customers"
          enableSearch={true}
          searchPlaceholder="Search customers..."
          size="sm"
          className="min-w-[200px]"
        />
        <MultiSelect
          options={billtoOptions}
          value={selectedBilltos}
          onChange={setSelectedBilltos}
          placeholder="All Billing Parties"
          showClearOption={true}
          clearOptionLabel="All Billing Parties"
          enableSearch={true}
          searchPlaceholder="Search billing parties..."
          size="sm"
          className="min-w-[200px]"
        />
        <MultiSelect
          options={keyAccountOptions}
          value={selectedKeyAccounts}
          onChange={setSelectedKeyAccounts}
          placeholder="All Key Accounts"
          showClearOption={true}
          clearOptionLabel="All Key Accounts"
          enableSearch={true}
          searchPlaceholder="Search key accounts..."
          size="sm"
          className="min-w-[180px]"
        />
        <MultiSelect
          options={dispatchRegionOptions}
          value={selectedDispatchRegions}
          onChange={setSelectedDispatchRegions}
          placeholder="All Dispatch Regions"
          showClearOption={true}
          clearOptionLabel="All Dispatch Regions"
          enableSearch={true}
          searchPlaceholder="Search dispatch regions..."
          size="sm"
          className="min-w-[180px]"
        />
        <MultiSelect
          options={warehouseOptions}
          value={selectedWarehouses}
          onChange={setSelectedWarehouses}
          placeholder="All Warehouses"
          showClearOption={true}
          clearOptionLabel="All Warehouses"
          enableSearch={true}
          searchPlaceholder="Search warehouses..."
          size="sm"
          className="min-w-[180px]"
        />
        {!currentWorkspaceId.startsWith('managed:') && <div className="flex items-center gap-2">
          <span className="text-sm text-muted">Show hidden</span>
          <Toggle checked={showHidden} onChange={checked => setShowHiddenWorkspaces(prev => ({ ...prev, [currentWorkspaceId]: checked }))} size="sm" ariaLabel="Show hidden cards" />
        </div>}
        {(selectedLCs.length > 0 || selectedCustomers.length > 0 || selectedBilltos.length > 0 || selectedKeyAccounts.length > 0 || selectedDispatchRegions.length > 0 || selectedWarehouses.length > 0) && (
          <button
            onClick={() => {
              setSelectedLCs([]);
              setSelectedCustomers([]);
              setSelectedBilltos([]);
              setSelectedKeyAccounts([]);
              setSelectedDispatchRegions([]);
              setSelectedWarehouses([]);
            }}
            className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
          >
            Clear Filters
          </button>
        )}
        <FilterBar.Group label="Drawer view">
          <Toggle
            checked={drawerView}
            onChange={(v) => {
              // Clear the open bucket/grid cell when switching modes so we don't
              // strand a full-screen view or pop a drawer unexpectedly.
              setVisibleCardKeys(new Set());
              setGridSel(null);
              setDrawerView(v);
            }}
            ariaLabel="Drawer view"
            size="sm"
          />
        </FilterBar.Group>
      </FilterBar>

      {/* Body */}
      <div className={`${styles.body} p-2 ${!drawerView && (isSingleCardSelected || gridSel || ediSelection) ? 'flex flex-col' : ''}`} style={{ position: 'relative' }}>
        <GridLabel areaName="body" position="top-right" />
        {/* Counts view. In page mode it shows only when no bucket is selected (the
            selected bucket swaps to full screen below). In drawer mode the counts
            stay put and the selected bucket opens in a right-side drawer instead. */}
        {/* Kept mounted whenever the panels are visible; only hidden via CSS while a
            grid cell is open in page view. Unmounting it here (instead of hiding)
            would orphan the GridStack instance — its init effect doesn't depend on
            gridSel — leaving tiles unpositioned when the grid remounts. */}
        {panelsVisible && (
          <div
            className={`grid-stack ${(gridSel || ediSelection) && !drawerView ? 'hidden' : ''}`}
            ref={panelGridRef}
            key={`panels-${currentWorkspaceId}`}
          >
            {currentPanelItems.map((item) => {
              const meta = LC_PANELS.find((p) => p.key === item.key);
              if (!meta) return null;
              const countConfig = meta.type === 'counts'
                ? LC_COUNT_PANELS.find((p) => p.key === item.key)
                : undefined;
              if (meta.type === 'counts' && !countConfig) return null;
              return (
                <div
                  key={item.key}
                  className={`grid-stack-item ${item.collapsed ? 'gs-collapsed' : ''}`}
                  gs-id={item.key}
                  gs-x={item.x}
                  gs-y={item.y}
                  gs-w={item.w}
                  gs-h={item.h}
                >
                  <div
                    className="grid-stack-item-content"
                    // The Invoicing Statistics tile's permission cog floats at the card's
                    // top-right corner; allow overflow so the badge isn't clipped (GridStack
                    // defaults this box to overflow-x:hidden / overflow-y:auto).
                    style={meta.type === 'invoicing-stats' ? { overflow: 'visible' } : undefined}
                  >
                    {(() => {
                      // A collapsed count panel hides its buckets, so its header carries the
                      // roll-up count + the worst severity inside. summarizeCountPanel returns
                      // null when this panel's counts aren't loaded (collapsed panels skip their
                      // query), and the header then shows the bare title rather than a false 0.
                      // Melrose builds its rows from fetched data, so an empty result leaves the
                      // counts map empty — its data flag distinguishes "none" from "not fetched".
                      const loadedZero: CountPanelSummary = { total: 0, severity: null };
                      const summary: CountPanelSummary | null =
                        meta.type === 'counts' ? summarizeCountPanel(countConfig!, bucketCounts)
                          : meta.type === 'tickets' ? summarizeCountPanel(TICKETS_CONFIG, ticketCounts)
                            : meta.type === 'melrose' ? (melroseData ? summarizeCountPanel(melroseConfig, melroseCounts) ?? loadedZero : null)
                              : meta.type === 'whsephotos' ? summarizeCountPanel(whsePhotoConfig, whsePhotoCounts)
                                : meta.type === 'edi-buckets' ? (filteredEdiBuckets.length ? { total: filteredEdiBuckets.reduce((s, b) => s + b.count, 0), severity: (worstUrgency(filteredEdiBuckets.map((b) => b.urgency)) || null) as CountSeverity | null } : null)
                                  : null;
                      const tile = (
                        <DashboardTile
                          title={meta.title}
                          collapsed={!!item.collapsed}
                          collapsedCount={summary ? summary.total : null}
                          collapsedTone={summary ? summary.severity : null}
                          onToggleCollapse={() => togglePanelCollapse(item.key)}
                          locked={!!item.locked}
                          onToggleLock={() => togglePanelLock(item.key)}
                        >
                          {meta.type === 'schedule' ? (
                            <SchedulePanel
                              data={scheduleData}
                              loading={scheduleLoading}
                              onSelectCell={openScheduleCell}
                            />
                          ) : meta.type === 'tracking' ? (
                            <TrackingPanel
                              data={trackingData}
                              loading={trackingLoading}
                              onSelectCell={openTrackingCell}
                            />
                          ) : meta.type === 'exports-summary' ? (
                            <ExportsSummaryPanel
                              data={exportsSummaryData}
                              loading={exportsSummaryLoading}
                              onSelectCell={openExportsSummaryCell}
                            />
                          ) : meta.type === 'exports-board' ? (
                            <ExportsBoardPanel
                              data={exportsBoardData}
                              loading={exportsBoardLoading}
                              groupBy={exportsBoardGroupBy}
                              onGroupByChange={setExportsBoardGroupBy}
                              onDrill={openExportsBoardDrill}
                            />
                          ) : meta.type === 'invoicing-stats' ? (
                            // Gating + the config cog live on the whole tile (wrapped
                            // below); the body itself just renders the stats panel.
                            <InvoicingStatsPanel
                              data={invStatsData}
                              loading={invStatsLoading}
                            />
                          ) : meta.type === 'per-diem' ? (
                            <PerDiemPanel
                              data={perDiemData}
                              loading={perDiemLoading}
                              onSelect={handlePerDiemSelect}
                            />
                          ) : meta.type === 'additional-cost' ? (
                            <AdditionalCostPanel
                              data={addCostData}
                              loading={addCostLoading}
                              onSelect={handleAdditionalCostSelect}
                            />
                          ) : meta.type === 'tickets' ? (
                            <CountPanel
                              config={TICKETS_CONFIG}
                              counts={ticketCounts}
                              loading={ticketLoading}
                              onSelect={handleTicketSelect}
                              embedded
                            />
                          ) : meta.type === 'chassis' ? (
                            <ChassisChargesPanel
                              data={chassisData}
                              loading={chassisLoading}
                              onSelectCell={openChassisCell}
                            />
                          ) : meta.type === 'melrose' ? (
                            <CountPanel
                              config={melroseConfig}
                              counts={melroseCounts}
                              loading={melroseLoading}
                              onSelect={handleMelroseSelect}
                              embedded
                            />
                          ) : meta.type === 'whsephotos' ? (
                            <CountPanel
                              config={whsePhotoConfig}
                              counts={whsePhotoCounts}
                              loading={whsePhotoLoading}
                              onSelect={handleWhsePhotoSelect}
                              embedded
                            />
                          ) : meta.type === 'edi-buckets' ? (
                            <EdiBucketPanel
                              embedded
                              buckets={filteredEdiBuckets}
                              loading={ediLoading}
                              onSelect={(edihandle, billto) => {
                                const b = filteredEdiBuckets.find((x) => x.edihandle === edihandle && x.billto === billto);
                                // One drill at a time — clear any open count/card drill.
                                setGridSel(null);
                                setVisibleCardKeys(new Set());
                                setEdiSelection({ edihandle, billto, label: b ? `${b.billtoname} — ${edihandle}` : edihandle });
                              }}
                              selectedKey={ediSelection ? `${ediSelection.edihandle}|${ediSelection.billto}` : null}
                            />
                          ) : (
                            <CountPanel
                              config={countConfig!}
                              counts={bucketCounts}
                              loading={
                                countConfig!.key === WAREHOUSING_PANEL_KEY ? warehouseCountsLoading
                                  : countConfig!.key === TABLEMAINT_PANEL_KEY ? tablemaintLoading
                                    : countConfig!.key === PINV_PANEL_KEY ? pinvCountsLoading
                                      : bucketCountsLoading
                              }
                              onSelect={handleCountSelect}
                              embedded
                            />
                          )}
                        </DashboardTile>
                      );
                      // Gate the WHOLE tile (not just the inner panel) for the
                      // permission-gated Invoicing Statistics card so the config cog
                      // renders on the card frame's top-right corner rather than
                      // inside the panel body.
                      return meta.type === 'invoicing-stats' ? (
                        <PermissionsContainer
                          componentId={INVSTATS_PERMISSION_ID}
                          componentName={INVSTATS_PERMISSION_NAME}
                          defaultHidden
                          className="h-full w-full"
                        >
                          {tile}
                        </PermissionsContainer>
                      ) : tile;
                    })()}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Single Card Full Screen Mode (page view only) */}
        {!drawerView && isSingleCardSelected && (() => {
          const selectedKey = Array.from(visibleCardKeys)[0];
          const cardConfig = cardConfigs.find(c => c.key === selectedKey);
          if (!cardConfig) return null;
          return (
            <div key={selectedKey} className="flex-1 min-h-0 flex flex-col">
              {/* Back bar — return from this bucket's results to the counts view,
                  plus the Cards/Table toggle for the open bucket. */}
              <div className="flex-shrink-0 mb-2 flex items-center">
                <button
                  type="button"
                  onClick={() => setVisibleCardKeys(new Set())}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                  title="Back to dashboard"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                  Back to dashboard
                </button>
                <span className="mx-2 text-gray-300 dark:text-gray-600">/</span>
                <span className="text-sm font-semibold text-body">{cardConfig.title}</span>
                <div className="ml-auto flex items-center gap-2">{renderPickupSaveBar(selectedKey)}{renderViewToggle()}</div>
              </div>
              <PermissionConfigurable
                componentId={`lc-${selectedKey}`}
                componentName={cardConfig.title}
                className="flex-1 flex flex-col min-h-0"
              >
                <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-600 overflow-hidden flex flex-col">
                  {renderBucketBody(selectedKey)}
                </div>
              </PermissionConfigurable>
            </div>
          );
        })()}

        {/* Grid cell drill-down (Delivery Schedule / Tracking) — full screen (page
            view). Same back bar + Cards/Table toggle as the bucket drill-down. */}
        {!drawerView && gridSel && (
          <div className="flex-1 min-h-0 flex flex-col">
            <div className="flex-shrink-0 mb-2 flex items-center">
              <button
                type="button"
                onClick={() => setGridSel(null)}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                title="Back to dashboard"
              >
                <ArrowLeftIcon className="h-4 w-4" />
                Back to dashboard
              </button>
              <span className="mx-2 text-gray-300 dark:text-gray-600">/</span>
              <span className="text-sm font-semibold text-body">{gridSel.title}</span>
              <div className="ml-auto flex items-center gap-2">{renderViewToggle()}</div>
            </div>
            <div className="flex-1 min-h-0 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-600 overflow-hidden flex flex-col">
              {renderGridDrillBody()}
            </div>
          </div>
        )}

        {/* Pending EDI Orders drill — page view. Full-screen swap like the bucket/grid
            drill-downs. The back bar goes IN as the drill's `header` so the drill's own
            view toggle sits on the breadcrumb line (the other drills' toggles do too),
            and fillContainer sizes the table off this box instead of a fixed
            h-[calc(100vh-280px)] — that calc is what made the box scroll vertically once
            page chrome or the font size grew. */}
        {!drawerView && ediSelection && (
          <div className="flex-1 min-h-0 flex flex-col">
            <EdiBucketDrill
              selection={ediSelection}
              onActionApplied={() => fetchEdiBuckets(true)}
              lcUsernames={selectedLCs}
              fillContainer
              header={(
                <>
                  <button
                    type="button"
                    onClick={() => setEdiSelection(null)}
                    className="inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 hover:underline"
                    title="Back to dashboard"
                  >
                    <ArrowLeftIcon className="h-4 w-4" />
                    Back to dashboard
                  </button>
                  <span className="-mx-1 text-gray-300 dark:text-gray-600">/</span>
                  <span className="text-sm font-semibold text-body">{ediSelection.label}</span>
                </>
              )}
            />
          </div>
        )}

        {/* Drawer view — the selected bucket's table slides in from the right while
            the counts stay visible behind. Closing returns to the counts. */}
        {drawerView && (() => {
          const selectedKey = isSingleCardSelected ? Array.from(visibleCardKeys)[0] : null;
          const cardConfig = selectedKey ? cardConfigs.find(c => c.key === selectedKey) : null;
          return (
            <Drawer
              isOpen={isSingleCardSelected && !!cardConfig}
              onClose={() => setVisibleCardKeys(new Set())}
              title={(
                <div className="flex items-center gap-3 pr-2">
                  <span>{cardConfig?.title || ''}</span>
                  <div className="ml-auto flex items-center gap-2">{renderPickupSaveBar(selectedKey)}{renderViewToggle()}</div>
                </div>
              )}
              position="right"
              width="80vw"
              contentClassName="relative flex-1 min-h-0 overflow-hidden"
              // Render as a plain z-[100] portal (not a top-layer <dialog>) so the
              // PRO detail slide-over (also z-[100], mounts later) layers on top when
              // opened from inside the drawer. Also keeps the counts visible behind.
              noBackdrop
            >
              {selectedKey && cardConfig && (
                <div className="h-full min-h-0 flex flex-col bg-gray-50 dark:bg-gray-800">
                  {renderBucketBody(selectedKey)}
                </div>
              )}
            </Drawer>
          );
        })()}

        {/* Grid cell drill-down (Delivery Schedule / Tracking) — drawer view. Slides in
            over the counts, same as the bucket drawer. */}
        {drawerView && (
          <Drawer
            isOpen={!!gridSel}
            onClose={() => setGridSel(null)}
            title={(
              <div className="flex items-center gap-3 pr-2">
                <span>{gridSel?.title || ''}</span>
                <div className="ml-auto flex items-center gap-2">{renderViewToggle()}</div>
              </div>
            )}
            position="right"
            width="80vw"
            contentClassName="relative flex-1 min-h-0 overflow-hidden"
            noBackdrop
          >
            {gridSel && (
              <div className="h-full min-h-0 flex flex-col bg-gray-50 dark:bg-gray-800">
                {renderGridDrillBody()}
              </div>
            )}
          </Drawer>
        )}

        {/* Pending EDI Orders drill — drawer view. noBackdrop (z-[100] portal, not a
            top-layer <dialog>) so the drill's EDI Info / reason / confirm modals layer
            on top instead of hiding underneath. */}
        {drawerView && (
          <Drawer
            isOpen={!!ediSelection}
            onClose={() => setEdiSelection(null)}
            title={ediSelection?.label || 'Pending EDI Orders'}
            position="right"
            width="80vw"
            contentClassName="relative flex-1 min-h-0 overflow-hidden"
            noBackdrop
          >
            {ediSelection && (
              <div className="h-full min-h-0 flex flex-col p-3 bg-gray-50 dark:bg-gray-800">
                <EdiBucketDrill selection={ediSelection} onActionApplied={() => fetchEdiBuckets(true)} lcUsernames={selectedLCs} fillContainer />
              </div>
            )}
          </Drawer>
        )}

        {/* Main Grid Layout - GridStack (hidden via CSS when single card selected to preserve layout) */}
        <div className={`relative ${isSingleCardSelected || isMainBoardUnselected ? 'hidden' : ''}`}>
          <div
            ref={gridRef}
            className="grid-stack"
            key={currentWorkspaceId}
          >
            {currentGridItems.map((item) => {
              const cardConfig = cardConfigs.find(c => c.key === item.id);
              if (!cardConfig) return null;

              return (
                <div
                  key={item.id}
                  className={`grid-stack-item ${collapsed[item.id] ? 'gs-collapsed' : ''}`}
                  gs-id={item.id}
                  gs-x={item.x}
                  gs-y={item.y}
                  gs-w={item.w}
                  gs-h={item.h}
                >
                  <div
                    className="grid-stack-item-content"
                  >
                    {/* h-full/w-full: in permissions mode this wrapper becomes a real div
                        between the bounded grid tile and the h-full card body — without a
                        height it breaks the chain, the table's scroll container grows to
                        content height, and the row virtualizer renders EVERY row (a 35k-row
                        card hard-freezes the tab). */}
                    <PermissionConfigurable
                      componentId={`lc-${item.id}`}
                      componentName={cardConfig.title}
                      className="h-full w-full"
                    >
                      <div className="h-full w-full bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md border border-gray-300 dark:border-gray-600 overflow-hidden">
                        {renderCardContent(item.id)}
                      </div>
                    </PermissionConfigurable>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Saving overlay */}
          {isSaving && (
            <div className="absolute inset-0 bg-gray-500/50 dark:bg-gray-900/50 flex items-center justify-center z-50 rounded-lg">
              <div className="bg-panel px-6 py-4 rounded-lg shadow-lg flex items-center gap-3">
                <svg className="animate-spin h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-gray-700 dark:text-gray-200 font-medium">Saving layout...</span>
              </div>
            </div>
          )}
        </div>

        {/* Empty state when no cards. Suppressed for panel-bearing workspaces (Main,
            Exports): their board is the panel grid, not drill cards, so the
            "create a card" prompt doesn't apply. */}
        {!currentWorkspaceHasPanels && !currentWorkspaceId.startsWith('managed:') && currentGridItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" />
            <h3 className="text-lg font-medium text-heading mb-1">No visible cards in {workspaces.find(w => w.id === currentWorkspaceId)?.name || 'this workspace'}</h3>
            <p className="text-sm text-muted mb-4">
              Add a card or turn on Show hidden to view hidden cards.
            </p>
            <button
              onClick={() => setIsFirstCardModalOpen(true)}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-accent-fg bg-accent rounded-lg shadow-sm hover:bg-accent-hover transition-colors"
            >
              <PlusIcon className="h-5 w-5" />
              Create Card
            </button>
          </div>
        )}
      </div>

      {/* Add Card Modal */}
      {isAddCardModalOpen && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div
            ref={addCardDrag.panelRef}
            style={addCardDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Create New Card"
              onClose={() => {
                setIsAddCardModalOpen(false);
                setNewCardTitle('');
                setNewCardColumn(0);
              }}
              dragHandleProps={addCardDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="cardTitle" className="block text-sm font-medium text-label mb-2">
                  Card Title
                </label>
                <input
                  id="cardTitle"
                  type="text"
                  value={newCardTitle}
                  onChange={(e) => setNewCardTitle(e.target.value)}
                  placeholder="Enter card title"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleCreateCard();
                    }
                  }}
                />
              </div>

              <div>
                <label htmlFor="cardColumn" className="block text-sm font-medium text-label mb-2">
                  Column
                </label>
                <select
                  id="cardColumn"
                  value={newCardColumn}
                  onChange={(e) => setNewCardColumn(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                >
                  {Array.from({ length: columnCount }, (_, i) => (
                    <option key={i} value={i}>
                      Column {i + 1}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Cards in workspace: {getWorkspaceCardCount(currentWorkspaceId)} / {LC_WORKSPACE_CARD_LIMIT}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setIsAddCardModalOpen(false);
                  setNewCardTitle('');
                  setNewCardColumn(0);
                }}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateCard}
                className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover"
              >
                Create Card
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Copy another user's count-panel layout */}
      {showCopyPanelsModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={copyPanelsDrag.panelRef}
            style={copyPanelsDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
          >
            <ModalHeader
              title="Copy Panel Layout From"
              onClose={() => setShowCopyPanelsModal(false)}
              dragHandleProps={copyPanelsDrag.dragHandleProps}
            />

            <div className="p-6 pb-3">
              <p className="text-sm text-muted">
                Replaces your count-panel arrangement across every workspace with theirs.
                Yours is kept — <span className="font-medium">Restore My Panel Layouts</span> puts
                it back. Your cards, filters and table settings are not affected.
              </p>
              {copyableSources.length > 0 && (
                <input
                  type="text"
                  value={copySourceQuery}
                  onChange={(e) => setCopySourceQuery(e.target.value)}
                  placeholder="Filter by name…"
                  className="mt-3 w-full px-3 py-2 text-sm rounded-md bg-theme-bg-secondary border border-theme-border-primary text-label placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              )}
            </div>

            <div className="flex-1 overflow-y-auto px-6 pb-6">
              {copySourcesLoading && (
                <p className="py-6 text-sm text-muted text-center">Loading layouts…</p>
              )}
              {!copySourcesLoading && copySourcesError && (
                <div className="py-6 text-center">
                  <p className="text-sm text-label">Couldn't load the list of layouts.</p>
                  <button
                    onClick={loadCopySources}
                    className="mt-2 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                  >
                    Try again
                  </button>
                </div>
              )}
              {!copySourcesLoading && !copySourcesError && copyableSources.length === 0 && (
                <p className="py-6 text-sm text-muted text-center">
                  Nobody else has arranged their count panels yet.
                </p>
              )}
              {!copySourcesLoading && !copySourcesError
                && copyableSources.length > 0 && visibleCopySources.length === 0 && (
                <p className="py-6 text-sm text-muted text-center">
                  No one matching "{copySourceQuery.trim()}".
                </p>
              )}
              {!copySourcesLoading && !copySourcesError && visibleCopySources.length > 0 && (
                <div className="space-y-2">
                  {visibleCopySources.map((source) => (
                    <div
                      key={source.user_email}
                      className="flex items-center justify-between gap-3 p-3 rounded-lg border border-theme-border-primary"
                    >
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-heading truncate">{source.display_name}</p>
                        <p className="text-xs text-muted truncate">
                          {source.panel_count} panel{source.panel_count === 1 ? '' : 's'} across{' '}
                          {source.workspace_count} workspace{source.workspace_count === 1 ? '' : 's'}
                          {source.updated_at
                            ? ` · updated ${new Date(source.updated_at).toLocaleDateString()}`
                            : ''}
                        </p>
                      </div>
                      <button
                        onClick={() => handleCopyPanelLayout(source)}
                        className="flex-shrink-0 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                      >
                        Use this layout
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reset Layout Confirmation Modal */}
      {showResetLayoutConfirm && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={resetLayoutDrag.panelRef}
            style={resetLayoutDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="Reset Grid Layout"
              onClose={() => setShowResetLayoutConfirm(false)}
              dragHandleProps={resetLayoutDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <ArrowPathIcon className="h-6 w-6 text-amber-600 dark:text-amber-400" />
                </div>
                <p className="text-sm text-muted">
                  This will reset card and count-panel positions
                </p>
              </div>
              <p className="text-sm text-label">
                Are you sure you want to reset the grid layout for{' '}
                <span className="font-semibold text-heading">
                  "{workspaces.find(w => w.id === currentWorkspaceId)?.name || 'this workspace'}"
                </span>
                ? All cards and count panels will return to their default positions.
              </p>
              <p className="mt-2 text-sm text-muted">
                Your count-panel arrangement is kept — use <span className="font-medium">Restore Panel Layout</span> in
                the same menu to put it back. Card positions cannot be restored.
              </p>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowResetLayoutConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleResetLayout}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 dark:bg-amber-600 rounded-lg hover:bg-amber-700 dark:hover:bg-amber-700 transition-colors"
              >
                Reset Layout
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Trashcan Modal */}
      {showTrashModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={trashDrag.panelRef}
            style={trashDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-lg w-full"
          >
            <ModalHeader
              title="Trashcan"
              onClose={() => setShowTrashModal(false)}
              dragHandleProps={trashDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="flex items-center gap-3 px-6 pt-6">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                <TrashIcon className="h-6 w-6 text-gray-600 dark:text-gray-400" />
              </div>
              <p className="text-sm text-muted">
                Deleted items are kept for 30 days
              </p>
            </div>
            <div className="p-6 max-h-96 overflow-y-auto">
              {trashLoading ? (
                <p className="text-sm text-muted text-center py-4">Loading...</p>
              ) : trashItems.length === 0 ? (
                <p className="text-sm text-muted text-center py-4">Trashcan is empty</p>
              ) : (
                <div className="space-y-3">
                  {/* Group by workspace */}
                  {Object.entries(
                    trashItems.reduce((acc: Record<string, { name: string; cards: any[]; date: string }>, item: any) => {
                      if (!acc[item.workspace_id]) {
                        acc[item.workspace_id] = { name: item.workspace_name, cards: [], date: item.date_deleted || '' };
                      }
                      if (item.card_key) {
                        acc[item.workspace_id].cards.push(item);
                      }
                      // Use earliest date_deleted for the workspace
                      if (item.date_deleted && item.date_deleted < acc[item.workspace_id].date) {
                        acc[item.workspace_id].date = item.date_deleted;
                      }
                      return acc;
                    }, {})
                  ).map(([wsId, ws]) => (
                    <div key={wsId} className="border border-gray-200 dark:border-gray-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-heading">{ws.name}</p>
                          <p className="text-xs text-muted">
                            {ws.cards.length} card{ws.cards.length !== 1 ? 's' : ''}
                            {ws.date && ` \u00b7 Deleted ${new Date(ws.date).toLocaleDateString()}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleRestoreWorkspace(wsId)}
                          className="px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-md hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                        >
                          Restore
                        </button>
                      </div>
                      {ws.cards.length > 0 && (
                        <div className="mt-2 pl-3 border-l-2 border-rule space-y-1">
                          {ws.cards.map((card: any) => (
                            <p key={card.card_key} className="text-xs text-gray-600 dark:text-gray-400">
                              {card.card_title || card.card_key}
                            </p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setShowTrashModal(false)}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* New Workspace Modal */}
      {showNewWorkspaceModal && (
        <div className="fixed inset-0 bg-scrim bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
          <div
            ref={newWorkspaceDrag.panelRef}
            style={newWorkspaceDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-md w-full"
          >
            <ModalHeader
              title="New Workspace"
              onClose={() => {
                setShowNewWorkspaceModal(false);
                setNewWorkspaceName('');
              }}
              dragHandleProps={newWorkspaceDrag.dragHandleProps}
            />

            {/* Content */}
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <PlusIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <p className="text-sm text-muted">
                  Create a new workspace to organize your cards
                </p>
              </div>
              <label className="block text-sm font-medium text-label mb-2">
                Workspace Name
              </label>
              <input
                type="text"
                value={newWorkspaceName}
                onChange={(e) => setNewWorkspaceName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    handleConfirmCreateWorkspace();
                  } else if (e.key === 'Escape') {
                    setShowNewWorkspaceModal(false);
                    setNewWorkspaceName('');
                  }
                }}
                placeholder={`Workspace ${workspaces.length + 1}`}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-heading placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                autoFocus
              />
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setShowNewWorkspaceModal(false);
                  setNewWorkspaceName('');
                }}
                className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCreateWorkspace}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Create Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* In-place bucket filter — the system-bucket counterpart to the card-settings
          modal above. Conditions are ANDed onto the bucket's own WHERE and live only as
          long as this page is mounted; see the bucketFilters state for why. */}
      {filterTarget && (
        <BucketFilterModal
          isOpen={true}
          bucketLabel={filterTarget.label}
          initialConditions={bucketFilters[filterTarget.key]}
          onApply={(conditions) => handleApplyBucketFilter(filterTarget.key, conditions)}
          onClose={() => setFilterTarget(null)}
        />
      )}

      {showManagedWorkspaces && <LCManagedWorkspacesModal
        onClose={() => setShowManagedWorkspaces(false)}
        onSaved={async () => {
          const layout = await userSettingsService.getDashboardLayoutNew('lc');
          const requiredWorkspaces: Workspace[] = layout.workspaces.filter((w: Workspace) => w.required);
          const requiredCards: CardConfig[] = layout.cardConfigs.filter((c: CardConfig) => c.required);
          setWorkspaces(prev => [...requiredWorkspaces, ...prev.filter(w => !w.id.startsWith('managed:'))]);
          setCardConfigs(prev => [...requiredCards, ...prev.filter(c => !c.key.startsWith('managed:'))]);
          if (currentWorkspaceId.startsWith('managed:')) setCurrentWorkspaceId('default');
          showToast({ type: 'success', message: 'Required assignments saved. LCs receive updates when they reload their dashboard.' });
        }}
      />}
      {/* First Card Creation Modal */}
      <FirstCardModal
        isOpen={isFirstCardModalOpen}
        dashboardType="lc"
        allowHidden
        initialHidden={isCurrentWorkspaceFull}
        visibleCardLimitReached={isCurrentWorkspaceFull}
        onClose={() => setIsFirstCardModalOpen(false)}
        hideStatButtonSettings={currentWorkspaceId !== 'default'}
        onSubmit={(cardName: string, conditions: FilterCondition[], view: string, bucket?: string, statButtonLabel?: string, statButtonCountField?: string, showStatButton?: boolean, isShared?: boolean, sharedTableSettings?: any, includeClosed?: boolean, hidden?: boolean) => {
          if (!canSaveWorkspaceCard(cardConfigs, currentWorkspaceId, hidden)) return;
          // Generate a unique key for the new card
          const newKey = `card-${Date.now()}`;

          // Get the highest order number in the first column
          const columnCards = cardConfigs.filter(c => c.column === 0 && c.workspaceId === currentWorkspaceId);
          const maxOrder = columnCards.length > 0 ? Math.max(...columnCards.map(c => c.order)) : -1;

          // Calculate GridStack position for new card
          const gridPos = getNextGridPosition(currentWorkspaceId);

          // Create the new card config with GridStack position
          const newCard: CardConfig = {
            key: newKey,
            title: cardName,
            column: 0,
            order: maxOrder + 1,
            visible: true,
            span: 1,
            workspaceId: currentWorkspaceId,
            filterConditions: conditions,
            filterView: view,
            includeClosed: includeClosed,
            hidden: hidden,
            isShared: isShared,
            bucket: bucket,
            showStatButton: showStatButton,
            statButtonLabel: statButtonLabel,
            statButtonCountField: statButtonCountField,
            gsX: gridPos.gsX,
            gsY: gridPos.gsY,
            gsW: gridPos.gsW,
            gsH: gridPos.gsH
          };

          // If shared card included table settings, populate the cache BEFORE mounting the card
          // so TanTable sees initialSettings on first render
          if (sharedTableSettings) {
            const settingsKey = `table_lc-card-${newKey}`;
            handleTableSettingsSaved(settingsKey, sharedTableSettings);
            // Persist to backend in background
            userSettingsService.saveUserSetting(settingsKey, sharedTableSettings).catch(err => {
              console.error('Failed to save shared table settings:', err);
            });
          }

          // Add the card to the config (triggers re-render + TanTable mount)
          const updatedConfigs = [...cardConfigs, newCard];
          setCardConfigs(updatedConfigs);

          // Save the layout immediately so new card persists
          saveLayout(updatedConfigs, true);

          // Fetch data for this card. includeClosed is passed explicitly: cardConfigsRef
          // has not picked up setCardConfigs yet, so the ref lookup inside would miss it.
          if (!hidden || showHidden) fetchCardData(
            newKey, conditions, view, bucket, false, newCard.additionalFields,
            undefined, undefined, includeClosed,
          );

          // Close the modal
          setIsFirstCardModalOpen(false);
        }}
      />

      {/* Edit Card Modal */}
      {editingCardFiltersKey && (() => {
        const editCard = cardConfigs.find(c => c.key === editingCardFiltersKey);
        if (!editCard) return null;

        return (
          <FirstCardModal
            isOpen={true}
            dashboardType="lc"
            onClose={() => setEditingCardFiltersKey(null)}
            initialCardName={editCard.title}
            initialConditions={editCard.filterConditions}
            initialView={editCard.filterView}
            initialBucket={editCard.bucket}
            initialStatButtonLabel={editCard.statButtonLabel}
            initialStatButtonCountField={editCard.statButtonCountField}
            initialShowStatButton={editCard.showStatButton}
            initialIsShared={editCard.isShared}
            allowHidden
            initialHidden={editCard.hidden}
            visibleCardLimitReached={!canSaveWorkspaceCard(cardConfigs, editCard.workspaceId || currentWorkspaceId, false, editCard.key)}
            initialIncludeClosed={editCard.includeClosed}
            hideStatButtonSettings={editCard.workspaceId !== 'default'}
            isEditMode={true}
            onSubmit={(cardName: string, conditions: FilterCondition[], view: string, bucket?: string, statButtonLabel?: string, statButtonCountField?: string, showStatButton?: boolean, isShared?: boolean, sharedTableSettings?: any, includeClosed?: boolean, hidden?: boolean) => {
              if (!canSaveWorkspaceCard(cardConfigs, editCard.workspaceId || currentWorkspaceId, hidden, editCard.key)) return;
              // Update the card configuration (visibleColumns preserved - managed separately on dashboard)
              const updatedConfigs = cardConfigs.map(card => {
                if (card.key === editingCardFiltersKey) {
                  return {
                    ...card,
                    title: cardName,
                    filterConditions: conditions,
                    filterView: view,
                    includeClosed: includeClosed,
                    bucket: bucket,
                    showStatButton: showStatButton,
                    statButtonLabel: statButtonLabel,
                    statButtonCountField: statButtonCountField,
                    isShared: isShared,
                    hidden: hidden
                  };
                }
                return card;
              });
              // IMPORTANT: Update ref BEFORE triggering state changes
              // This ensures fetchAllCardData reads the updated filter conditions
              cardConfigsRef.current = updatedConfigs;

              setCardConfigs(updatedConfigs);

              // Save the layout immediately so edits persist
              saveLayout(updatedConfigs, true);

              // Fetch updated data using batch-cards endpoint (not the old bucket-specific endpoint)
              // Find the updated card config to fetch
              const updatedCard = updatedConfigs.find(c => c.key === editingCardFiltersKey);
              if (updatedCard && (updatedCard.bucket || (updatedCard.filterConditions && updatedCard.filterConditions.length > 0))) {
                // Use batch endpoint for bucket cards (including single card)
                // For non-bucket cards, this will fall back to individual fetch
                fetchAllCardData([updatedCard], false, false);
              }

              // Close the modal
              setEditingCardFiltersKey(null);
            }}
            onDelete={() => {
              // Remove the card from cardConfigs
              const cardKey = editingCardFiltersKey;
              const updatedConfigs = cardConfigs.filter(card => card.key !== cardKey);
              setCardConfigs(updatedConfigs);

              // Save the layout immediately so deletion persists
              saveLayout(updatedConfigs, true);

              // MEMORY OPTIMIZATION: Clear the data for this card to free memory
              if (cardKey) {
                setCardData(prev => {
                  const newData = { ...prev };
                  delete newData[cardKey];
                  return newData;
                });
                // Drop any captured Documents scope too — card keys are timestamps so a
                // stale entry could not be reused, but it pins a PRO array for the rest
                // of the session.
                closeCardDocuments(cardKey);
              }

              // Close the modal
              setEditingCardFiltersKey(null);
            }}
          />
        );
      })()}

      {/* ProDetailsView Modal — single-pro. Suppressed while Change All is active
          so the two drawers never overlap. On close, refresh the open bucket/drill —
          a save inside the drawer (LFD, invoice hold, …) can move the pro out of the
          bucket, and the auto-refresh was paused the whole time it was open. */}
      {selectedProNumber && !changeAllPros && (
        <ProDetailsView
          isOpen={true}
          pronumber={selectedProNumber}
          onClose={() => {
            setSelectedProNumber(null);
            refreshOpenBucket();
            setGridDrillNonce((n) => n + 1);
          }}
        />
      )}

      {/* Change All — the same drawer opened in change-all mode, anchored on the
          first visible pro, fanning checked edits across the bucket's pros. On
          close, refresh the open bucket so the edits show. */}
      {changeAllPros && changeAllPros.length > 0 && (
        <ProDetailsView
          isOpen={true}
          pronumber={changeAllPros[0]}
          changeAll={{ targetPros: changeAllPros }}
          onClose={() => {
            setChangeAllPros(null);
            refreshOpenBucket();
            setGridDrillNonce((n) => n + 1);
          }}
        />
      )}

      {/* Additional Cost record editor — opened from the Additional Cost drill's
          "Invoice #" cell. On save, refresh the open drill table + the panel counts. */}
      {addCostModal.isOpen && (
        <AdditionalCostItemModal
          isOpen={addCostModal.isOpen}
          onClose={() => setAddCostModal({ isOpen: false, pronumber: 0, recno: null })}
          pronumber={addCostModal.pronumber}
          recno={addCostModal.recno}
          onSaved={() => { setGridDrillNonce((n) => n + 1); fetchAdditionalCost(true); }}
        />
      )}

      {/* Duplicate Card Modal */}
      <DuplicateCardModal
        isOpen={duplicateCardConfig !== null}
        onClose={() => setDuplicateCardConfig(null)}
        onSubmit={handleDuplicateCard}
        originalCardName={duplicateCardConfig?.title || ''}
        workspaces={workspaces}
        currentWorkspaceId={currentWorkspaceId}
        getWorkspaceCardCount={getWorkspaceCardCount}
        maxCardsPerWorkspace={LC_WORKSPACE_CARD_LIMIT}
        isWorkspaceProtected={isLcSystemWorkspace}
      />

      {/* Mass-send results (Send All Haz Docs / Send All Rail Bill) — the V1
          summary of which pros sent vs. were skipped and why. */}
      {/* Change Template for All — bulk apply to a bucket's visible/filtered pros. */}
      <ChangeTemplateBulkModal
        isOpen={ctBulkOpen}
        pros={ctBulkPros}
        onClose={() => setCtBulkOpen(false)}
        onApplied={() => setGridDrillNonce((n) => n + 1)}
      />
      {/* Clear All Leg Appt Dates — confirm dialog + mandatory service failure when a
          delivery appointment is being removed (Past Due - Not Dispatched bucket). */}
      {clearApptPreview && (
        <ClearApptDatesModal
          preview={clearApptPreview}
          saving={clearApptBusy}
          onCancel={() => setClearApptPreview(null)}
          onConfirm={confirmClearApptDates}
        />
      )}
      {massSendResult && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-gray-900/50 backdrop-blur-sm p-4"
          onClick={() => setMassSendResult(null)}
        >
          <div
            ref={massSendResultDrag.panelRef}
            style={massSendResultDrag.panelStyle}
            className="bg-panel rounded-lg shadow-xl max-w-lg w-full max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <ModalHeader
              title={massSendResult.title}
              onClose={() => setMassSendResult(null)}
              dragHandleProps={massSendResultDrag.dragHandleProps}
              className="shrink-0"
            />
            <div className="px-5 py-3 border-b border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {massSendResult.message}
              </p>
            </div>
            <div className="px-5 py-3 overflow-y-auto text-sm">
              {massSendResult.sent.length > 0 && (
                <div className="mb-3">
                  <div className="font-medium text-emerald-700 dark:text-emerald-400 mb-1">
                    Sent ({massSendResult.sent.length})
                  </div>
                  <div className="text-xs text-label break-words">
                    {massSendResult.sent.join(', ')}
                  </div>
                </div>
              )}
              {massSendResult.skipped.length > 0 && (
                <div>
                  <div className="font-medium text-amber-700 dark:text-amber-400 mb-1">
                    Skipped ({massSendResult.skipped.length})
                  </div>
                  <ul className="space-y-0.5">
                    {massSendResult.skipped.map((s) => (
                      <li key={s.pronumber} className="text-xs text-label">
                        <span>{s.pronumber}</span>: {s.reason}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {massSendResult.total === 0 && (
                <div className="text-muted">No containers in this result set.</div>
              )}
            </div>
            <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex justify-end">
              <button
                type="button"
                onClick={() => setMassSendResult(null)}
                className="px-4 py-1.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function LCDashboards() {
  return (
    <ViewedProsProvider>
      {/* Owns the trace result modal for the whole page — the bucket tables rebuild
          their columns on every render, so a modal held inside a cell is torn down
          before it can be seen. */}
      <TraceResultsProvider>
        <LCDashboardsContent />
      </TraceResultsProvider>
    </ViewedProsProvider>
  );
}
